import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef, useCallback, useMemo } from 'react';
import { themeColor } from '../utils/themeColor';
import { API_BASE_URL, API_ENDPOINTS } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import TradovatePanel from './TradovatePanel';
import useWebSocket from '../hooks/useWebSocket';
import '../pages/NinjaTraderIntegration.css';
import './StrategyRowCard.css';
import './RiskManagementView.css';
import { IconZap, IconCopy, IconTrash, IconList, IconChart, IconPencil } from './Icons';
import MyTradeHistory from './MyTradeHistory';
import HeaderInfoTooltip from './HeaderInfoTooltip';
import { sortStrategies } from '../utils/strategySort';
import { getResultsNavLabel } from '../utils/activeEntity';
import { getBrandIdentity, useBrandIdentity } from '../utils/brandIdentity';

/** Connection source for + Accounts modal: NinjaTrader connector vs Tradovate OAuth */
function ConnectAccountMethodSelect({ value, onChange, id = 'connect-account-method' }) {
  return (
    <div className="nt-form-group">
      <label htmlFor={id}>Connection Method</label>
      <select
        id={id}
        className="nt-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ padding: '0.75rem', fontSize: '0.9rem' }}
      >
        <option value="risklo-connector">RiskLo Connector</option>
        <option value="tradovate">Tradovate</option>
      </select>
      <small className="nt-form-help">
        {value === 'risklo-connector'
          ? 'NinjaTrader 8 add-on connection'
          : 'Connect with Tradovate OAuth (Live or Demo)'}
      </small>
    </div>
  );
}

/** localStorage keys for Risk Management preferences */
const RISK_PREFS_KEY = 'risklo-risk-prefs';

/** Load persisted Risk Management preferences */
function loadRiskPrefs() {
  try {
    const s = localStorage.getItem(RISK_PREFS_KEY);
    if (s) {
      const p = JSON.parse(s);
      return {
        riskFilter: p.riskFilter || 'all',
        riskSortBy: p.riskSortBy || 'account',
        riskSortDirection: p.riskSortDirection || 'asc',
        riskViewMode: p.riskViewMode || 'table',
        columns: p.columns || null
      };
    }
  } catch (_) {}
  return null;
}

/** Save Risk Management preferences */
function saveRiskPrefs(prefs) {
  try {
    localStorage.setItem(RISK_PREFS_KEY, JSON.stringify(prefs));
  } catch (_) {}
}

/** Health Score history key */
const HEALTH_SCORE_HISTORY_KEY = 'risklo-health-score-history';
const MAX_HEALTH_HISTORY = 20;
const NON_STRATEGY_NAME_REGEX = /^(buy\d*|sell\d*|sellshort\d*|buytocover\d*|close|stopcancelclose|stop\s*loss|profit\s*target|entry|exit|manual|unknown)$/i;

function loadHealthScoreHistory() {
  try {
    const s = localStorage.getItem(HEALTH_SCORE_HISTORY_KEY);
    if (s) return JSON.parse(s);
  } catch (_) {}
  return [];
}

function saveHealthScoreHistory(history) {
  try {
    localStorage.setItem(HEALTH_SCORE_HISTORY_KEY, JSON.stringify(history.slice(-MAX_HEALTH_HISTORY)));
  } catch (_) {}
}

/** Current Results week in ET: new week starts 12:00 AM ET Monday. On Sunday ET, show previous week. */
function getCurrentResultsWeekET() {
  const now = new Date();
  const dayNameET = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', weekday: 'long' }).format(now);
  const dayOfWeekET = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].indexOf(dayNameET);
  const etParts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  const etYear = parseInt(etParts.find(p => p.type === 'year').value, 10);
  const etMonth = parseInt(etParts.find(p => p.type === 'month').value, 10) - 1;
  const etDay = parseInt(etParts.find(p => p.type === 'day').value, 10);
  // Sunday ET = show last week (6 days back to Monday); Monday = 0, Tuesday = 1, ...
  const displayMondayOffset = dayOfWeekET === 0 ? 6 : dayOfWeekET - 1;
  let mondayDay = etDay - displayMondayOffset;
  let mondayMonth = etMonth;
  let mondayYear = etYear;
  while (mondayDay < 1) {
    mondayMonth -= 1;
    if (mondayMonth < 0) {
      mondayMonth += 12;
      mondayYear -= 1;
    }
    mondayDay += new Date(mondayYear, mondayMonth + 1, 0).getDate();
  }
  const startDate = `${mondayYear}-${String(mondayMonth + 1).padStart(2, '0')}-${String(mondayDay).padStart(2, '0')}`;
  const mondayDate = new Date(mondayYear, mondayMonth, mondayDay);
  mondayDate.setDate(mondayDate.getDate() + 4);
  const endDate = `${mondayDate.getFullYear()}-${String(mondayDate.getMonth() + 1).padStart(2, '0')}-${String(mondayDate.getDate()).padStart(2, '0')}`;
  return { startDate, endDate };
}

/** Current weekday in ET (e.g. "Monday"). Used to prefer realized_pnl for today's P&L. */
function getCurrentETWeekday() {
  return new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', weekday: 'long' }).format(new Date());
}

const NinjaTraderDashboard = forwardRef(({
  user, onSync, viewMode = 'summary', cardViewMode: externalCardViewMode,
  mthDateRangeFilter = 'all-time', mthCustomStartDate = '', mthCustomEndDate = '',
  mthSelectedAccounts = 'all',
  sheetNames: propSheetNames = [], connectButtonInParent = false, isAdmin = false,
  accountPlatformFilter = 'all',
}, ref) => {
  const { appName } = useBrandIdentity();
  const [mergedConnections, setMergedConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [showInactiveAccounts, setShowInactiveAccounts] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState(new Set());
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [trades, setTrades] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'winners', 'losers'
  const [strategyFilter, setStrategyFilter] = useState('');
  const [cardViewMode] = useState('table'); // 'cards' or 'table' for Summary view
  const [sortBy, setSortBy] = useState('name'); // 'name', 'pnl', 'cash'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc', 'desc'
  
  // Use external cardViewMode if provided (for Summary view controlled by parent)
  const effectiveCardViewMode = externalCardViewMode || cardViewMode;

  const connections = useMemo(() => {
    if (!accountPlatformFilter || accountPlatformFilter === 'all') return mergedConnections;
    return mergedConnections.filter((c) => (c.platform || 'ninjatrader') === accountPlatformFilter);
  }, [mergedConnections, accountPlatformFilter]);

  // Trade modal states
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [modalTrades, setModalTrades] = useState([]);
  const [modalStats, setModalStats] = useState(null);
  const [loadingModalTrades, setLoadingModalTrades] = useState(false);
  const [loadingTrades, setLoadingTrades] = useState(false);
  
  // Account settings modal states
  const [editingAccount, setEditingAccount] = useState(null);
  const [editAccountType, setEditAccountType] = useState('prop');
  const [editStartingBalance, setEditStartingBalance] = useState('');
  const [savingAccountSettings, setSavingAccountSettings] = useState(false);
  
  // Form state for Summary view
  const [showConnectForm, setShowConnectForm] = useState(false);
  /** 'risklo-connector' | 'tradovate' — which flow is shown in + Accounts modal */
  const [connectAccountMethod, setConnectAccountMethod] = useState('risklo-connector');
  const [apiKey, setApiKey] = useState('');
  const [accountNames, setAccountNames] = useState(['']);
  const [connecting, setConnecting] = useState(false);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [showQuickSetup, setShowQuickSetup] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [accountToDisconnect, setAccountToDisconnect] = useState(null);
  const [showStrategyModal, setShowStrategyModal] = useState(false);
  const [selectedStrategyDetail, setSelectedStrategyDetail] = useState(null); // { strategy, accountName } or { strategies: [], accountName }
  const [strategyDetailTab, setStrategyDetailTab] = useState(0);
  const [syncingStrategies, setSyncingStrategies] = useState({});
  const [editingTradeId, setEditingTradeId] = useState(null);
  const [editStrategyValue, setEditStrategyValue] = useState('');
  const [availableStrategies, setAvailableStrategies] = useState([]);
  const [apiProvider] = useState('risklo-connector');
  
  // Sorting state for table (Summary view)
  const [sortColumn, setSortColumn] = useState(null);
  // Mobile only: expand to show all metric cards (default: first 4 only)
  const [showMoreMetrics, setShowMoreMetrics] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches);
  const [tableSortDirection, setTableSortDirection] = useState('asc'); // 'asc' or 'desc'
  
  // API Key Management (for RiskLo Connector)
  const [userApiKeys, setUserApiKeys] = useState([]);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [newApiKey, setNewApiKey] = useState(null);
  const [keyName, setKeyName] = useState('');
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [cleaningStressAccounts, setCleaningStressAccounts] = useState(false);
  const activeConnectorKeys = userApiKeys.filter((k) => k.is_active && k.api_key);
  
  // Risk Management state (R2: persisted via localStorage)
  const [riskAssessments, setRiskAssessments] = useState([]);
  const [loadingRisk, setLoadingRisk] = useState(false);
  const [riskError, setRiskError] = useState(null);
  const [vectorSpreadsheetId, setVectorSpreadsheetId] = useState('');
  const [riskFilter, setRiskFilter] = useState(() => { const p = loadRiskPrefs(); return p?.riskFilter ?? 'all'; });
  const [fetchingSpreadsheetId, setFetchingSpreadsheetId] = useState(false);
  const [selectedRiskAccount, setSelectedRiskAccount] = useState(null);
  const [riskViewMode, setRiskViewMode] = useState(() => { const p = loadRiskPrefs(); return p?.riskViewMode ?? 'table'; });
  const [showHealthScoreModal, setShowHealthScoreModal] = useState(false);
  const [riskSortBy, setRiskSortBy] = useState(() => { const p = loadRiskPrefs(); return p?.riskSortBy ?? 'account'; });
  const [riskSortDirection, setRiskSortDirection] = useState(() => { const p = loadRiskPrefs(); return p?.riskSortDirection ?? 'asc'; });
  const [riskSearch, setRiskSearch] = useState(''); // R8: search
  const [riskColumns, setRiskColumns] = useState(() => { const p = loadRiskPrefs(); return p?.columns ?? { balance: true, trailingDD: true, distanceToBlowout: true, exceedProb: true }; });
  const [showRiskColumnMenu, setShowRiskColumnMenu] = useState(false);
  const [showFirstTimeHint, setShowFirstTimeHint] = useState(() => { try { return localStorage.getItem('risklo-risk-hint-dismissed') !== 'true'; } catch (_) { return true; } });
  const [healthScoreHistory, setHealthScoreHistory] = useState(loadHealthScoreHistory); // R6: trend
  const [riskLastUpdatedAt, setRiskLastUpdatedAt] = useState(null); // R3: last updated
  const riskSearchInputRef = useRef(null); // R8: keyboard shortcut
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [showRiskTypesInfo, setShowRiskTypesInfo] = useState(false); // R11: 30% rule + blowout info
  const [riskModalTab, setRiskModalTab] = useState('blowout'); // 'blowout' | 'thirtyPercent' for account detail modal
  const [copyToast, setCopyToast] = useState(false);

  // Fetch user's Vector Results spreadsheet ID
  const fetchVectorSpreadsheetId = useCallback(async () => {
    if (!user?.email) return;

    try {
      setFetchingSpreadsheetId(true);
      const response = await fetch(`${API_BASE_URL}/api/user-spreadsheet-id`);

      if (response.ok) {
        const data = await response.json();
        console.log('📊 Spreadsheet config:', data);

        if (data.spreadsheetId) {
          setVectorSpreadsheetId(data.spreadsheetId);
          console.log('✅ Using Vector spreadsheet mode:', data.spreadsheetId);
        } else {
          console.log('ℹ️ No Vector spreadsheet configured');
          setVectorSpreadsheetId('');
        }
      } else {
        setVectorSpreadsheetId('');
      }
    } catch (err) {
      console.error('❌ Error fetching spreadsheet ID:', err);
      setVectorSpreadsheetId('');
    } finally {
      setFetchingSpreadsheetId(false);
    }
  }, [user?.email]);
  
  // Helper to format currency - shared across all views
  const formatCurrency = (num) => {
    const sign = num < 0 ? '-' : '';
    const abs = Math.abs(num);
    const formatted = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${sign}$${formatted}`;
  };

  // Helper to calculate true P&L from cash value based on prop firm account tiers
  // For prop firms: P&L = cash value - account size (tier). Always use this for prop firms.
  // For live/personal: use custom starting balance when set.
  const calculateTruePnL = (conn) => {
    const cashValue = conn.starting_balance;
    if (cashValue === null || cashValue === undefined) return 0;
    // If account is live/personal and has custom starting balance, use that
    if (conn.account_type === 'live' && conn.custom_starting_balance != null) {
      return cashValue - conn.custom_starting_balance;
    }
    
    // Otherwise use prop firm tier detection (default behavior)
    // Determine account tier based on cash value (±$20k range)
    let accountTier;
    if (cashValue >= 5000 && cashValue <= 45000) {
      accountTier = 25000;
    } else if (cashValue >= 30000 && cashValue <= 70000) {
      accountTier = 50000;
    } else if (cashValue >= 80000 && cashValue <= 120000) {
      accountTier = 100000;
    } else if (cashValue >= 130000 && cashValue <= 170000) {
      accountTier = 150000;
    } else if (cashValue >= 230000 && cashValue <= 270000) {
      accountTier = 250000;
    } else if (cashValue >= 280000 && cashValue <= 320000) {
      accountTier = 300000;
    } else {
      // If outside expected ranges, try to guess the closest tier
      if (cashValue < 37500) accountTier = 25000;
      else if (cashValue < 75000) accountTier = 50000;
      else if (cashValue < 125000) accountTier = 100000;
      else if (cashValue < 200000) accountTier = 150000;
      else if (cashValue < 275000) accountTier = 250000;
      else accountTier = 300000;
    }
    
    // True P&L = Current Cash Value - Account Tier Starting Balance
    return cashValue - accountTier;
  };
  
  const connectedAccountsRef = useRef(null);

  // Fetch available strategies: prefer prop from parent (App/ResultsDashboard), else fetch from API
  useEffect(() => {
    const excludedSheets = [
      'Current Results',
      'Current Results (Hidden)',
      'Sample Strategy (Demo)',
      'Past Results',
      'Past Results (hidden)',
      'Sheet2',
      'Results',
      'Results Updated',
      'Summary'
    ];
    const dateSheetPattern = /^\d{1,2}[./]\d{1,2}([./]\d{2,4})?$/;

    if (propSheetNames && propSheetNames.length > 0) {
      const filtered = propSheetNames.filter(sheet => {
        const isDateFormat = dateSheetPattern.test(sheet);
        const isExcluded = excludedSheets.some(excluded =>
          sheet.toLowerCase() === excluded.toLowerCase()
        );
        return !isDateFormat && !isExcluded;
      });
      setAvailableStrategies(filtered);
      return;
    }

    const fetchStrategies = async () => {
      try {
        if (!user?.email) return;
        // Entity header required: this list drives strategy matching, and
        // without it every brand matches against Vector's strategies.
        const response = await fetch(`${API_BASE_URL}/api/sheets`, {
          headers: getBearerAuthHeaders(),
        });
        if (response.ok) {
          const data = await response.json();
          const filtered = (data.sheets || []).filter(sheet => {
            const isDateFormat = dateSheetPattern.test(sheet);
            const isExcluded = excludedSheets.some(excluded =>
              sheet.toLowerCase() === excluded.toLowerCase()
            );
            return !isDateFormat && !isExcluded;
          });
          setAvailableStrategies(filtered);
        }
      } catch (error) {
        console.error('Error fetching strategies:', error);
      }
    };
    fetchStrategies();
  }, [user, propSheetNames]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = () => setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Wrap fetchConnections in useCallback to prevent infinite loops
  const fetchConnections = useCallback(async () => {
    try {
      if (!user?.email) {
        console.log('⚠️ Cannot fetch connections - no user email');
        return;
      }
      
      setLoading(true);
      console.log('🔍 Fetching connections for:', user.email);
      const response = await fetch(`${API_BASE_URL}/api/ninjatrader/accounts?userEmail=${encodeURIComponent(user.email)}`, {
        headers: getBearerAuthHeaders(),
      });
      
      if (!response.ok) {
        console.error('❌ Failed to fetch accounts, status:', response.status);
        const isServerError = response.status >= 500;
        const msg = isServerError
          ? 'Backend error. If using Railway, there may be an outage.'
          : 'Failed to fetch accounts';
        throw new Error(msg);
      }

      const data = await response.json();
      console.log('✅ Fetched connections:', data.accounts?.length, 'accounts');
      let list = (data.accounts || []).map((a) => ({ ...a, platform: 'ninjatrader' }));
      try {
        const st = await fetch(`${API_BASE_URL}/api/tradovate/status`, {
          headers: { ...getBearerAuthHeaders() },
        });
        if (st.ok) {
          const stJson = await st.json().catch(() => ({}));
          if (stJson.success && stJson.connected) {
            const ac = await fetch(`${API_BASE_URL}/api/tradovate/accounts`, {
              headers: { ...getBearerAuthHeaders() },
            });
            if (ac.ok) {
              const acJson = await ac.json().catch(() => ({}));
              const tvRows = (acJson.accounts || []).map((a) => ({
                id: `tradovate-${a.id}`,
                platform: 'tradovate',
                account_name: a.name || `Tradovate ${a.id}`,
                api_provider: 'tradovate',
                is_active: a.active !== false,
                last_sync_at: null,
                last_sync_status: null,
                last_sync_error: null,
                created_at: null,
                starting_balance: 0,
                account_type: null,
                custom_starting_balance: null,
                exclude_from_metrics: false,
                trailing_max_drawdown: null,
                realized_pnl: null,
                strategies: [],
                last_strategy: null,
                total_pnl: 0,
                total_trades: 0,
                cash_value: 0,
                tradovate_account_id: a.id,
              }));
              list = [...list, ...tvRows];
            }
          }
        }
      } catch (tvErr) {
        console.warn('Tradovate merge skipped:', tvErr?.message || tvErr);
      }
      setMergedConnections(list);
      setError(null);
    } catch (err) {
      console.error('❌ Error fetching NinjaTrader accounts:', err);
      const isNetworkError = err instanceof TypeError || (err.message && (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')));
      const userMsg = isNetworkError
        ? `${getBrandIdentity().appName} is temporarily unavailable. We're working on it and will be back online shortly.`
        : (err.message || 'Failed to fetch accounts');
      setError(userMsg);
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  const fetchTrades = useCallback(async () => {
    if (!user || !user.email) {
      console.warn('⚠️ Cannot fetch trades: user or email not available');
      return;
    }

    try {
      // Current Results week: ET, new week at 12:00 AM Monday; Sunday ET = previous week
      const { startDate, endDate } = getCurrentResultsWeekET();

      const url = `${API_BASE_URL}/api/ninjatrader/trades?userEmail=${encodeURIComponent(user.email)}&startDate=${startDate}&endDate=${endDate}`;
      console.log('🔍 Fetching trades for date range:', startDate, 'to', endDate);
      console.log('📍 URL:', url);

      const response = await fetch(url, { headers: getBearerAuthHeaders() });
      
      console.log('📡 Response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Failed to fetch trades:', response.status, errorData);
        throw new Error(errorData.error || `Failed to fetch trades (${response.status})`);
      }

      const data = await response.json();
      console.log('✅ Fetched trades:', data.trades?.length || 0, 'trades');
      console.log('📦 Sample trade:', data.trades?.[0]);
      
      if (!data.trades || data.trades.length === 0) {
        console.log('ℹ️ No trades found for this date range');
        setTrades({});
        return;
      }
      
      // Group trades by account and day
      const tradesByAccount = {};
      data.trades.forEach(trade => {
        // Use entry_time to determine the day of the week
        const tradeDate = new Date(trade.entry_time);
        const dayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][tradeDate.getDay()];
        
        const accountName = trade.account_name;
        
        if (!accountName) {
          console.warn('⚠️ Trade missing account_name:', trade);
          return;
        }
        
        if (!tradesByAccount[accountName]) {
          tradesByAccount[accountName] = {
            Monday: [],
            Tuesday: [],
            Wednesday: [],
            Thursday: [],
            Friday: []
          };
        }
        
        // Only add weekday trades
        if (tradesByAccount[accountName][dayName]) {
          tradesByAccount[accountName][dayName].push(trade);
        }
      });

      console.log('📊 Trades grouped by account:', Object.keys(tradesByAccount).length, 'accounts');
      console.log('📊 Full trades data:', tradesByAccount);
      setTrades(tradesByAccount);
    } catch (err) {
      console.error('❌ Error fetching trades:', err);
      console.error('❌ Error stack:', err.stack);
    }
  }, [user]);

  // Helpers - prefer active (LIVE) strategy when available, else most recently updated
  const isUsableStrategy = (strategy) => {
    if (!strategy) return false;
    const name = String(strategy?.strategy_name || '').trim();
    const displayName = String(strategy?.display_name || '').trim();
    if (!name) return false;
    if (NON_STRATEGY_NAME_REGEX.test(name)) return false;
    if (displayName && NON_STRATEGY_NAME_REGEX.test(displayName)) return false;
    return true;
  };
  const getUsableStrategies = (conn) => {
    if (!conn?.strategies?.length) return [];
    return conn.strategies.filter(isUsableStrategy);
  };
  const getDisplayStrategy = (conn) => {
    if (!conn) return null;
    const active = conn.strategies?.find(s => s.is_active !== false && isUsableStrategy(s));
    if (active) return active;
    if (isUsableStrategy(conn.last_strategy)) return conn.last_strategy;
    const fallback = conn.strategies?.find(isUsableStrategy);
    return fallback || null;
  };

  const renderCardStrategies = (conn) => {
    const usableStrategies = getUsableStrategies(conn);
    if (!conn.strategies?.length) {
      return (
        <div className="nt-strategy-item nt-strategy-empty">
          <div className="nt-strategy-main">
            <span className="nt-strategy-name">No Active Strategies</span>
            <span className="nt-strategy-display">Enable strategies in NinjaTrader to sync</span>
          </div>
        </div>
      );
    }
    if (usableStrategies.length === 0) {
      return (
        <div className="nt-strategy-item nt-strategy-empty">
          <div className="nt-strategy-main">
            <span className="nt-strategy-name">No Active Strategies</span>
            <span className="nt-strategy-display">Enable strategies in NinjaTrader to sync</span>
          </div>
        </div>
      );
    }
    if (usableStrategies.length >= 2) {
      return (
        <div
          className="nt-strategy-item"
          role="button"
          tabIndex={0}
          onClick={() => {
            setSelectedStrategyDetail({ strategies: usableStrategies, accountName: conn.account_name });
            setStrategyDetailTab(0);
            setShowStrategyModal(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setSelectedStrategyDetail({ strategies: usableStrategies, accountName: conn.account_name });
              setStrategyDetailTab(0);
              setShowStrategyModal(true);
            }
          }}
          style={{ cursor: 'pointer' }}
        >
          <div className="nt-strategy-main">
            <span className="nt-strategy-name">Multiple Strats</span>
            <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>
              ({usableStrategies.length} strategies)
            </span>
          </div>
        </div>
      );
    }
    return usableStrategies.map((strat, idx) => (
      <div
        key={idx}
        className="nt-strategy-item"
        role="button"
        tabIndex={0}
        onClick={() => {
          setSelectedStrategyDetail({ strategy: strat, accountName: conn.account_name });
          setStrategyDetailTab(0);
          setShowStrategyModal(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setSelectedStrategyDetail({ strategy: strat, accountName: conn.account_name });
            setStrategyDetailTab(0);
            setShowStrategyModal(true);
          }
        }}
        style={{ cursor: 'pointer' }}
      >
        <div className="nt-strategy-main">
          <span className="nt-strategy-name" title={strat.strategy_name}>
            {strat.strategy_name}
          </span>
          {strat.display_name && (
            <span className="nt-strategy-display" title={strat.display_name}>
              ({strat.display_name.length > 30
                ? `${strat.display_name.substring(0, 30)}...`
                : strat.display_name})
            </span>
          )}
        </div>
        <div className="nt-strategy-details">
          {strat.instrument && extractContractType(strat.instrument) && (
            <span className="nt-strategy-instrument" title={strat.instrument}>
              {extractContractType(strat.instrument)}
            </span>
          )}
          <span className={`nt-strategy-position ${(strat.position || 'flat').toLowerCase()}`}>
            {strat.position || 'FLAT'}
          </span>
          {(parseInt(strat.quantity) || extractContractSize(strat.display_name, strat.strategy_name)) && (
            <span className="nt-strategy-qty">
              {parseInt(strat.quantity) || extractContractSize(strat.display_name, strat.strategy_name)}
            </span>
          )}
        </div>
      </div>
    ));
  };

  const formatDateTimeET = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch (err) {
      return 'Never';
    }
  };

  // Fetch risk assessments for Risk Management view
  const fetchRiskAssessments = useCallback(async () => {
    if (!user?.email) {
      console.warn('⚠️ Cannot fetch risk: user email not available');
      return;
    }

    if (!vectorSpreadsheetId) {
      console.log('ℹ️ No Vector Results spreadsheet configured');
      setRiskAssessments([]);
      return;
    }

    try {
      setLoadingRisk(true);
      setRiskError(null);
      console.log('🎯 Calculating risk for:', user.email);

      const response = await fetch(`${API_BASE_URL}/api/ninjatrader/calculate-risk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({
          userEmail: user.email
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to calculate risk');
      }

      const data = await response.json();
      console.log('✅ Risk assessments:', data.riskAssessments?.length || 0, 'accounts');
      const assessments = data.riskAssessments || [];
      setRiskAssessments(assessments);
      setRiskLastUpdatedAt(Date.now()); // R3: last updated
      setEmailError(null); // Clear email error on refresh
      // R6: Append Health Score to history for trend
      if (assessments.length > 0) {
        const avg = assessments.reduce((s, a) => s + (a.overallRiskScore || 0), 0) / assessments.length;
        const score = Math.round(100 - avg);
        setHealthScoreHistory(prev => {
          const next = [...prev, { score, ts: Date.now() }].slice(-MAX_HEALTH_HISTORY);
          saveHealthScoreHistory(next);
          return next;
        });
      }

    } catch (err) {
      console.error('❌ Error calculating risk:', err);
      setRiskError(err.message);
      setRiskAssessments([]);
    } finally {
      setLoadingRisk(false);
    }
  }, [user?.email, vectorSpreadsheetId]);

  // Handle table column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if clicking same column
      setTableSortDirection(tableSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setTableSortDirection('asc');
    }
  };

  // WebSocket connection for real-time updates
  const refreshTimeoutRef = useRef(null);
  const lastRefreshRef = useRef(0);
  
  const handleWebSocketMessage = useCallback((data) => {
    if (data.type === 'sync_complete') {
      console.log(`🔄 Received sync update for ${data.accountName}`);
      
      // Debounce refreshes - only refresh once every 2 seconds
      const now = Date.now();
      if (now - lastRefreshRef.current < 2000) {
        console.log('⏸️  Skipping refresh (too soon)');
        return;
      }
      
      // Clear any pending refresh
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      
      // Schedule refresh with small delay to batch multiple updates
      refreshTimeoutRef.current = setTimeout(() => {
        if (!user?.email) {
          console.log('⚠️ Cannot refresh - no user email');
          return;
        }
        
        console.log('🔄 Refreshing data...');
        lastRefreshRef.current = Date.now();
        
        fetchConnections();
        if (viewMode === 'current') {
          fetchTrades();
        }
      }, 500); // Wait 500ms to batch multiple sync updates
    }
  }, [viewMode, user?.email, fetchConnections, fetchTrades]);

  const { isConnected } = useWebSocket(user, handleWebSocketMessage);

  // Show WebSocket connection status in console
  useEffect(() => {
    if (isConnected) {
      console.log('✅ Real-time updates enabled via WebSocket');
      console.log('⏰ Backend auto-sync runs at :29 and :59 of every hour');
    }
  }, [isConnected]);

  // Fetch risk assessments when Risk Management or History view is active
  useEffect(() => {
    if (viewMode === 'risk' || viewMode === 'history') {
      // First fetch the user's spreadsheet ID
      fetchVectorSpreadsheetId();
    }
  }, [viewMode, fetchVectorSpreadsheetId]);

  // Auto-fetch risk assessments once we have the spreadsheet ID
  useEffect(() => {
    if ((viewMode === 'risk' || viewMode === 'history') && vectorSpreadsheetId && !fetchingSpreadsheetId) {
      fetchRiskAssessments();
    }
  }, [viewMode, vectorSpreadsheetId, fetchingSpreadsheetId, fetchRiskAssessments]);

  // R2: Persist Risk Management preferences
  useEffect(() => {
    if (viewMode !== 'risk') return;
    saveRiskPrefs({ riskFilter, riskSortBy, riskSortDirection, riskViewMode, columns: riskColumns });
  }, [viewMode, riskFilter, riskSortBy, riskSortDirection, riskViewMode, riskColumns]);

  // R8: Keyboard shortcuts (Escape = close modal, / = focus search)
  useEffect(() => {
    if (viewMode !== 'risk') return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (selectedRiskAccount) setSelectedRiskAccount(null);
        else if (showHealthScoreModal) setShowHealthScoreModal(false);
      }
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        riskSearchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [viewMode, selectedRiskAccount, showHealthScoreModal]);

  // Reset modal tab when account selection changes
  useEffect(() => {
    if (selectedRiskAccount) setRiskModalTab('blowout');
  }, [selectedRiskAccount]);

  // Handle strategy edit save
  const handleSaveStrategy = async (tradeId) => {
    try {
      console.log('💾 Saving strategy:', { tradeId, strategyName: editStrategyValue });
      
      const response = await fetch(`${API_BASE_URL}/api/ninjatrader/trades/${tradeId}/strategy`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({ strategyName: editStrategyValue })
      });

      console.log('📡 Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Strategy updated:', data);
        
        // Update the trade in modalTrades
        setModalTrades(prev => prev.map(trade => 
          trade.id === tradeId ? { ...trade, strategy_name: editStrategyValue } : trade
        ));
        setEditingTradeId(null);
        setEditStrategyValue('');
        alert('Strategy updated successfully!');
      } else {
        const errorData = await response.json();
        console.error('❌ Failed to update:', errorData);
        alert(`Failed to update strategy: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error updating strategy:', error);
      alert(`Error updating strategy: ${error.message}`);
    }
  };

  // Expose refresh method to parent component
  useImperativeHandle(ref, () => ({
    refreshData: async () => {
      await fetchConnections();
      if (viewMode === 'current') {
        await fetchTrades();
      }
    },
    openQuickSetup: () => {
      setShowQuickSetup(true);
    },
    openConnectModal: () => {
      setConnectAccountMethod('risklo-connector');
      setShowConnectForm(true);
    }
  }));

  // Fetch connections and trades on mount
  useEffect(() => {
    if (user && user.email) {
      fetchConnections();
      if (viewMode === 'current') {
        fetchTrades();
      }
    }
  }, [user, viewMode, fetchConnections, fetchTrades]);

  // The connect form opens only from the "Connect Accounts" button (or the
  // imperative openConnectModal handle above). It used to auto-open whenever
  // the Accounts view had no connections, which meant simply navigating to My
  // Results threw a modal in the way before you could look at the page — and
  // there was no way to dismiss it for good, since the effect re-fired on every
  // dependency change while the account list stayed empty.

  // Fetch trades for trade modal (with date filtering based on viewMode)
  const fetchAccountTrades = async (accountName, dateRangeOverride) => {
    console.log('🔍 fetchAccountTrades called for:', accountName, 'viewMode:', viewMode);
    setLoadingModalTrades(true);
    setLoadingTrades(true);
    setSelectedAccount(accountName);
    setError(null);

    try {
      // Build URL with date filtering based on viewMode
      let url = `${API_BASE_URL}/api/ninjatrader/trades?userEmail=${encodeURIComponent(user.email)}&accountName=${encodeURIComponent(accountName)}`;
      
      // Override: single-day filter from My Trade History
      if (dateRangeOverride?.startDate && dateRangeOverride?.endDate) {
        url += `&startDate=${dateRangeOverride.startDate}&endDate=${dateRangeOverride.endDate}`;
      }
      // For Current Results view, only show this week's trades (ET week; Sunday = previous week)
      else if (viewMode === 'current') {
        const { startDate, endDate } = getCurrentResultsWeekET();
        url += `&startDate=${startDate}&endDate=${endDate}`;
      }
      // For Summary view, fetch all trades (no date filter)

      // Fetch trades
      const tradesResponse = await fetch(url, { headers: getBearerAuthHeaders() });
      const tradesData = await tradesResponse.json();

      if (!tradesResponse.ok) {
        throw new Error(tradesData.error || 'Failed to fetch trades');
      }

      // Trades now have strategy_name stored in the database
      // If a trade doesn't have a strategy_name (old data), fall back to current strategy
      const accountData = connections.find(conn => conn.account_name === accountName);
      const currentStrategy = accountData?.strategies?.[0]?.strategy_name;

      const tradesWithStrategy = (tradesData.trades || []).map(trade => ({
        ...trade,
        strategy_name: trade.strategy_name || currentStrategy || 'Unknown'
      }));

      setModalTrades(tradesWithStrategy);

      // Stats: use API for all-time/summary, or compute from trades when date-filtered (e.g. single day from Trade History)
      if (dateRangeOverride?.startDate && dateRangeOverride?.endDate) {
        const trades = tradesWithStrategy;
        const totalTrades = trades.length;
        const winningTrades = trades.filter(t => (parseFloat(t.net_pnl) || 0) > 0).length;
        const losingTrades = trades.filter(t => (parseFloat(t.net_pnl) || 0) < 0).length;
        const totalPnL = trades.reduce((s, t) => s + (parseFloat(t.net_pnl) || 0), 0);
        const pnls = trades.map(t => parseFloat(t.net_pnl) || 0).filter(Boolean);
        setModalStats({
          totalTrades,
          winningTrades,
          losingTrades,
          winRate: totalTrades > 0 ? parseFloat((winningTrades / totalTrades * 100).toFixed(2)) : 0,
          totalPnL,
          avgPnL: totalTrades > 0 ? totalPnL / totalTrades : 0,
          bestTrade: pnls.length ? Math.max(...pnls) : 0,
          worstTrade: pnls.length ? Math.min(...pnls) : 0
        });
      } else {
        const statsUrl = `${API_BASE_URL}/api/ninjatrader/stats?userEmail=${encodeURIComponent(user.email)}&accountName=${encodeURIComponent(accountName)}`;
        const statsResponse = await fetch(statsUrl, { headers: getBearerAuthHeaders() });
        const statsData = await statsResponse.json();
        if (!statsResponse.ok) {
          throw new Error(statsData.error || 'Failed to fetch stats');
        }
        setModalStats(statsData.stats || null);
      }
      setSelectedAccount(accountName);
    } catch (err) {
      console.error('Error fetching trades for modal:', err);
      setError(err.message);
    } finally {
      setLoadingModalTrades(false);
      setLoadingTrades(false);
    }
  };

  // API Key Management Functions
  const fetchApiKeys = useCallback(async () => {
    if (!user?.email) return;
    
    setLoadingKeys(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/connector-keys/list`, {
        headers: { ...getBearerAuthHeaders() },
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserApiKeys(data.keys || []);
      } else {
        console.error('Failed to fetch API keys');
      }
    } catch (error) {
      console.error('Error fetching API keys:', error);
    } finally {
      setLoadingKeys(false);
    }
  }, [user?.email]);

  // Fetch API keys when provider changes to risklo-connector
  useEffect(() => {
    if (apiProvider === 'risklo-connector' && user?.email) {
      fetchApiKeys();
    }
  }, [apiProvider, user?.email, fetchApiKeys]);

  useEffect(() => {
    if (apiProvider !== 'risklo-connector') return;
    if (activeConnectorKeys.length === 0) {
      setApiKey('');
      return;
    }
    const hasSelectedKey = activeConnectorKeys.some((key) => key.api_key === apiKey);
    if (!hasSelectedKey) {
      setApiKey(activeConnectorKeys[0].api_key);
    }
  }, [apiProvider, activeConnectorKeys, apiKey]);

  const handleGenerateApiKey = async () => {
    if (!user?.email) {
      setError('Please sign in to generate API key');
      return;
    }

    setGeneratingKey(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/connector-keys/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({
          keyName: keyName || 'My NinjaTrader'
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setNewApiKey(data.key.api_key);
        setApiKey(data.key.api_key);
        setShowApiKeyModal(true);
        setKeyName('');
        // Refresh keys list
        await fetchApiKeys();
        setSuccess('API key generated successfully!');
      } else {
        setError(data.error || 'Failed to generate API key');
      }
    } catch (error) {
      console.error('Error generating API key:', error);
      setError('Failed to generate API key');
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleDeleteApiKey = async (keyId) => {
    if (!user?.email) return;
    
    if (!window.confirm('Are you sure you want to deactivate this API key? Your NinjaTrader add-on will stop syncing.')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/connector-keys/${keyId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('API key deactivated successfully');
        await fetchApiKeys();
      } else {
        setError(data.error || 'Failed to deactivate API key');
      }
    } catch (error) {
      console.error('Error deleting API key:', error);
      setError('Failed to deactivate API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setSuccess('API key copied to clipboard!');
      setTimeout(() => setSuccess(null), 2000);
    }).catch(() => {
      setError('Failed to copy to clipboard');
    });
  };

  const maskApiKey = (rawKey) => {
    const value = String(rawKey || '');
    if (!value) return '';
    if (value.length <= 12) return value;
    return `${value.substring(0, 8)}••••${value.slice(-4)}`;
  };

  // Bulk connect multiple accounts (for Summary view)
  const handleBulkConnect = async (e) => {
    e.preventDefault();
    
    if (!user?.email) {
      setError('Please sign in to connect NinjaTrader');
      return;
    }

    const validAccountNames = accountNames.filter(name => name.trim().length > 0);
    
    if (validAccountNames.length === 0) {
      setError('Please enter at least one account name');
      return;
    }

    if (apiProvider === 'risklo-connector' && !apiKey.trim()) {
      setError('Generate or select a connector API key before connecting accounts');
      return;
    }

    setConnecting(true);
    setError(null);
    setSuccess(null);

    let successCount = 0;
    let failedAccounts = [];

    try {
      // Ensure we send the exact provider value (risklo-connector vs crosstrade)
      const providerToSend = apiProvider === 'risklo-connector' ? 'risklo-connector' : (apiProvider || 'crosstrade');
      console.log('🔗 Connecting with provider:', providerToSend, '(from state:', apiProvider, ')');

      for (const accountName of validAccountNames) {
        try {
          const payload = {
            userEmail: user.email,
            apiProvider: providerToSend,
            apiKey: apiKey.trim(),
            accountName: accountName.trim()
          };
          const response = await fetch(`${API_BASE_URL}/api/ninjatrader/connect`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
            body: JSON.stringify(payload)
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || 'Failed to connect account');
          }

          successCount++;
        } catch (err) {
          console.error(`Error connecting ${accountName}:`, err);
          failedAccounts.push(accountName);
        }
      }

      if (successCount === validAccountNames.length) {
        setSuccess(`Successfully connected all ${successCount} account${successCount > 1 ? 's' : ''}!`);
      } else if (successCount > 0) {
        setSuccess(`Connected ${successCount} of ${validAccountNames.length} accounts`);
        if (failedAccounts.length > 0) {
          setError(`Failed to connect: ${failedAccounts.join(', ')}`);
        }
      } else {
        setError('Failed to connect all accounts');
      }

      if (successCount === validAccountNames.length) {
        setApiKey('');
        setAccountNames(['']);
        setUploadedFileName(null);
        setShowConnectForm(false);
        
        setTimeout(() => {
          if (connectedAccountsRef.current) {
            connectedAccountsRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }
        }, 100);
      }
      
      await fetchConnections();
    } catch (err) {
      console.error('Error in bulk connect:', err);
      setError(err.message);
    } finally {
      setConnecting(false);
    }
  };

  const handleAddAccountField = () => {
    setAccountNames([...accountNames, '']);
  };

  const handleRemoveAccountField = (index) => {
    const newAccountNames = accountNames.filter((_, i) => i !== index);
    setAccountNames(newAccountNames.length > 0 ? newAccountNames : ['']);
  };

  const handleAccountNameChange = (index, value) => {
    const newAccountNames = [...accountNames];
    newAccountNames[index] = value;
    setAccountNames(newAccountNames);
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await processCsvFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      await processCsvFile(file);
    }
  };

  const processCsvFile = async (file) => {
    if (!file) return;

    setUploadingCsv(true);
    setUploadedFileName(file.name);
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length === 0) {
          setError('CSV file is empty');
          setUploadingCsv(false);
          return;
        }
        
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        const accountColumnIndex = headers.findIndex(h => 
          h.includes('strategy') || h.includes('account') || h.includes('name') || h.includes('display')
        );

        if (accountColumnIndex === -1) {
          setError('Could not find account name column in CSV');
          setUploadingCsv(false);
          return;
        }

        const extractedNames = [];
        for (let i = 1; i < lines.length; i++) {
          const columns = lines[i].split(',');
          const accountName = columns[accountColumnIndex]?.trim();
          if (accountName && accountName.length > 0) {
            extractedNames.push(accountName);
          }
        }

        if (user && user.email) {
          try {
            const updateResponse = await fetch(`${API_BASE_URL}/api/ninjatrader/update-cash-from-csv`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
              body: JSON.stringify({
                userEmail: user.email,
                csvContent: text
              })
            });
            
            if (updateResponse.ok) {
              const updateData = await updateResponse.json();
              console.log(`✅ Updated ${updateData.updatedCount} account cash values from CSV`);
            }
          } catch (updateError) {
            console.error('⚠️ Failed to update cash values from CSV:', updateError);
          }
        }

        if (extractedNames.length === 0) {
          setError('No account names found in CSV');
          setUploadingCsv(false);
          return;
        }

        const uniqueNames = [];
        extractedNames.forEach(name => {
          if (!uniqueNames.some(n => n.toLowerCase() === name.toLowerCase())) {
            uniqueNames.push(name);
          }
        });

        setAccountNames(uniqueNames);
        setSuccess(`Loaded ${uniqueNames.length} account${uniqueNames.length > 1 ? 's' : ''} from CSV`);
        
        setTimeout(() => {
          if (connectedAccountsRef.current) {
            connectedAccountsRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }
        }, 100);
      } catch (err) {
        console.error('Error parsing CSV:', err);
        setError('Failed to parse CSV file');
      } finally {
        setUploadingCsv(false);
      }
    };

    reader.readAsText(file);
  };

  const handleDisconnect = async (accountId, accountName, platform) => {
    setAccountToDisconnect({ id: accountId, name: accountName, platform: platform || 'ninjatrader' });
    setShowDisconnectModal(true);
  };

  const handleDeleteStressTestAccounts = async () => {
    if (!isAdmin) return;

    if (!window.confirm('Delete all StressTestAccount_* records? This permanently removes those connections and related data.')) {
      return;
    }

    setCleaningStressAccounts(true);
    setError(null);
    setSuccess(null);

    try {
      const authToken = localStorage.getItem('risklo_auth_token');
      if (!authToken) {
        throw new Error('Admin session not found. Please sign in again.');
      }

      const response = await fetch(API_ENDPOINTS.adminDeleteStressTestAccounts, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to remove stress test accounts');
      }

      await fetchConnections();
      setSelectedAccounts(new Set());
      setSuccess(
        data.deletedCount > 0
          ? `Removed ${data.deletedCount} stress test account(s).`
          : 'No stress test accounts found.'
      );
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err.message || 'Failed to remove stress test accounts');
      setTimeout(() => setError(null), 5000);
    } finally {
      setCleaningStressAccounts(false);
    }
  };

  const confirmDisconnect = async () => {
    if (!accountToDisconnect) return;

    try {
      if (accountToDisconnect.platform === 'tradovate') {
        const response = await fetch(`${API_BASE_URL}/api/tradovate/disconnect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || 'Failed to disconnect Tradovate');
        }
        setSuccess(`Disconnected Tradovate (${accountToDisconnect.name})`);
      } else {
        const response = await fetch(`${API_BASE_URL}/api/ninjatrader/disconnect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
          body: JSON.stringify({
            userEmail: user.email,
            connectionId: accountToDisconnect.id,
            deleteData: true
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to disconnect account');
        }

        setSuccess(`Disconnected ${accountToDisconnect.name}`);
      }
      await fetchConnections();
    } catch (err) {
      console.error('Error disconnecting account:', err);
      setError(err.message);
    } finally {
      setShowDisconnectModal(false);
      setAccountToDisconnect(null);
    }
  };

  const handleSyncStrategies = async (accountName) => {
    setSyncingStrategies(prev => ({ ...prev, [accountName]: true }));
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/ninjatrader/sync-strategies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({
          userEmail: user.email,
          accountName: accountName
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to sync strategies');
      }

      setSuccess(`Synced strategies for ${accountName}`);
      await fetchConnections();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error(`Error syncing strategies for ${accountName}:`, err);
      setError(err.message);
    } finally {
      setSyncingStrategies(prev => ({ ...prev, [accountName]: false }));
    }
  };

  const handleSyncAccount = async (accountName) => {
    setSyncing(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/ninjatrader/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({
          userEmail: user.email,
          accountName: accountName,
          lookbackDays: 90
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to sync account');
      }

      setSuccess(`Synced ${accountName}`);
      await fetchConnections();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error(`Error syncing ${accountName}:`, err);
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const toggleRowExpansion = (connId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(connId)) {
      newExpanded.delete(connId);
    } else {
      newExpanded.add(connId);
    }
    setExpandedRows(newExpanded);
  };

  if (!user) {
    return (
      <div className="nt-empty-state">
        <div className="nt-empty-icon">🔐</div>
        <h3>Sign In Required</h3>
        <p>Please sign in to view your NinjaTrader accounts</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="nt-loading">
        <div className="nt-spinner"></div>
        <p>Loading NinjaTrader accounts...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="nt-alert nt-alert-error">
        <span className="nt-alert-icon">❌</span>
        <span>{error}</span>
      </div>
    );
  }

  // When no connections, don't return early - fall through so Summary view can show the connect form in place

  // Filter connections for metrics - exclude inactive AND exclude_from_metrics accounts
  const activeConnections = connections.filter(c => c.is_active);
  const metricsConnections = activeConnections.filter(c => !c.exclude_from_metrics);

  const displayedConnections = showInactiveAccounts 
    ? connections.filter(c => !c.is_active)
    : activeConnections;

  // Apply sorting to displayed connections
  const sortedConnections = [...displayedConnections].sort((a, b) => {
    if (!sortColumn) return 0;
    
    let aValue, bValue;
    
    switch (sortColumn) {
      case 'account_name':
        aValue = a.account_name?.toLowerCase() || '';
        bValue = b.account_name?.toLowerCase() || '';
        break;
      case 'last_sync':
        aValue = a.last_sync_at ? new Date(a.last_sync_at).getTime() : 0;
        bValue = b.last_sync_at ? new Date(b.last_sync_at).getTime() : 0;
        break;
      case 'strategy':
        aValue = getDisplayStrategy(a)?.strategy_name?.toLowerCase() || '';
        bValue = getDisplayStrategy(b)?.strategy_name?.toLowerCase() || '';
        break;
      case 'contracts':
        aValue = getDisplayStrategy(a)?.quantity ? parseInt(getDisplayStrategy(a).quantity) : 0;
        bValue = getDisplayStrategy(b)?.quantity ? parseInt(getDisplayStrategy(b).quantity) : 0;
        break;
      case 'pnl':
        aValue = calculateTruePnL(a);
        bValue = calculateTruePnL(b);
        break;
      case 'cash_value':
        aValue = a.starting_balance || 0;
        bValue = b.starting_balance || 0;
        break;
      case 'trailing_drawdown':
        aValue = parseFloat(a.trailing_max_drawdown) || 0;
        bValue = parseFloat(b.trailing_max_drawdown) || 0;
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return tableSortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return tableSortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Extract contract type (MNQ/NQ) from instrument string
  const extractContractType = (instrument) => {
    if (!instrument || typeof instrument !== 'string') return null;
    const upper = instrument.toUpperCase().trim();
    if (upper.startsWith('MNQ')) return 'MNQ';
    if (upper.startsWith('NQ')) return 'NQ';
    return null;
  };

  // Extract contract size from strategy display name (copied from NinjaTraderIntegration)
  const extractContractSize = (displayName, strategyName) => {
    if (!displayName) return null;
    
    // Special handling for BEEFBowl - contract size is the last number
    if (strategyName && strategyName.toLowerCase().includes('beefbowl')) {
      const beefMatch = displayName.match(/,(\d+)\)$/);
      if (beefMatch) {
        return parseInt(beefMatch[1]);
      }
    }
    
    // For Primebeef - first number in parentheses like "Primebeef(3,3,10,50,0.6)"
    const simpleMatch = displayName.match(/\((\d+),/);
    if (simpleMatch) {
      return parseInt(simpleMatch[1]);
    }
    
    // For Grassfed - look for number pairs like "(...,1,1,50,80,...)"
    const complexMatch = displayName.match(/,(\d+),\d+,/);
    if (complexMatch) {
      return parseInt(complexMatch[1]);
    }
    
    return null;
  };

  // Account Settings Modal Functions
  const openAccountSettings = (conn) => {
    setEditingAccount(conn);
    setEditAccountType(conn.account_type || 'prop');
    setEditStartingBalance(conn.custom_starting_balance || '');
  };

  const saveAccountSettings = async () => {
    if (!editingAccount) return;
    
    setSavingAccountSettings(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/ninjatrader/update-account-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({
          userEmail: user.email,
          connectionId: editingAccount.id,
          accountType: editAccountType,
          customStartingBalance: editAccountType === 'live' ? parseFloat(editStartingBalance) : null
        })
      });

      if (response.ok) {
        await fetchConnections();
        setEditingAccount(null);
        setSuccess('Account settings updated successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update account settings');
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(null), 5000);
    } finally {
      setSavingAccountSettings(false);
    }
  };

  const renderAccountSettingsModal = () => {
    if (!editingAccount) return null;
    
    return (
      <>
        <div className="nt-modal-overlay" onClick={() => setEditingAccount(null)} />
        <div className="nt-trades-modal" style={{ maxWidth: '500px' }}>
          <div className="nt-modal-header">
            <h2>⚙️ Account Settings - {editingAccount.account_name}</h2>
            <button 
              className="nt-modal-close"
              onClick={() => setEditingAccount(null)}
            >
              ✕
            </button>
          </div>

          <div style={{ padding: '1.5rem' }}>
            {/* Account Type */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                Account Type
              </label>
              <select
                value={editAccountType}
                onChange={(e) => setEditAccountType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '0.95rem'
                }}
              >
                <option value="prop">Prop Firm (Auto-detect tier: $25k, $50k, $100k, $150k, $250k, $300k)</option>
                <option value="live">Live/Personal Account (Custom starting balance)</option>
              </select>
            </div>

            {/* Custom Starting Balance (only shown for live accounts) */}
            {editAccountType === 'live' && (
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                  Starting Balance (Your Initial Deposit)
                </label>
                <input
                  type="number"
                  value={editStartingBalance}
                  onChange={(e) => setEditStartingBalance(e.target.value)}
                  placeholder="e.g. 250"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '0.95rem'
                  }}
                />
                <small style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.8rem', marginTop: '0.5rem', display: 'block' }}>
                  Enter the amount you deposited (e.g., $250). P&L will be calculated as: Current Cash - Starting Balance
                </small>
              </div>
            )}

            {/* Current Values Display */}
            <div style={{ 
              padding: '1rem', 
              background: 'rgba(59, 130, 246, 0.1)', 
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '6px',
              marginBottom: '1.5rem'
            }}>
              <div style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.7)', marginBottom: '0.5rem' }}>
                Current Cash Value: <strong style={{ color: '#fff' }}>${(editingAccount.starting_balance || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong>
              </div>
              <div style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                Calculated P&L: <strong style={{ color: calculateTruePnL({...editingAccount, account_type: editAccountType, custom_starting_balance: editAccountType === 'live' ? parseFloat(editStartingBalance) : null}) >= 0 ? '#22c55e' : '#ef4444' }}>
                  ${calculateTruePnL({...editingAccount, account_type: editAccountType, custom_starting_balance: editAccountType === 'live' ? parseFloat(editStartingBalance) : null}).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </strong>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setEditingAccount(null)}
                className="nt-btn nt-btn-secondary"
                disabled={savingAccountSettings}
              >
                Cancel
              </button>
              <button
                onClick={saveAccountSettings}
                className="nt-btn nt-btn-primary"
                disabled={savingAccountSettings || (editAccountType === 'live' && !editStartingBalance)}
              >
                {savingAccountSettings ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  // Trade Modal JSX (rendered for both Current and Summary views)
  const renderTradeModal = () => {
    if (!selectedAccount) return null;
    
    return (
      <>
        <div className="nt-modal-overlay" onClick={() => {
          setSelectedAccount(null);
          setModalTrades([]);
          setModalStats(null);
        }} />
        <div className="nt-trades-modal">
          <div className="nt-modal-header">
            <h2><IconChart size={28} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />Trades - {selectedAccount}</h2>
            <button 
              className="nt-modal-close"
              onClick={() => {
                setSelectedAccount(null);
                setModalTrades([]);
                setModalStats(null);
              }}
            >
              ✕
            </button>
          </div>
          <div className="nt-modal-body">

        {/* Stats Cards */}
        {modalStats && (
          <div className="nt-stats-grid">
            <div className="nt-stat-card">
              <div className="nt-stat-card-label">Total Trades</div>
              <div className="nt-stat-card-value">{modalStats.totalTrades || 0}</div>
            </div>
            <div className="nt-stat-card">
              <div className="nt-stat-card-label">Win Rate</div>
              <div className="nt-stat-card-value" style={{ color: (modalStats.winRate || 0) >= 50 ? '#10b981' : '#ef4444' }}>
                {(modalStats.winRate || 0).toFixed(1)}%
              </div>
            </div>
            <div className="nt-stat-card">
              <div className="nt-stat-card-label">Total P&L</div>
              <div className="nt-stat-card-value" style={{ color: (modalStats.totalPnL || 0) >= 0 ? '#10b981' : '#ef4444' }}>
                {formatCurrency(modalStats.totalPnL || 0)}
              </div>
            </div>
            <div className="nt-stat-card">
              <div className="nt-stat-card-label">Avg P&L</div>
              <div className="nt-stat-card-value" style={{ color: (modalStats.avgPnL || 0) >= 0 ? '#10b981' : '#ef4444' }}>
                {formatCurrency(modalStats.avgPnL || 0)}
              </div>
            </div>
            <div className="nt-stat-card">
              <div className="nt-stat-card-label">Best Trade</div>
              <div className="nt-stat-card-value" style={{ color: '#10b981' }}>
                {formatCurrency(modalStats.bestTrade || 0)}
              </div>
            </div>
            <div className="nt-stat-card">
              <div className="nt-stat-card-label">Worst Trade</div>
              <div className="nt-stat-card-value" style={{ color: '#ef4444' }}>
                {formatCurrency(modalStats.worstTrade || 0)}
              </div>
            </div>
            {(() => {
              const modalAccount = connections.find(c => c.account_name === selectedAccount);
              if (!modalAccount) return null;
              return (
                <>
                  <div className="nt-stat-card">
                    <div className="nt-stat-card-label">Account Total</div>
                    <div className="nt-stat-card-value" style={{ color: '#fff' }}>
                      {modalAccount.starting_balance != null ? formatCurrency(modalAccount.starting_balance) : '-'}
                    </div>
                  </div>
                  <div className="nt-stat-card">
                    <div className="nt-stat-card-label">Trailing DD</div>
                    <div className="nt-stat-card-value" style={{ color: '#fff' }}>
                      {modalAccount.trailing_max_drawdown != null && parseFloat(modalAccount.trailing_max_drawdown) > 0
                        ? formatCurrency(modalAccount.trailing_max_drawdown)
                        : '-'}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Trades Table */}
        {modalTrades.length === 0 ? (
          <div className="nt-empty-state">
            <div className="nt-empty-icon">📈</div>
            <h3>No Trades Yet</h3>
            <p>No trades found for this account {viewMode === 'current' ? 'this week' : ''}</p>
          </div>
        ) : (
          <div className="nt-table-wrapper">
            <table className="nt-trades-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Instrument</th>
                  <th>Strategy</th>
                  <th>Position</th>
                  <th>Entry</th>
                  <th>Exit</th>
                  <th>Qty</th>
                  <th>P&L</th>
                </tr>
              </thead>
              <tbody>
                {modalTrades.map((trade) => (
                  <tr key={trade.id}>
                    <td>
                      <div className="nt-trade-date">
                        <div>{new Date(trade.trade_date).toLocaleDateString()}</div>
                        <small>{new Date(trade.entry_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</small>
                      </div>
                    </td>
                    <td>
                      <span className="nt-instrument-badge">{trade.instrument}</span>
                    </td>
                    <td className="nt-table-strategy">
                      {editingTradeId === trade.id ? (
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', rowGap: '0.25rem' }}>
                          <select 
                            value={editStrategyValue}
                            onChange={(e) => setEditStrategyValue(e.target.value)}
                            style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              border: '1px solid #374151',
                              background: '#1f2937',
                              color: '#e5e7eb',
                              fontSize: '0.875rem',
                              minWidth: '160px'
                            }}
                          >
                            <option value="">Select Strategy</option>
                            {availableStrategies.map(strat => (
                              <option key={strat} value={strat}>{strat}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleSaveStrategy(trade.id)}
                            disabled={!editStrategyValue}
                            style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              border: 'none',
                              background: editStrategyValue ? '#10b981' : '#374151',
                              color: 'white',
                              fontSize: '0.75rem',
                              cursor: editStrategyValue ? 'pointer' : 'not-allowed',
                              fontWeight: '500',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditingTradeId(null);
                              setEditStrategyValue('');
                            }}
                            style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              border: 'none',
                              background: '#374151',
                              color: '#9ca3af',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <span>{trade.strategy_name || 'Unknown'}</span>
                          <button
                            onClick={() => {
                              setEditingTradeId(trade.id);
                              setEditStrategyValue(trade.strategy_name || '');
                            }}
                            style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              border: 'none',
                              background: 'transparent',
                              color: '#6b7280',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}
                            title="Edit strategy"
                          >
                            <IconPencil size={14} color="rgba(255,255,255,0.7)" />
                          </button>
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`nt-position-badge ${trade.market_position.toLowerCase()}`}>
                        {trade.market_position}
                      </span>
                    </td>
                    <td>{parseFloat(trade.entry_price).toFixed(2)}</td>
                    <td>{parseFloat(trade.exit_price).toFixed(2)}</td>
                    <td>{trade.quantity}</td>
                    <td>
                      <span className={`nt-pnl ${parseFloat(trade.net_pnl) >= 0 ? 'positive' : 'negative'}`}>
                        ${parseFloat(trade.net_pnl).toFixed(2)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
          </div>
        </div>
      </>
    );
  };

  const renderStrategyDetailModal = () => {
    if (!showStrategyModal || !selectedStrategyDetail) return null;
    const strategies = selectedStrategyDetail.strategies || (selectedStrategyDetail.strategy ? [selectedStrategyDetail.strategy] : []);
    const currentStrategy = strategies[strategyDetailTab] || strategies[0];
    const hasMultipleTabs = strategies.length > 1;
    const renderStrategyContent = (strategy) => (
      <div style={{
        display: 'grid',
        gap: '0.75rem',
        padding: '1rem',
        background: 'rgba(255,255,255,0.05)',
        borderRadius: '8px',
        border: '1px solid rgba(255,255,255,0.1)'
      }}>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.25rem' }}>Strategy Name</div>
          <div style={{ color: '#fff', fontWeight: '500' }}>{strategy.strategy_name || '—'}</div>
        </div>
        {strategy.display_name != null && strategy.display_name !== '' && (
          <div>
            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.25rem' }}>Display Name</div>
            <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem', wordBreak: 'break-word' }} title={strategy.display_name}>{strategy.display_name}</div>
          </div>
        )}
        <div>
          {strategy.instrument != null && strategy.instrument !== '' && extractContractType(strategy.instrument) && (
            <>
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.25rem' }}>Instrument</div>
              <div style={{ color: '#fff', fontWeight: '500', fontSize: '0.9rem' }} title={strategy.instrument}>
                {extractContractType(strategy.instrument)}
              </div>
            </>
          )}
          <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.25rem' }}>Strategy ID</div>
          <div style={{ color: 'rgba(255,255,255,0.85)', fontFamily: 'monospace', fontSize: '0.85rem' }}>{strategy.strategy_id || '—'}</div>
        </div>
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.25rem' }}>Position</div>
            <div style={{ color: '#fff' }}>{strategy.position ?? 'FLAT'}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.25rem' }}>Quantity</div>
            <div style={{ color: '#fff' }}>{strategy.quantity ?? '—'}</div>
          </div>
        </div>
        {strategy.average_price != null && strategy.average_price !== '' && (
          <div>
            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.25rem' }}>Average Price</div>
            <div style={{ color: '#fff' }}>{strategy.average_price}</div>
          </div>
        )}
        {strategy.unrealized_pnl != null && strategy.unrealized_pnl !== '' && (
          <div>
            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.25rem' }}>Unrealized P&L</div>
            <div style={{ color: parseFloat(strategy.unrealized_pnl) >= 0 ? '#10b981' : '#ef4444' }}>
              {formatCurrency(parseFloat(strategy.unrealized_pnl))}
            </div>
          </div>
        )}
        {strategy.last_updated && (
          <div>
            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.25rem' }}>Last Updated</div>
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>
              {new Date(strategy.last_updated).toLocaleString()}
            </div>
          </div>
        )}
        <div>
          <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.25rem' }}>Status</div>
          <div style={{ color: strategy.is_active !== false ? '#10b981' : 'rgba(255,255,255,0.6)' }}>
            {strategy.is_active !== false ? 'Active' : 'Inactive'}
          </div>
        </div>
      </div>
    );
    return (
      <>
        <div className="nt-modal-overlay" onClick={() => setShowStrategyModal(false)} />
        <div className="nt-trades-modal" style={{ maxWidth: '480px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
          <div className="nt-modal-header">
            <h2 style={{ fontSize: '1.1rem' }}>Strategy Details</h2>
            <button className="nt-modal-close" onClick={() => setShowStrategyModal(false)}>✕</button>
          </div>
          <div style={{ padding: '1.25rem', overflowY: 'auto', flex: 1, minHeight: 0 }}>
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.35rem' }}>Account</div>
              <div style={{ fontWeight: '600', color: '#fff' }}>{selectedStrategyDetail.accountName}</div>
            </div>
            {hasMultipleTabs && (
              <div style={{ display: 'flex', gap: '0.35rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                {strategies.map((strat, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setStrategyDetailTab(idx)}
                    style={{
                      padding: '0.4rem 0.75rem',
                      fontSize: '0.85rem',
                      fontWeight: strategyDetailTab === idx ? 600 : 400,
                      background: strategyDetailTab === idx ? 'rgba(59, 130, 246, 0.25)' : 'rgba(255,255,255,0.06)',
                      border: `1px solid ${strategyDetailTab === idx ? 'rgba(59, 130, 246, 0.5)' : 'rgba(255,255,255,0.12)'}`,
                      borderRadius: '6px',
                      color: strategyDetailTab === idx ? '#60a5fa' : 'rgba(255,255,255,0.8)',
                      cursor: 'pointer'
                    }}
                  >
                    {strat.strategy_name || `Strategy ${idx + 1}`}
                  </button>
                ))}
              </div>
            )}
            {currentStrategy && renderStrategyContent(currentStrategy)}
            <button
              type="button"
              className="nt-btn nt-btn-primary"
              style={{ width: '100%', marginTop: '1rem' }}
              onClick={() => setShowStrategyModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      </>
    );
  };

  // Current Results View - grouped by account with daily P&L (matching Vector Results style)
  // Always show only active accounts here; ignore Inactive tab state from Accounts view
  if (viewMode === 'risk') {
    // Risk Management View - exclude accounts marked exclude_from_metrics in Accounts view
    const riskAssessmentsFiltered = riskAssessments.filter(acc => {
      const conn = connections.find(c => c.account_name === acc.accountName);
      return !conn?.exclude_from_metrics;
    });
    const healthScore = riskAssessmentsFiltered.length > 0
      ? Math.round(100 - (riskAssessmentsFiltered.reduce((sum, acc) => sum + (acc.overallRiskScore || 0), 0) / riskAssessmentsFiltered.length))
      : 0;

    const criticalAccounts = riskAssessmentsFiltered.filter(acc => acc.blowAccountStatus === 'NO GO' || acc.overallRiskLevel === 'high');
    const warningAccounts = riskAssessmentsFiltered.filter(acc => acc.blowAccountStatus === 'CAUTION' || acc.overallRiskLevel === 'moderate');
    const safeAccounts = riskAssessmentsFiltered.filter(acc => acc.blowAccountStatus === 'GO' && acc.overallRiskLevel === 'low');
    const noStrategyAccounts = riskAssessmentsFiltered.filter(acc => !acc.strategies || acc.strategies.length === 0);
    
    const totalCapitalAtRisk = criticalAccounts.reduce((sum, acc) => {
      const distanceToBlowout = parseFloat(acc.distanceToBlowout) || 0;
      return sum + Math.max(0, -distanceToBlowout); // Only count if negative (past blowout threshold)
    }, 0);

    // R2 & R8: Filter risk assessments (including critical+warning, search)
    let filteredRisk = riskAssessmentsFiltered;
    if (riskFilter === 'critical+warning') {
      filteredRisk = [...criticalAccounts, ...warningAccounts];
    } else if (riskFilter !== 'all') {
      if (riskFilter === 'critical') filteredRisk = criticalAccounts;
      else if (riskFilter === 'warning') filteredRisk = warningAccounts;
      else if (riskFilter === 'safe') filteredRisk = safeAccounts;
      else if (riskFilter === 'no-strategy') filteredRisk = noStrategyAccounts;
    }
    if (riskSearch.trim()) {
      const q = riskSearch.trim().toLowerCase();
      filteredRisk = filteredRisk.filter(a => {
        const name = (a.accountName || '').toLowerCase();
        const strat = (a.strategies?.[0]?.strategyName || '').toLowerCase();
        const conn = connections.find(c => c.account_name === a.accountName);
        const hasMultiple = getUsableStrategies(conn).length >= 2;
        const multiMatch = hasMultiple && 'multiple strats'.includes(q);
        return name.includes(q) || strat.includes(q) || multiMatch;
      });
    }

    // R2: Sort with extended options
    filteredRisk = [...filteredRisk].sort((a, b) => {
      let aVal, bVal;
      if (riskSortBy === 'account') {
        aVal = (a.accountName || '').toLowerCase();
        bVal = (b.accountName || '').toLowerCase();
      } else if (riskSortBy === 'riskScore') {
        aVal = a.overallRiskScore || 0;
        bVal = b.overallRiskScore || 0;
      } else if (riskSortBy === 'balance') {
        aVal = parseFloat(a.currentBalance) || 0;
        bVal = parseFloat(b.currentBalance) || 0;
      } else if (riskSortBy === 'trailingDD') {
        aVal = parseFloat(a.maxDrawdown) || 0;
        bVal = parseFloat(b.maxDrawdown) || 0;
      } else if (riskSortBy === 'distanceToBlowout') {
        aVal = parseFloat(a.distanceToBlowout) || 0;
        bVal = parseFloat(b.distanceToBlowout) || 0;
      } else if (riskSortBy === 'exceedProb') {
        aVal = parseFloat(a.blowAccountProbability) || 0;
        bVal = parseFloat(b.blowAccountProbability) || 0;
      } else if (riskSortBy === 'thirtyPercent') {
        aVal = a.strategies?.some(s => s.apexMaeComparison?.exceedsMae) ? 1 : 0;
        bVal = b.strategies?.some(s => s.apexMaeComparison?.exceedsMae) ? 1 : 0;
      } else return 0;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        if (aVal < bVal) return riskSortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return riskSortDirection === 'asc' ? 1 : -1;
      } else {
        if (aVal < bVal) return riskSortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return riskSortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });

    const handleRiskSort = (col) => {
      if (riskSortBy === col) setRiskSortDirection(d => d === 'asc' ? 'desc' : 'asc');
      else { setRiskSortBy(col); setRiskSortDirection('asc'); }
    };

    const copyAccountId = (e, text) => {
      e.stopPropagation();
      navigator.clipboard?.writeText(text || '').then(() => {
        setCopyToast(true);
        setTimeout(() => setCopyToast(false), 2000);
      });
    };

    return (
      <div className="ninjatrader-dashboard-view">
        {/* Vector Results Service Account Not Configured */}
        {!vectorSpreadsheetId && !fetchingSpreadsheetId && (
          <div className="nt-info-banner" style={{
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
            <h3 style={{ color: '#60a5fa', fontSize: '1.25rem', marginBottom: '0.75rem' }}>
              Strategy Results Service Account Required
            </h3>
            <p style={{ margin: '0 0 1.25rem 0', color: 'rgba(255,255,255,0.8)', lineHeight: '1.6', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto' }}>
              Risk Management uses the shared <strong>{getResultsNavLabel()}</strong> spreadsheet to calculate blowout risk based on historical performance data.
              The service account isn't configured on the server yet. Set <strong>GOOGLE_SERVICE_ACCOUNT_JSON</strong> or <strong>GOOGLE_APPLICATION_CREDENTIALS</strong> to enable this.
            </p>
          </div>
        )}

        {/* Loading Spreadsheet ID */}
        {fetchingSpreadsheetId && (
          <div className="nt-loading" style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="nt-spinner"></div>
            <p style={{ marginTop: '1rem', color: 'rgba(255,255,255,0.7)' }}>Loading {getResultsNavLabel()} configuration...</p>
          </div>
        )}

        {/* R11: Blowout Risk & 30% Rule info */}
        {!loadingRisk && vectorSpreadsheetId && (
          <div className="nt-risk-types-info-wrap" style={{ marginBottom: '1rem' }}>
            <button
              onClick={() => setShowRiskTypesInfo(!showRiskTypesInfo)}
              style={{
                padding: '0.5rem 1rem',
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '6px',
                color: '#60a5fa',
                fontSize: '0.9rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <span>{showRiskTypesInfo ? '▼' : '▶'}</span> About Blowout Risk & % Rule
            </button>
            {showRiskTypesInfo && (
              <div style={{
                marginTop: '0.75rem',
                padding: '1rem 1.25rem',
                background: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                fontSize: '0.9rem',
                lineHeight: 1.6,
                color: 'rgba(255, 255, 255, 0.9)'
              }}>
                <p style={{ margin: '0 0 0.75rem 0' }}>
                  <strong>Blowout Risk</strong> — Compares your current balance to the max trailing drawdown limit. GO = balance above the threshold; NO GO = past limit (or high probability of exceeding it). Based on historical loss data from your brand&apos;s strategy results.
                </p>
                <p style={{ margin: 0 }}>
                  <strong>% Rule</strong> — Consistency checks use your selected percentage to evaluate whether loss/profit behavior stays within firm limits. This view shows Blowout Risk; % Rule compliance is analyzed separately.
                </p>
              </div>
            )}
          </div>
        )}

        {/* R1: Critical/Warning Alert Banner */}
        {!loadingRisk && vectorSpreadsheetId && riskAssessmentsFiltered.length > 0 && (criticalAccounts.length > 0 || warningAccounts.length > 0) && (
          <div className={`nt-risk-alert-banner ${criticalAccounts.length > 0 ? '' : 'warning'}`}>
            <span className="nt-risk-alert-text">
              {criticalAccounts.length > 0
                ? `${criticalAccounts.length} account${criticalAccounts.length !== 1 ? 's' : ''} need immediate attention`
                : `${warningAccounts.length} account${warningAccounts.length !== 1 ? 's' : ''} in warning`}
            </span>
            <button
              className="nt-risk-alert-action"
              onClick={() => setRiskFilter(criticalAccounts.length > 0 ? 'critical' : 'warning')}
            >
              View {criticalAccounts.length > 0 ? 'Critical' : 'Warning'} ({criticalAccounts.length > 0 ? criticalAccounts.length : warningAccounts.length})
            </button>
          </div>
        )}

        {/* Summary Cards */}
        {!loadingRisk && vectorSpreadsheetId && riskAssessmentsFiltered.length > 0 && (
          <div className="nt-metrics-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            {/* Health Score */}
            <div 
              className="nt-metric-card" 
              style={{
                background: healthScore >= 70 ? 'rgba(16, 185, 129, 0.1)' : healthScore >= 40 ? 'rgba(245, 158, 11, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                border: healthScore >= 70 ? '1px solid rgba(16, 185, 129, 0.3)' : healthScore >= 40 ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                padding: '1rem',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => setShowHealthScoreModal(true)}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                Health Score
                <HeaderInfoTooltip
                  label=""
                  tooltip="Overall portfolio risk indicator. Calculated as 100 minus the average risk score across all accounts."
                />
              </div>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: healthScore >= 70 ? '#10b981' : healthScore >= 40 ? '#f59e0b' : '#ef4444' }}>
                {healthScore}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.5)', marginTop: '0.25rem' }}>
                {healthScore >= 70 ? 'Healthy' : healthScore >= 40 ? 'Monitor' : 'At Risk'}
              </div>
              {/* R6: Health Score trend sparkline */}
              {healthScoreHistory.length > 1 && (
                <div className="nt-health-trend" title={`History: ${healthScoreHistory.map(h => h.score).join(' → ')}`}>
                  {healthScoreHistory.slice(-12).map((h, i) => (
                    <div
                      key={i}
                      className={`nt-health-trend-bar ${h.score < 40 ? 'low' : h.score < 70 ? 'medium' : ''}`}
                      style={{ flex: 1, height: `${Math.max(2, (h.score / 100) * 24)}px` }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Accounts at Risk - R5: with mini bar chart */}
            <div className="nt-metric-card" style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                Accounts at Risk
                <HeaderInfoTooltip
                  label=""
                  tooltip="Count of accounts in each risk category: Critical (past limit), Warning (near limit), Safe, and No Strategy."
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
                <span style={{ fontSize: '2rem', fontWeight: '700', color: '#ef4444' }}>{criticalAccounts.length}</span>
                <span style={{ fontSize: '1.25rem', fontWeight: '600', color: '#f59e0b' }}>{warningAccounts.length}</span>
                <span style={{ fontSize: '1rem', fontWeight: '500', color: '#10b981' }}>{safeAccounts.length}</span>
                <span style={{ fontSize: '0.9rem', fontWeight: '500', color: '#9ca3af' }}>{noStrategyAccounts.length}</span>
              </div>
              <div className="nt-accounts-at-risk-bars">
                {['critical', 'warning', 'safe', 'no-strategy'].map(k => {
                  const count = k === 'critical' ? criticalAccounts.length : k === 'warning' ? warningAccounts.length : k === 'safe' ? safeAccounts.length : noStrategyAccounts.length;
                  const max = Math.max(1, criticalAccounts.length, warningAccounts.length, safeAccounts.length, noStrategyAccounts.length);
                  const h = max ? Math.max(4, (count / max) * 48) : 4;
                  return <div key={k} className={`nt-risk-bar ${k.replace('-', '')}`} style={{ height: `${h}px` }} title={`${count} ${k.replace('-', ' ')}`} />;
                })}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.5)', marginTop: '0.25rem' }}>
                🔴 Critical  🟡 Warning  🟢 Safe  ⚪ No Strategy
              </div>
            </div>

            {/* Total Capital at Risk - R4: tooltip */}
            <div className="nt-metric-card" style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                Capital at Risk
                <HeaderInfoTooltip
                  label=""
                  tooltip="Sum of (negative) distance-to-blowout for accounts past their limit. Only critical (NO GO) accounts contribute. $0 when no accounts are past limit."
                />
              </div>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#ef4444' }}>
                {formatCurrency(totalCapitalAtRisk)}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.5)', marginTop: '0.25rem' }}>
                {criticalAccounts.length} account{criticalAccounts.length !== 1 ? 's' : ''} past limit
              </div>
            </div>

            {/* Total Accounts */}
            <div className="nt-metric-card" style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                Total Accounts
                <HeaderInfoTooltip
                  label=""
                  tooltip="Total number of monitored accounts included in risk assessment."
                />
              </div>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#fff' }}>
                {riskAssessmentsFiltered.length}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.5)', marginTop: '0.25rem' }}>
                Monitored
              </div>
            </div>
          </div>
        )}

        {/* R6: Risk score distribution chart */}
        {!loadingRisk && vectorSpreadsheetId && riskAssessmentsFiltered.length > 0 && (
          <div className="nt-risk-distribution" style={{ marginBottom: '1.5rem' }}>
            <div className="nt-risk-distribution-title">Risk Score Distribution</div>
            {[
              { label: 'Safe (0-40)', count: riskAssessmentsFiltered.filter(a => (a.overallRiskScore || 0) <= 40).length, color: '#10b981' },
              { label: 'Moderate (41-70)', count: riskAssessmentsFiltered.filter(a => (a.overallRiskScore || 0) > 40 && (a.overallRiskScore || 0) <= 70).length, color: '#f59e0b' },
              { label: 'High (71-100)', count: riskAssessmentsFiltered.filter(a => (a.overallRiskScore || 0) > 70).length, color: '#ef4444' }
            ].map(({ label, count, color }) => (
              <div key={label} className="nt-risk-distribution-bar">
                <span className="nt-risk-distribution-label">{label}</span>
                <div className="nt-risk-distribution-track">
                  {count > 0 && <div className="nt-risk-distribution-fill" style={{ width: `${riskAssessmentsFiltered.length ? (count / riskAssessmentsFiltered.length) * 100 : 0}%`, background: color }} />}
                </div>
                <span className="nt-risk-distribution-count">{count}</span>
              </div>
            ))}
          </div>
        )}

        {copyToast && (
          <div style={{
            position: 'fixed',
            bottom: '2rem',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '0.75rem 1.5rem',
            background: 'rgba(16, 185, 129, 0.95)',
            color: '#fff',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '0.9rem',
            zIndex: 10001,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
          }}>
            Copied!
          </div>
        )}

        {/* Email confirmation banner */}
        {emailSent && user?.email && (
          <div style={{
            padding: '1rem',
            marginBottom: '1rem',
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '8px',
            color: '#10b981'
          }}>
            <strong>✓ Email sent:</strong> Risk summary emailed to <strong>{user.email}</strong>
          </div>
        )}
        {emailError && (
          <div style={{
            padding: '1rem',
            marginBottom: '1rem',
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: '8px',
            color: '#f59e0b'
          }}>
            <strong>⚠ Note:</strong> {emailError}
          </div>
        )}

        {/* R4: First-time hint */}
        {!loadingRisk && vectorSpreadsheetId && riskAssessmentsFiltered.length > 0 && showFirstTimeHint && (
          <div className="nt-first-time-hint">
            <span>💡 Click a row for details. Use search and filters to find accounts quickly.</span>
            <button className="nt-first-time-hint-dismiss" onClick={() => { setShowFirstTimeHint(false); try { localStorage.setItem('risklo-risk-hint-dismissed', 'true'); } catch (_) {} }}>
              Dismiss
            </button>
          </div>
        )}

        {/* Filters & Actions - Two-row layout with visual grouping */}
        {!loadingRisk && vectorSpreadsheetId && riskAssessmentsFiltered.length > 0 && (
          <div className="nt-risk-filters-sticky nt-risk-controls-layout" style={{ marginBottom: '1.5rem' }}>
            {/* Row 1: Filter row - search + filters */}
            <div className="nt-filter-row">
              <div className="nt-filter-search-wrap">
                <input
                  ref={riskSearchInputRef}
                  type="text"
                  placeholder="Search accounts or strategies… (press /)"
                  value={riskSearch}
                  onChange={(e) => setRiskSearch(e.target.value)}
                  className="nt-filter-search-input"
                />
              </div>
              {/* Desktop: filter chips */}
              <div className="nt-filter-chips">
                {[
                  { id: 'all', label: 'All', count: riskAssessmentsFiltered.length, dot: null, color: '#60a5fa' },
                  { id: 'critical', label: 'Critical', count: criticalAccounts.length, dot: '#ef4444', color: '#ef4444' },
                  { id: 'critical+warning', label: 'Critical+Warn', count: criticalAccounts.length + warningAccounts.length, dot: null, dotTwo: ['#ef4444', '#f59e0b'], color: '#ef4444' },
                  { id: 'warning', label: 'Warning', count: warningAccounts.length, dot: '#f59e0b', color: '#f59e0b' },
                  { id: 'safe', label: 'Safe', count: safeAccounts.length, dot: '#10b981', color: '#10b981' },
                  { id: 'no-strategy', label: 'No Strat', count: noStrategyAccounts.length, dot: '#9ca3af', color: '#9ca3af' }
                ].map(({ id, label, count, dot, dotTwo, color }) => (
                  <button
                    key={id}
                    className={`nt-filter-chip ${riskFilter === id ? 'active' : ''}`}
                    onClick={() => setRiskFilter(id)}
                    style={{
                      background: riskFilter === id ? `${color}20` : 'rgba(255,255,255,0.05)',
                      borderColor: riskFilter === id ? `${color}40` : 'rgba(255,255,255,0.1)',
                      color: riskFilter === id ? color : 'rgba(255,255,255,0.7)'
                    }}
                  >
                    {dotTwo ? (
                      <span className="nt-filter-dots" style={{ marginRight: '0.35rem' }}>
                        <span className="nt-filter-dot" style={{ background: dotTwo[0] }} />
                        <span className="nt-filter-dot" style={{ background: dotTwo[1] }} />
                      </span>
                    ) : dot ? (
                      <span className="nt-filter-dot" style={{ background: dot, marginRight: '0.35rem' }} />
                    ) : null}
                    {label} ({count})
                  </button>
                ))}
              </div>
              {/* Mobile: filter dropdown */}
              <div className="nt-filter-dropdown-wrap">
                <select
                  value={riskFilter}
                  onChange={(e) => setRiskFilter(e.target.value)}
                  className="nt-filter-dropdown"
                  aria-label="Filter by status"
                >
                  <option value="all">All ({riskAssessmentsFiltered.length})</option>
                  <option value="critical">Critical ({criticalAccounts.length})</option>
                  <option value="critical+warning">Critical+Warn ({criticalAccounts.length + warningAccounts.length})</option>
                  <option value="warning">Warning ({warningAccounts.length})</option>
                  <option value="safe">Safe ({safeAccounts.length})</option>
                  <option value="no-strategy">No Strat ({noStrategyAccounts.length})</option>
                </select>
              </div>
            </div>

            {/* Row 2: Action bar - Columns, Updated, actions, view toggle */}
            <div className="nt-action-bar">
              <div className="nt-action-bar-left">
                {riskViewMode === 'table' && (
                  <div className="nt-action-columns-wrap" style={{ position: 'relative' }}>
                    <button
                      className="nt-action-btn"
                      onClick={() => setShowRiskColumnMenu(!showRiskColumnMenu)}
                      title="Toggle columns"
                    >
                      Columns ▼
                    </button>
                    {showRiskColumnMenu && (
                      <div className="nt-action-columns-menu">
                        {['balance', 'trailingDD', 'distanceToBlowout'].map(col => (
                          <label key={col} className="nt-action-columns-label">
                            <input type="checkbox" checked={riskColumns[col] !== false} onChange={() => setRiskColumns(prev => ({ ...prev, [col]: !prev[col] }))} />
                            {col === 'balance' ? 'Balance' : col === 'trailingDD' ? 'Trailing DD' : 'Distance to Blowout'}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {riskLastUpdatedAt && (
                  <span className="nt-action-updated" title={new Date(riskLastUpdatedAt).toLocaleString()}>
                    Updated {(() => { const d = (Date.now() - riskLastUpdatedAt) / 1000; return d < 60 ? `${Math.round(d)}s ago` : d < 3600 ? `${Math.round(d/60)}m ago` : `${Math.round(d/3600)}h ago`; })()}
                  </span>
                )}
              </div>
              <div className="nt-action-bar-right">
                <div className="nt-action-buttons-group">
                  <button
                    className="nt-action-btn nt-action-btn-refresh"
                    onClick={() => fetchRiskAssessments()}
                    disabled={loadingRisk}
                    title="Refresh risk data"
                  >
                    {loadingRisk ? '…' : '↻'} Refresh
                  </button>
                <button
                  className="nt-action-btn nt-action-btn-export"
                  onClick={() => {
                    const header = ['Account', 'Balance', 'Trailing DD', 'Distance to Blowout', 'Risk Status', 'Risk Score', 'Strategy'];
                    const rows = filteredRisk.map(a => {
                      const p = a.strategies?.[0];
                      const t30 = a.strategies?.find(s => s.apexMaeComparison?.exceedsMae) || p;
                      const exceedPct = parseFloat(a.blowAccountProbability ?? a.strategies?.[0]?.blowAccountProbability ?? 0).toFixed(1);
                      const thirtyPct = t30?.apexMaeComparison?.maeBreachProbability != null
                        ? parseFloat(t30.apexMaeComparison.maeBreachProbability).toFixed(1) + '%'
                        : '—';
                      const riskStatus = `${a.blowAccountStatus || ''} (${exceedPct}% blow · ${thirtyPct} % rule)`;
                      return [
                        a.accountName || '',
                        formatCurrency(a.currentBalance),
                        a.maxDrawdown ? formatCurrency(a.maxDrawdown) : '-',
                        formatCurrency(a.distanceToBlowout),
                        riskStatus,
                        `${a.overallRiskScore || 0}/100`,
                        p ? `${p.strategyName} (${p.contracts}×${p.contractType})` : 'No Strategy'
                      ];
                    });
                    const csv = [header.join(','), ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `risk-summary-${new Date().toISOString().slice(0,10)}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  title="Export CSV"
                >
                  Export CSV
                </button>
                <button
                  className="nt-action-btn nt-action-btn-email"
                  onClick={async () => {
                    if (!user?.email || riskAssessmentsFiltered.length === 0 || emailSending) return;
                    setEmailSending(true);
                    setEmailError(null);
                    setEmailSent(false);
                    try {
                      const results = riskAssessmentsFiltered.flatMap(a => {
                        const strs = a.strategies?.length ? a.strategies : [{ strategyName: 'No Strategy', contracts: 1, contractType: 'NQ', riskScore: a.overallRiskScore, blowAccountStatus: a.blowAccountStatus, blowAccountProbability: null, apexMaeComparison: null }];
                        return strs.map(s => ({
                          accountName: a.accountName,
                          strategy: s.strategyName,
                          contracts: s.contracts || 1,
                          contractType: s.contractType || 'NQ',
                          currentBalance: a.currentBalance,
                          maxDrawdown: a.maxDrawdown,
                          metrics: { riskScore: s.riskScore ?? a.overallRiskScore, blowAccountStatus: s.blowAccountStatus ?? a.blowAccountStatus, blowAccountProbability: s.blowAccountProbability ?? a.blowAccountProbability ?? null },
                          apexMaeMetrics: s.apexMaeComparison ? { apexMaeComparison: s.apexMaeComparison } : {}
                        }));
                      });
                      const res = await fetch(API_ENDPOINTS.sendRiskSummary, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: user.email, results, riskMode: 'both' }) });
                      const data = await res.json();
                      if (data.emailSent) { setEmailSent(true); setEmailError(null); }
                      else { setEmailError(data.message || 'Email could not be sent'); }
                    } catch (err) { setEmailError(err.message || 'Failed to send'); }
                    finally { setEmailSending(false); }
                  }}
                  disabled={emailSending || !user?.email || riskAssessmentsFiltered.length === 0}
                  title="Email risk summary"
                >
                  {emailSending ? 'Sending…' : '✉ Email Summary'}
                </button>
                {emailSent && <span className="nt-action-sent" title={`Sent to ${user?.email}`}>✓ Sent</span>}
                {emailError && <span className="nt-action-error" title={emailError}>{emailError.slice(0, 30)}{emailError.length > 30 ? '…' : ''}</span>}
                </div>
                <div className="nt-view-toggle">
                  <span className="nt-view-label">View</span>
                  <button className={`nt-view-btn ${riskViewMode === 'table' ? 'active' : ''}`} onClick={() => setRiskViewMode('table')} title="Table View">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><line x1="1" y1="3" x2="15" y2="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><line x1="1" y1="7" x2="15" y2="7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><line x1="1" y1="11" x2="15" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  </button>
                  <button className={`nt-view-btn ${riskViewMode === 'cards' ? 'active' : ''}`} onClick={() => setRiskViewMode('cards')} title="Card View">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/><rect x="9" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/><rect x="1" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/><rect x="9" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* R10: Loading - skeleton loader vs simple spinner */}
        {loadingRisk && (
          <div>
            <div className="nt-risk-skeleton-cards">
              {[1,2,3,4].map(i => <div key={i} className="nt-risk-skeleton-card" />)}
            </div>
            <div className="nt-risk-skeleton">
              {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="nt-risk-skeleton-row" />)}
            </div>
            <p style={{ textAlign: 'center', marginTop: '1rem', color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>Calculating risk...</p>
          </div>
        )}

        {riskError && (
          <div className="nt-error-banner" style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem',
            color: '#ef4444'
          }}>
            <strong>Error:</strong> {riskError}
          </div>
        )}

        {/* R10: Clearer empty state */}
        {!loadingRisk && !riskError && filteredRisk.length === 0 && vectorSpreadsheetId && (
          <div className="nt-empty-state" style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="nt-empty-icon" style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
            <h3 style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
              {riskSearch.trim() ? 'No matches' : riskFilter !== 'all' ? 'No accounts match this filter' : 'No risk data'}
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '1rem' }}>
              {riskSearch.trim() ? 'Try a different search term.' : riskFilter !== 'all' ? 'Try "All" or another filter.' : 'Risk data will appear after connecting NinjaTrader accounts and configuring strategy results.'}
            </p>
            {riskSearch.trim() && <button onClick={() => setRiskSearch('')} style={{ padding: '0.5rem 1rem', background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.4)', borderRadius: '6px', color: '#60a5fa', cursor: 'pointer' }}>Clear search</button>}
            {riskFilter !== 'all' && !riskSearch.trim() && <button onClick={() => setRiskFilter('all')} style={{ padding: '0.5rem 1rem', background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.4)', borderRadius: '6px', color: '#60a5fa', cursor: 'pointer' }}>Show all accounts</button>}
          </div>
        )}

        {!loadingRisk && !riskError && filteredRisk.length > 0 && (
          <>
            {/* Card View */}
            {riskViewMode === 'cards' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1rem' }}>
                {filteredRisk.map((assessment, idx) => {
                const statusColor = 
                  assessment.blowAccountStatus === 'NO GO' ? '#ef4444' :
                  assessment.blowAccountStatus === 'CAUTION' ? '#f59e0b' :
                  '#10b981';

                // Get primary strategy info for compact view
                const primaryStrategy = assessment.strategies && assessment.strategies.length > 0 
                  ? assessment.strategies[0] 
                  : null;

                return (
                  <div
                    key={idx}
                    style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: `1px solid ${statusColor}40`,
                      borderLeft: `4px solid ${statusColor}`,
                      borderRadius: '8px',
                      padding: '1rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onClick={() => setSelectedRiskAccount(assessment)}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)'}
                  >
                    {/* Compact Card */}
                    <div style={{ marginBottom: '0.75rem' }}>
                      <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#fff' }}>
                        {assessment.accountName}
                      </h4>
                      <div style={{ fontSize: '0.8rem', color: primaryStrategy ? 'rgba(255,255,255,0.5)' : '#9ca3af', marginTop: '0.25rem' }}>
                        {primaryStrategy ? primaryStrategy.strategyName : 'No Strategy'}
                      </div>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.85rem' }}>
                      <div>
                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>Contracts</div>
                        <div style={{ color: '#fff', fontWeight: '600' }}>
                          {primaryStrategy ? `${primaryStrategy.contracts} × ${primaryStrategy.contractType}` : '-'}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>Balance</div>
                        <div style={{ color: '#fff', fontWeight: '600' }}>{formatCurrency(assessment.currentBalance)}</div>
                      </div>
                      <div>
                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>Trailing DD</div>
                        <div style={{ color: '#fff', fontWeight: '600' }}>
                          {assessment.maxDrawdown ? formatCurrency(assessment.maxDrawdown) : '-'}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>Distance to Blowout</div>
                        <div style={{ 
                          color: parseFloat(assessment.distanceToBlowout) < 0 ? '#ef4444' : parseFloat(assessment.distanceToBlowout) < 5000 ? '#f59e0b' : '#10b981',
                          fontWeight: '600'
                        }}>
                          {formatCurrency(assessment.distanceToBlowout)}
                        </div>
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', marginBottom: '0.25rem' }}>Risk Status</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '0.2rem 0.5rem',
                            background: `${statusColor}20`,
                            border: `1px solid ${statusColor}40`,
                            borderRadius: '4px',
                            color: statusColor,
                            fontSize: '0.75rem',
                            fontWeight: '700',
                            width: 'fit-content'
                          }}>
                            {assessment.blowAccountStatus}
                          </span>
                          <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>
                            {(() => {
                              const t30 = assessment.strategies?.find(s => s.apexMaeComparison?.exceedsMae) || primaryStrategy;
                              const exceedPct = parseFloat(assessment.blowAccountProbability || 0).toFixed(1);
                              const thirtyPct = t30?.apexMaeComparison?.maeBreachProbability != null ? parseFloat(t30.apexMaeComparison.maeBreachProbability).toFixed(1) + '%' : '—';
                              return (
                                <><span style={{ color: parseFloat(assessment.blowAccountProbability || 0) > 5 ? '#ef4444' : parseFloat(assessment.blowAccountProbability || 0) > 1 ? '#f59e0b' : '#10b981' }}>{exceedPct}%</span> blow · <span style={{ color: t30?.apexMaeComparison?.maeBreachProbability != null ? (t30.apexMaeComparison.maeBreachProbability > 0 ? '#f59e0b' : '#10b981') : 'rgba(255,255,255,0.5)' }}>{thirtyPct}</span> % rule</>
                              );
                            })()}
                          </span>
                          {assessment.missingTrailingDrawdown &&
                            String(assessment.blowAccountStatus || '').toUpperCase() === 'UNKNOWN' && (
                              <span style={{ fontSize: '0.75rem', color: '#f59e0b', lineHeight: 1.3 }}>
                                Trailing DD not provided by account connection. Status remains UNKNOWN.
                              </span>
                            )}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>Risk Score</div>
                        <div style={{ 
                          color: assessment.overallRiskScore > 70 ? '#ef4444' : assessment.overallRiskScore > 40 ? '#f59e0b' : '#10b981',
                          fontWeight: '600'
                        }}>
                          {assessment.overallRiskScore}/100
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ 
                      marginTop: '0.75rem', 
                      paddingTop: '0.75rem', 
                      borderTop: '1px solid rgba(255,255,255,0.1)',
                      fontSize: '0.75rem',
                      color: 'rgba(59, 130, 246, 0.8)',
                      textAlign: 'center'
                    }}>
                      Click for details ↗
                    </div>
                  </div>
                );
              })}
              </div>
            )}

            {/* R3: Table View - sticky header, clickable columns, R1 quick actions, R5 Critical emphasis */}
            {riskViewMode === 'table' && (
              <div className="nt-risk-table-wrapper">
                <table className="nt-risk-table">
                  <thead>
                    <tr>
                      <th className="nt-sortable" onClick={() => handleRiskSort('account')}>
                        Account {riskSortBy === 'account' && <span className="nt-sort-icon">{riskSortDirection === 'asc' ? '↑' : '↓'}</span>}
                      </th>
                      {riskColumns.balance !== false && (
                        <th className="nt-sortable" onClick={() => handleRiskSort('balance')}>
                          Balance {riskSortBy === 'balance' && <span className="nt-sort-icon">{riskSortDirection === 'asc' ? '↑' : '↓'}</span>}
                        </th>
                      )}
                      {riskColumns.trailingDD !== false && (
                        <th className="nt-sortable" onClick={() => handleRiskSort('trailingDD')}>
                          <HeaderInfoTooltip label="Trailing DD" tooltip="Maximum adverse excursion (drawdown) from equity peak." />
                          {riskSortBy === 'trailingDD' && <span className="nt-sort-icon">{riskSortDirection === 'asc' ? '↑' : '↓'}</span>}
                        </th>
                      )}
                      {riskColumns.distanceToBlowout !== false && (
                        <th className="nt-sortable" onClick={() => handleRiskSort('distanceToBlowout')}>
                          <HeaderInfoTooltip label="Distance to Blowout" tooltip="Buffer until trailing drawdown limit is breached. Red = past limit." />
                          {riskSortBy === 'distanceToBlowout' && <span className="nt-sort-icon">{riskSortDirection === 'asc' ? '↑' : '↓'}</span>}
                        </th>
                      )}
                      <th>
                        <HeaderInfoTooltip label="Risk Status" tooltip="GO/NO GO verdict with blowout probability (losses exceeding max drawdown) and % rule probability (consistency limit)." />
                      </th>
                      <th className="nt-sortable" onClick={() => handleRiskSort('riskScore')}>
                        <span onClick={(e) => e.stopPropagation()}><HeaderInfoTooltip label="Risk Score" tooltip="0-40 = Safe, 41-70 = Moderate, 71-100 = High. Based on historical loss vs max drawdown from strategy results." /></span>
                        {riskSortBy === 'riskScore' && <span className="nt-sort-icon">{riskSortDirection === 'asc' ? '↑' : '↓'}</span>}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRisk.map((assessment, idx) => {
                      const statusColor = assessment.blowAccountStatus === 'NO GO' ? '#ef4444' : assessment.blowAccountStatus === 'CAUTION' ? '#f59e0b' : '#10b981';
                      const primaryStrategy = assessment.strategies?.[0] || null;
                      const thirtyPercentStrategy = assessment.strategies?.find(s => s.apexMaeComparison?.exceedsMae) || primaryStrategy;
                      const distVal = parseFloat(assessment.distanceToBlowout) || 0;
                      const exceedVal = parseFloat(assessment.blowAccountProbability || 0);
                      const isCritical = assessment.blowAccountStatus === 'NO GO';
                      const riskConn = connections.find(c => c.account_name === assessment.accountName);
                      const usableStrategies = getUsableStrategies(riskConn);
                      const hasMultipleStrats = usableStrategies.length >= 2;
                      return (
                        <tr
                          key={idx}
                          className={isCritical ? 'nt-critical-row' : ''}
                          onClick={() => setSelectedRiskAccount(assessment)}
                        >
                          <td data-label="Account">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                              <strong style={{ color: '#fff', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                {assessment.accountName}
                                <button
                                  type="button"
                                  className="nt-copy-btn"
                                  onClick={(e) => copyAccountId(e, assessment.accountName)}
                                  title="Copy account name"
                                  style={{ background: 'none', border: 'none', padding: '0.2rem', cursor: 'pointer', opacity: 0.6 }}
                                >
                                  <IconCopy size={14} color="rgba(255,255,255,0.7)" />
                                </button>
                              </strong>
                              {primaryStrategy || usableStrategies.length > 0 || getDisplayStrategy(riskConn) ? (
                                <span
                                  style={{
                                    fontSize: '0.8rem',
                                    color: hasMultipleStrats ? '#60a5fa' : 'rgba(255,255,255,0.6)',
                                    cursor: hasMultipleStrats ? 'pointer' : 'default',
                                    textDecoration: hasMultipleStrats ? 'underline' : 'none'
                                  }}
                                  onClick={hasMultipleStrats ? (e) => {
                                    e.stopPropagation();
                                    setSelectedStrategyDetail({ strategies: usableStrategies, accountName: assessment.accountName });
                                    setStrategyDetailTab(0);
                                    setShowStrategyModal(true);
                                  } : undefined}
                                  title={hasMultipleStrats ? `Click to view ${usableStrategies.length} strategies` : undefined}
                                >
                                  {hasMultipleStrats
                                    ? 'Multiple Strats'
                                    : (primaryStrategy
                                        ? `${primaryStrategy.strategyName} • ${primaryStrategy.contracts}×${primaryStrategy.contractType}`
                                        : (() => {
                                            const ds = getDisplayStrategy(riskConn);
                                            const qty = ds?.quantity ? parseInt(ds.quantity) : null;
                                            const inst = ds?.instrument ? extractContractType(ds.instrument) : null;
                                            return `${ds?.strategy_name || '—'}${qty != null && inst ? ` • ${qty}×${inst}` : ''}`;
                                          })())}
                                </span>
                              ) : (
                                <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>No Strategy</span>
                              )}
                            </div>
                          </td>
                          {riskColumns.balance !== false && (
                            <td data-label="Balance">{formatCurrency(assessment.currentBalance)}</td>
                          )}
                          {riskColumns.trailingDD !== false && (
                            <td data-label="Trailing DD">{assessment.maxDrawdown ? formatCurrency(assessment.maxDrawdown) : '-'}</td>
                          )}
                          {riskColumns.distanceToBlowout !== false && (
                            <td data-label="Distance to Blowout" style={{ color: distVal < 0 ? '#ef4444' : distVal < 5000 ? '#f59e0b' : '#10b981', fontWeight: '600' }}>
                              {formatCurrency(assessment.distanceToBlowout)}
                            </td>
                          )}
                          <td data-label="Risk Status">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', alignItems: 'flex-start' }}>
                              <span
                                className="nt-info-tooltip-wrapper"
                                style={{ padding: '0.25rem 0.5rem', background: `${statusColor}20`, border: `1px solid ${statusColor}40`, borderRadius: '4px', color: statusColor, fontSize: '0.75rem', fontWeight: '700', cursor: 'help' }}
                                title={assessment.blowAccountStatus === 'NO GO' ? 'NO GO = past drawdown limit or high Exceed Probability. Distance to Blowout negative = past limit.' : assessment.blowAccountStatus === 'CAUTION' ? 'CAUTION = close to limit or elevated exceed probability.' : 'GO = within safe parameters.'}
                              >
                                {assessment.blowAccountStatus}
                              </span>
                              <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.3 }}>
                                <span style={{ color: exceedVal > 5 ? '#ef4444' : exceedVal > 1 ? '#f59e0b' : '#10b981' }}>{exceedVal.toFixed(1)}%</span> blow
                                <span style={{ color: 'rgba(255,255,255,0.5)', margin: '0 0.2rem' }}>·</span>
                                <span style={{ color: thirtyPercentStrategy?.apexMaeComparison?.maeBreachProbability != null ? (thirtyPercentStrategy.apexMaeComparison.maeBreachProbability > 0 ? '#f59e0b' : '#10b981') : 'rgba(255,255,255,0.5)' }}>
                                  {thirtyPercentStrategy?.apexMaeComparison?.maeBreachProbability != null
                                    ? parseFloat(thirtyPercentStrategy.apexMaeComparison.maeBreachProbability).toFixed(1) + '%'
                                    : '—'}
                                </span>
                                <span style={{ color: 'rgba(255,255,255,0.5)', marginLeft: '0.1rem' }}> % rule</span>
                              </span>
                              {assessment.missingTrailingDrawdown &&
                                String(assessment.blowAccountStatus || '').toUpperCase() === 'UNKNOWN' && (
                                  <span style={{ fontSize: '0.72rem', color: '#f59e0b', lineHeight: 1.3 }}>
                                    Missing trailing DD from connection; status is UNKNOWN.
                                  </span>
                                )}
                            </div>
                          </td>
                          <td data-label="Risk Score" style={{ color: assessment.overallRiskScore > 70 ? '#ef4444' : assessment.overallRiskScore > 40 ? '#f59e0b' : '#10b981', fontWeight: '600' }}>
                            {assessment.overallRiskScore}/100
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Health Score Explanation Modal */}
            {showHealthScoreModal && (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.85)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10000,
                  padding: '2rem'
                }}
                onClick={() => setShowHealthScoreModal(false)}
              >
                <div
                  style={{
                    background: '#1a1a2e',
                    borderRadius: '16px',
                    maxWidth: '600px',
                    width: '100%',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    position: 'relative',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6)',
                    padding: '2rem'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Close Button */}
                  <button
                    onClick={() => setShowHealthScoreModal(false)}
                    style={{
                      position: 'absolute',
                      top: '1rem',
                      right: '1rem',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      color: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.25rem',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                  >
                    ×
                  </button>

                  <h2 style={{ margin: '0 0 1.5rem 0', color: '#fff', fontSize: '1.5rem', fontWeight: '700' }}>
                    Health Score Explanation
                  </h2>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <p style={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: '1.6', marginBottom: '1rem' }}>
                      The Health Score is an overall indicator of your account portfolio's risk level. 
                      It's calculated by averaging the risk scores of all your accounts and converting it to a health metric.
                    </p>

                    <div style={{
                      background: 'rgba(59, 130, 246, 0.1)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '8px',
                      padding: '1rem',
                      marginBottom: '1rem'
                    }}>
                      <h3 style={{ color: '#60a5fa', fontSize: '1rem', fontWeight: '600', marginBottom: '0.75rem' }}>
                        Calculation Formula
                      </h3>
                      <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontFamily: 'monospace', fontSize: '0.9rem', lineHeight: '1.8' }}>
                        <div style={{ marginBottom: '0.5rem' }}>
                          <strong style={{ color: '#60a5fa' }}>Step 1:</strong> Calculate average risk score
                        </div>
                        <div style={{ 
                          background: 'rgba(0, 0, 0, 0.3)', 
                          padding: '0.75rem', 
                          borderRadius: '6px',
                          marginBottom: '0.75rem',
                          border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          Average Risk Score = Σ(Risk Scores) ÷ Number of Accounts
                        </div>
                        <div style={{ marginBottom: '0.5rem' }}>
                          <strong style={{ color: '#60a5fa' }}>Step 2:</strong> Convert to Health Score
                        </div>
                        <div style={{ 
                          background: 'rgba(0, 0, 0, 0.3)', 
                          padding: '0.75rem', 
                          borderRadius: '6px',
                          border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          Health Score = 100 - Average Risk Score
                        </div>
                      </div>
                    </div>

                    <div style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      padding: '1rem',
                      marginBottom: '1rem'
                    }}>
                      <h3 style={{ color: '#fff', fontSize: '1rem', fontWeight: '600', marginBottom: '0.75rem' }}>
                        Current Calculation
                      </h3>
                      <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem', lineHeight: '1.8' }}>
                        <div style={{ marginBottom: '0.5rem' }}>
                          Total Accounts: <strong>{riskAssessmentsFiltered.length}</strong>
                        </div>
                        <div style={{ marginBottom: '0.5rem' }}>
                          Sum of Risk Scores: <strong>{riskAssessmentsFiltered.reduce((sum, acc) => sum + (acc.overallRiskScore || 0), 0)}</strong>
                        </div>
                        <div style={{ marginBottom: '0.5rem' }}>
                          Average Risk Score: <strong>{riskAssessmentsFiltered.length > 0 ? (riskAssessmentsFiltered.reduce((sum, acc) => sum + (acc.overallRiskScore || 0), 0) / riskAssessmentsFiltered.length).toFixed(1) : 0}</strong>
                        </div>
                        <div style={{ 
                          marginTop: '0.75rem',
                          paddingTop: '0.75rem',
                          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                          fontSize: '1.1rem',
                          fontWeight: '600',
                          color: healthScore >= 70 ? '#10b981' : healthScore >= 40 ? '#f59e0b' : '#ef4444'
                        }}>
                          Health Score = 100 - {riskAssessmentsFiltered.length > 0 ? (riskAssessmentsFiltered.reduce((sum, acc) => sum + (acc.overallRiskScore || 0), 0) / riskAssessmentsFiltered.length).toFixed(1) : 0} = <strong>{healthScore}</strong>
                        </div>
                      </div>
                    </div>

                    <div style={{
                      background: 'rgba(16, 185, 129, 0.1)',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      borderRadius: '8px',
                      padding: '1rem',
                      marginBottom: '1rem'
                    }}>
                      <h3 style={{ color: '#10b981', fontSize: '1rem', fontWeight: '600', marginBottom: '0.75rem' }}>
                        Score Interpretation
                      </h3>
                      <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem', lineHeight: '1.8' }}>
                        <div style={{ marginBottom: '0.5rem' }}>
                          <strong style={{ color: '#10b981' }}>70-100:</strong> Healthy - Low overall risk across your portfolio
                        </div>
                        <div style={{ marginBottom: '0.5rem' }}>
                          <strong style={{ color: '#f59e0b' }}>40-69:</strong> Monitor - Moderate risk, keep an eye on critical accounts
                        </div>
                        <div>
                          <strong style={{ color: '#ef4444' }}>0-39:</strong> At Risk - High overall risk, immediate attention recommended
                        </div>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => setShowHealthScoreModal(false)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(59, 130, 246, 0.2)',
                      border: '1px solid rgba(59, 130, 246, 0.4)',
                      borderRadius: '6px',
                      color: '#60a5fa',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.3)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)'}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}

            {/* Risk Details Modal */}
            {selectedRiskAccount && (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.85)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 9999,
                  padding: '2rem'
                }}
                onClick={() => setSelectedRiskAccount(null)}
              >
                <div
                  style={{
                    background: '#1a1a2e',
                    borderRadius: '16px',
                    maxWidth: '900px',
                    width: '100%',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    position: 'relative',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6)'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Close Button */}
                  <button
                    onClick={() => setSelectedRiskAccount(null)}
                    style={{
                      position: 'absolute',
                      top: '1.5rem',
                      right: '1.5rem',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: 'none',
                      color: '#fff',
                      fontSize: '1.75rem',
                      width: '3rem',
                      height: '3rem',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background 0.2s',
                      zIndex: 10
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                  >
                    ×
                  </button>

                  <div style={{ padding: '2.5rem' }}>
                    {(() => {
                      const assessment = selectedRiskAccount;
                      const statusColor = 
                        assessment.blowAccountStatus === 'NO GO' ? '#ef4444' :
                        assessment.blowAccountStatus === 'CAUTION' ? '#f59e0b' :
                        '#10b981';

                      return (
                        <>
                          {/* Header with tabs */}
                          <div style={{ marginBottom: '2rem' }}>
                            <div style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.6)', marginBottom: '1rem' }}>
                              {assessment.accountName}
                            </div>
                            <div style={{
                              display: 'flex',
                              gap: '0.5rem',
                              borderBottom: '2px solid rgba(255,255,255,0.15)',
                              marginBottom: 0
                            }}>
                              {(() => {
                                const blowoutColor = assessment.blowAccountStatus === 'NO GO' ? '#ef4444' : assessment.blowAccountStatus === 'CAUTION' ? '#f59e0b' : '#10b981';
                                const thirtyExceeds = assessment.strategies?.some(s => s.apexMaeComparison?.exceedsMae);
                                const thirtyHasData = assessment.strategies?.some(s => s.apexMaeComparison);
                                const thirtyColor = !thirtyHasData ? 'rgba(255,255,255,0.5)' : thirtyExceeds ? '#ef4444' : '#10b981';
                                return (
                                  <>
                                    <button
                                      onClick={() => setRiskModalTab('blowout')}
                                      style={{
                                        padding: '0.75rem 1.25rem',
                                        background: riskModalTab === 'blowout' ? `${blowoutColor}30` : 'transparent',
                                        border: 'none',
                                        borderBottom: riskModalTab === 'blowout' ? `2px solid ${blowoutColor}` : '2px solid transparent',
                                        color: riskModalTab === 'blowout' ? '#fff' : 'rgba(255,255,255,0.7)',
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        marginBottom: '-2px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                      }}
                                    >
                                      Blowout Risk Results
                                      <span style={{ padding: '0.15rem 0.45rem', borderRadius: '4px', background: `${blowoutColor}40`, color: blowoutColor, fontSize: '0.75rem', fontWeight: 700 }}>
                                        {assessment.blowAccountStatus === 'CAUTION' ? 'CAUTION' : assessment.blowAccountStatus}
                                      </span>
                                    </button>
                                    <button
                                      onClick={() => setRiskModalTab('thirtyPercent')}
                                      style={{
                                        padding: '0.75rem 1.25rem',
                                        background: riskModalTab === 'thirtyPercent' ? (thirtyHasData ? `${thirtyColor}30` : 'rgba(99, 102, 241, 0.3)') : 'transparent',
                                        border: 'none',
                                        borderBottom: riskModalTab === 'thirtyPercent' ? `2px solid ${thirtyHasData ? thirtyColor : themeColor('#6366f1')}` : '2px solid transparent',
                                        color: riskModalTab === 'thirtyPercent' ? '#fff' : 'rgba(255,255,255,0.7)',
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        marginBottom: '-2px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                      }}
                                    >
                                      % Rule Risk
                                      <span style={{ padding: '0.15rem 0.45rem', borderRadius: '4px', background: thirtyHasData ? `${thirtyColor}40` : 'rgba(255,255,255,0.15)', color: thirtyHasData ? thirtyColor : 'rgba(255,255,255,0.6)', fontSize: '0.75rem', fontWeight: 700 }}>
                                        {thirtyHasData ? (thirtyExceeds ? 'NO GO' : 'GO') : '—'}
                                      </span>
                                    </button>
                                  </>
                                );
                              })()}
                            </div>
                          </div>

                          {riskModalTab === 'blowout' && (
                          <>
                          {/* Account Blowout Risk Indicator */}
                          <div className="blow-account-indicator" style={{
                            border: `2px solid ${statusColor}`,
                            borderRadius: '12px',
                            padding: '1.5rem',
                            marginBottom: '2rem',
                            backgroundColor: `${statusColor}15`,
                            backdropFilter: 'blur(10px)'
                          }}>
                            <div className="blow-account-status" style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              marginBottom: '1rem',
                              flexWrap: 'wrap',
                              gap: '1rem'
                            }}>
                              <span className="blow-account-label" style={{
                                fontSize: '1rem',
                                color: 'rgba(255, 255, 255, 0.7)',
                                fontWeight: 500
                              }}>
                                Account Blowout Risk:
                              </span>
                              <span className="blow-account-value" style={{
                                fontSize: '2rem',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '2px',
                                color: statusColor
                              }}>
                                {assessment.blowAccountStatus}
                              </span>
                            </div>

                            {assessment.strategies && assessment.strategies.length > 0 && (
                              <div className="contract-type-badge" style={{
                                display: 'inline-block',
                                background: 'rgba(255, 255, 255, 0.1)',
                                padding: '0.25rem 0.75rem',
                                borderRadius: '6px',
                                fontSize: '0.85rem',
                                color: 'rgba(255, 255, 255, 0.8)',
                                marginBottom: '0.75rem'
                              }}>
                                Contract Type: <strong style={{ color: '#fff', fontWeight: 600 }}>
                                  {assessment.strategies[0].contractType}
                                </strong>
                              </div>
                            )}

                            <p className="blow-account-message" style={{
                              color: 'rgba(255, 255, 255, 0.9)',
                              fontSize: '1.1rem',
                              lineHeight: 1.6,
                              marginBottom: '0.5rem'
                            }}>
                              {assessment.blowAccountMessage}
                            </p>
                            {assessment.missingTrailingDrawdown &&
                              String(assessment.blowAccountStatus || '').toUpperCase() === 'UNKNOWN' && (
                                <p style={{
                                  color: '#f59e0b',
                                  fontSize: '0.95rem',
                                  lineHeight: 1.5,
                                  marginTop: '0.25rem',
                                  marginBottom: '0.75rem',
                                }}>
                                  {assessment.missingTrailingDrawdownNote || 'Trailing drawdown was not provided by this account connection, so a precise GO/NO GO status cannot be calculated.'}
                                </p>
                              )}

                            {assessment.blowAccountProbability !== null && assessment.blowAccountProbability !== undefined && (
                              <div className="blow-account-probability" style={{
                                color: 'rgba(255, 255, 255, 0.8)',
                                fontSize: '0.95rem',
                                marginTop: '0.5rem'
                              }}>
                                Probability of end-of-day losses exceeding max drawdown: <strong style={{
                                  fontSize: '1.1rem',
                                  color: '#fff'
                                }}>
                                  {parseFloat(assessment.blowAccountProbability).toFixed(1)}%
                                </strong>
                                <div style={{ 
                                  marginTop: '0.75rem', 
                                  padding: '0.75rem', 
                                  background: 'rgba(245, 158, 11, 0.15)', 
                                  borderRadius: '8px', 
                                  border: '1px solid rgba(245, 158, 11, 0.3)', 
                                  fontSize: '0.9em' 
                                }}>
                                  <strong>⚠ Important:</strong> Trailing drawdown is based on <strong>intraday drawdown movement</strong>, not end-of-day P&amp;L. A trade could close positive but still blow the account if it exceeded the drawdown limit during the day. This analysis uses end-of-day data, so <strong>actual risk may be higher</strong> than shown.
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Risk Score Display */}
                          <div className="dashboard-header" style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '2rem',
                            flexWrap: 'wrap',
                            gap: '1rem'
                          }}>
                            <div className="risk-score-display" style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}>
                              <span className="risk-score-label" style={{
                                color: 'rgba(255, 255, 255, 0.7)',
                                fontSize: '0.9rem'
                              }}>
                                Risk Score:
                              </span>
                              <span className="risk-score-value" style={{
                                fontSize: '1.5rem',
                                fontWeight: 700,
                                color: assessment.overallRiskScore > 70 ? '#ef4444' : assessment.overallRiskScore > 40 ? '#f59e0b' : '#10b981'
                              }}>
                                {assessment.overallRiskScore}/100
                              </span>
                            </div>
                            <div style={{
                              padding: '0.6rem 1.2rem',
                              background: assessment.overallRiskScore > 70 ? 'rgba(239, 68, 68, 0.2)' : assessment.overallRiskScore > 40 ? 'rgba(245, 158, 11, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                              border: assessment.overallRiskScore > 70 ? '1px solid rgba(239, 68, 68, 0.4)' : assessment.overallRiskScore > 40 ? '1px solid rgba(245, 158, 11, 0.4)' : '1px solid rgba(16, 185, 129, 0.4)',
                              borderRadius: '8px',
                              color: assessment.overallRiskScore > 70 ? '#ef4444' : assessment.overallRiskScore > 40 ? '#f59e0b' : '#10b981',
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              textTransform: 'uppercase'
                            }}>
                              {assessment.overallRiskScore > 70 ? 'HIGH RISK' : assessment.overallRiskScore > 40 ? 'MODERATE RISK' : 'LOW RISK'}
                            </div>
                          </div>

                          {/* Metrics Grid - same 8 cards as Blowout Risk Analysis (from historical Vector Results) */}
                          {(() => {
                            const primaryStrat = assessment.strategies && assessment.strategies.length > 0
                              ? assessment.strategies.reduce((worst, s) => (s.riskScore || 0) > (worst.riskScore || 0) ? s : worst, assessment.strategies[0])
                              : null;
                            const contracts = primaryStrat ? primaryStrat.contracts : 1;
                            const cardStyle = {
                              background: 'rgba(255, 255, 255, 0.03)',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              borderRadius: '12px',
                              padding: '1.25rem',
                              textAlign: 'center'
                            };
                            const labelStyle = { fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' };
                            const valueStyle = (color) => ({ fontSize: '1.5rem', fontWeight: 700, color: color || '#fff' });
                            const subStyle = { fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.25rem' };

                            if (!primaryStrat) {
                              return (
                                <div style={{ marginBottom: '2rem', color: 'rgba(255,255,255,0.6)' }}>
                                  No strategy data available for historical metrics.
                                </div>
                              );
                            }

                            const drawdownBreach = primaryStrat.drawdownBreach;
                            const maxDdValue = assessment.maxDrawdown || (drawdownBreach && drawdownBreach.maxDrawdown);
                            const bufferVal = drawdownBreach && (drawdownBreach.buffer != null ? drawdownBreach.buffer : drawdownBreach.margin);

                            return (
                              <div className="metrics-grid" style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '1.5rem',
                                marginBottom: '2rem'
                              }}>
                                <div className="metric-card" style={cardStyle}>
                                  <div style={labelStyle}>Risk Score</div>
                                  <div style={valueStyle(assessment.overallRiskScore > 70 ? '#ef4444' : assessment.overallRiskScore > 40 ? '#f59e0b' : '#10b981')}>
                                    {primaryStrat.riskScore}/100
                                  </div>
                                  <div style={subStyle}>{primaryStrat.riskLevel ? primaryStrat.riskLevel.replace(/^./, c => c.toUpperCase()) : (assessment.overallRiskScore < 40 ? 'Low Risk' : assessment.overallRiskScore < 70 ? 'Moderate Risk' : 'High Risk')}</div>
                                </div>

                                <div className="metric-card" style={cardStyle}>
                                  <div style={labelStyle}>Highest Loss</div>
                                  <div style={valueStyle('#ef4444')}>
                                    {formatCurrency(parseFloat(primaryStrat.worstLossForSize || 0))}
                                  </div>
                                  <div style={subStyle}>
                                    {primaryStrat.worstLossPercent}% of account ({contracts} contract{contracts !== 1 ? 's' : ''})
                                  </div>
                                </div>

                                <div className="metric-card" style={cardStyle}>
                                  <div style={labelStyle}>Average Loss</div>
                                  <div style={valueStyle('#ef4444')}>
                                    {formatCurrency(parseFloat(primaryStrat.avgLossForSize || 0))}
                                  </div>
                                  <div style={subStyle}>
                                    {primaryStrat.avgLossPercent}% of account ({contracts} contract{contracts !== 1 ? 's' : ''})
                                  </div>
                                </div>

                                <div className="metric-card" style={cardStyle}>
                                  <div style={labelStyle}>Max Profit</div>
                                  <div style={valueStyle('#10b981')}>
                                    {formatCurrency(parseFloat(primaryStrat.maxProfitForSize || 0))}
                                  </div>
                                  <div style={subStyle}>
                                    {primaryStrat.maxProfitForSize && assessment.accountSize
                                      ? (parseFloat(primaryStrat.maxProfitForSize) / assessment.accountSize * 100).toFixed(2) + '% of account'
                                      : ''} ({contracts} contract{contracts !== 1 ? 's' : ''})
                                  </div>
                                </div>

                                <div className="metric-card" style={cardStyle}>
                                  <div style={labelStyle}>Average Profit</div>
                                  <div style={valueStyle('#10b981')}>
                                    {formatCurrency(parseFloat(primaryStrat.avgProfitForSize || 0))}
                                  </div>
                                  <div style={subStyle}>
                                    {primaryStrat.avgProfitPercent}% of account ({contracts} contract{contracts !== 1 ? 's' : ''})
                                  </div>
                                </div>

                                <div className="metric-card" style={cardStyle}>
                                  <div style={labelStyle}>Per-Contract Loss</div>
                                  <div style={valueStyle('#ef4444')}>
                                    {formatCurrency(parseFloat(primaryStrat.worstLossPerContract || 0))}
                                  </div>
                                  <div style={subStyle}>Worst loss per contract</div>
                                </div>

                                <div className="metric-card" style={cardStyle}>
                                  <div style={labelStyle}>Trading Days Analyzed</div>
                                  <div style={valueStyle()}>
                                    {primaryStrat.totalTradingDays != null ? primaryStrat.totalTradingDays : '-'}
                                  </div>
                                  <div style={subStyle}>
                                    {primaryStrat.totalProfitDays != null && primaryStrat.totalLossDays != null
                                      ? `${primaryStrat.totalProfitDays} winning, ${primaryStrat.totalLossDays} losing days`
                                      : `From ${getResultsNavLabel()}`}
                                  </div>
                                </div>

                                <div className="metric-card" style={cardStyle}>
                                  <div style={labelStyle}>Max Drawdown Analysis</div>
                                  <div style={valueStyle()}>
                                    {maxDdValue ? formatCurrency(parseFloat(maxDdValue)) : '-'}
                                  </div>
                                  <div style={subStyle}>
                                    {drawdownBreach && drawdownBreach.breaches > 0
                                      ? `${drawdownBreach.breaches} breach(es) - ${drawdownBreach.breachProbability}% probability`
                                      : bufferVal != null
                                        ? `No breaches - ${formatCurrency(parseFloat(bufferVal))} buffer`
                                        : assessment.maxDrawdown ? 'From NT8 Data' : 'Tier-based'}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Footer message - same as Blowout Risk Analysis */}
                          {assessment.strategies && assessment.strategies.length > 0 && (() => {
                            const s = assessment.strategies.reduce((w, x) => (x.riskScore || 0) > (w.riskScore || 0) ? x : w, assessment.strategies[0]);
                            return (
                              <div style={{
                                background: 'rgba(255, 255, 255, 0.03)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: '12px',
                                padding: '1rem 1.25rem',
                                marginBottom: '2rem',
                                color: 'rgba(255, 255, 255, 0.9)',
                                fontSize: '1rem',
                                lineHeight: 1.6
                              }}>
                                {s.riskMessage || `Your current setup appears ${s.blowAccountStatus === 'GO' ? 'safe' : 'at risk'}. Highest historical loss was ${formatCurrency(parseFloat(s.worstLossForSize || 0))} (${s.worstLossPercent}% of account) with ${s.contracts} contract(s).`}
                              </div>
                            );
                          })()}

                          {/* Strategies */}
                          {assessment.strategies && assessment.strategies.length > 0 && (
                            <div>
                              <h3 style={{ 
                                fontSize: '1.25rem', 
                                fontWeight: 600, 
                                color: '#fff', 
                                marginBottom: '1rem' 
                              }}>
                                Strategies ({assessment.strategies.length})
                              </h3>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {assessment.strategies.map((strat, sidx) => (
                                  <div
                                    key={sidx}
                                    style={{
                                      background: 'rgba(255,255,255,0.05)',
                                      border: '1px solid rgba(255,255,255,0.1)',
                                      borderRadius: '8px',
                                      padding: '1rem 1.25rem',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center'
                                    }}
                                  >
                                    <div>
                                      <div style={{ fontWeight: 600, color: '#fff', fontSize: '1rem' }}>
                                        {strat.strategyName}
                                      </div>
                                      <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.35rem' }}>
                                        {strat.contracts} contracts • {strat.contractType}
                                      </div>
                                    </div>
                                    <div style={{
                                      padding: '0.5rem 1rem',
                                      background: strat.riskLevel === 'high' ? 'rgba(239, 68, 68, 0.2)' : strat.riskLevel === 'moderate' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                                      border: strat.riskLevel === 'high' ? '1px solid rgba(239, 68, 68, 0.4)' : strat.riskLevel === 'moderate' ? '1px solid rgba(245, 158, 11, 0.4)' : '1px solid rgba(16, 185, 129, 0.4)',
                                      borderRadius: '6px',
                                      color: strat.riskLevel === 'high' ? '#ef4444' : strat.riskLevel === 'moderate' ? '#f59e0b' : '#10b981',
                                      fontSize: '0.85rem',
                                      fontWeight: 600,
                                      textTransform: 'uppercase'
                                    }}>
                                      {strat.riskLevel}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* R7: Risk Management Considerations */}
                          <div className="nt-modal-section">
                            <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#fff', marginBottom: '0.75rem' }}>Risk Management Considerations</h3>
                            <ul className="nt-recommended-actions">
                              {assessment.blowAccountStatus === 'NO GO' && (
                                <>
                                  <li><span className="nt-action-icon">⚠</span> Historical drawdown metrics suggest this setup may carry elevated risk relative to your selected limits.</li>
                                  <li><span className="nt-action-icon">📊</span> Consider reviewing strategy performance in {getResultsNavLabel()}, contract size, and drawdown tolerance before continuing.</li>
                                  <li><span className="nt-action-icon">🎯</span> A smaller contract size may provide more room for historical drawdown variation.</li>
                                </>
                              )}
                              {assessment.blowAccountStatus === 'CAUTION' && (
                                <>
                                  <li><span className="nt-action-icon">👀</span> Historical results suggest this account is operating closer to the selected drawdown limit.</li>
                                  <li><span className="nt-action-icon">📉</span> Consider reviewing contract size and drawdown tolerance if volatility increases.</li>
                                </>
                              )}
                              {assessment.blowAccountStatus === 'GO' && (
                                <li><span className="nt-action-icon">✓</span> Historical results suggest this account remains within the selected risk thresholds, though ongoing monitoring is still important.</li>
                              )}
                              {(!assessment.strategies || assessment.strategies.length === 0) && (
                                <li><span className="nt-action-icon">🔧</span> Enable strategy in NinjaTrader to sync and calculate risk.</li>
                              )}
                            </ul>
                          </div>

                          {/* R7: Similar Accounts (same strategy) */}
                          {assessment.strategies?.[0]?.strategyName && (() => {
                            const stratName = assessment.strategies[0].strategyName;
                            const similar = riskAssessmentsFiltered.filter(a => a !== assessment && a.strategies?.some(s => s.strategyName === stratName)).slice(0, 5);
                            if (similar.length === 0) return null;
                            return (
                              <div className="nt-modal-section">
                                <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#fff', marginBottom: '0.75rem' }}>Same Strategy ({stratName})</h3>
                                <div className="nt-similar-accounts-list">
                                  {similar.map((a, i) => (
                                    <div key={i} className="nt-similar-account-item" onClick={() => { setSelectedRiskAccount(a); }}>
                                      <span>{a.accountName}</span>
                                      <span style={{ color: a.overallRiskScore > 70 ? '#ef4444' : a.overallRiskScore > 40 ? '#f59e0b' : '#10b981', fontWeight: 600 }}>{a.overallRiskScore}/100</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                          </>
                          )}

                          {riskModalTab === 'thirtyPercent' && (() => {
                            const thirtyStrat = assessment.strategies?.find(s => s.apexMaeComparison) || assessment.strategies?.[0];
                            const apex = thirtyStrat?.apexMaeComparison;
                            if (!apex) {
                              return (
                                <div style={{ padding: '2rem', color: 'rgba(255,255,255,0.6)', textAlign: 'center' }}>
                                  No % Rule data available for this account.
                                </div>
                              );
                            }
                            const statusColor = apex.exceedsMae ? '#ef4444' : '#10b981';
                            return (
                              <>
                                <div style={{
                                  border: `2px solid ${statusColor}`,
                                  borderRadius: '12px',
                                  padding: '1.5rem',
                                  marginBottom: '2rem',
                                  backgroundColor: `${statusColor}15`
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
                                    <span style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>% Rule Status:</span>
                                    <span style={{ fontSize: '1.5rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '2px', color: statusColor }}>
                                      {apex.exceedsMae ? 'NO GO' : 'GO'}
                                    </span>
                                  </div>
                                  <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1rem', lineHeight: 1.6, marginBottom: '0.75rem' }}>
                                    {apex.maeMessage}
                                  </p>
                                  {apex.maeBreachProbability != null && (
                                    <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.95rem' }}>
                                      Probability of exceeding consistency limit: <strong style={{ fontSize: '1.1rem', color: apex.maeBreachProbability > 0 ? '#f59e0b' : '#fff' }}>
                                        {parseFloat(apex.maeBreachProbability).toFixed(1)}%
                                      </strong>
                                      {apex.maeBreaches != null && apex.totalTradingDays != null && apex.maeBreaches > 0 && (
                                        <span style={{ marginLeft: '0.5rem' }}>
                                          ({apex.maeBreaches} of {apex.totalTradingDays} trading days exceeded the limit)
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                                  <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '1.25rem', textAlign: 'center' }}>
                                    <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Consistency Limit</div>
                                    <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{apex.maxMaePerTrade != null ? formatCurrency(parseFloat(apex.maxMaePerTrade)) : '—'}</div>
                                  </div>
                                  <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '1.25rem', textAlign: 'center' }}>
                                    <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Worst Loss</div>
                                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#ef4444' }}>{apex.worstLossForSize != null ? formatCurrency(parseFloat(apex.worstLossForSize)) : '—'}</div>
                                  </div>
                                  <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '1.25rem', textAlign: 'center' }}>
                                    <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Buffer</div>
                                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: parseFloat(apex.maeBuffer || 0) >= 0 ? '#10b981' : '#ef4444' }}>
                                      {apex.maeBuffer != null ? formatCurrency(parseFloat(apex.maeBuffer)) : '—'}
                                    </div>
                                  </div>
                                  {apex.totalTradingDays != null && (
                                    <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '1.25rem', textAlign: 'center' }}>
                                      <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Trading Days</div>
                                      <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{apex.totalTradingDays}</div>
                                    </div>
                                  )}
                                </div>
                                {thirtyStrat?.apexWindfallRiskComparison && (
                                  <div style={{
                                    marginTop: '1.5rem',
                                    padding: '1.25rem',
                                    background: 'rgba(255,255,255,0.03)',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    borderRadius: '12px'
                                  }}>
                                    <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#fff', marginBottom: '0.75rem' }}>Windfall Rule (Payout Path)</h3>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
                                      <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                                        <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.35rem' }}>Largest Safe Day</div>
                                        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#10b981' }}>
                                          {formatCurrency(parseFloat(thirtyStrat.apexWindfallRiskComparison.largestSafeDay))}
                                        </div>
                                        <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>For fastest payout at $2,600</div>
                                      </div>
                                      <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                                        <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.35rem' }}>Strategy Highest Day</div>
                                        <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>
                                          {formatCurrency(parseFloat(thirtyStrat.apexWindfallRiskComparison.maxProfitDay || 0))}
                                        </div>
                                      </div>
                                      <div style={{ background: thirtyStrat.apexWindfallRiskComparison.exceedsWindfall ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)', border: `1px solid ${thirtyStrat.apexWindfallRiskComparison.exceedsWindfall ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)'}`, borderRadius: '8px', padding: '1rem', textAlign: 'center' }}>
                                        <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.35rem' }}>Payout Path</div>
                                        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: thirtyStrat.apexWindfallRiskComparison.exceedsWindfall ? '#ef4444' : '#10b981' }}>
                                          {thirtyStrat.apexWindfallRiskComparison.windfallStatus === 'SAFE' ? 'ALIGNED' : 'DELAYED'}
                                        </div>
                                      </div>
                                    </div>
                                    <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', lineHeight: 1.5, margin: 0 }}>
                                      {thirtyStrat.apexWindfallRiskComparison.windfallMessage}
                                    </p>
                                  </div>
                                )}
                                {assessment.strategies && assessment.strategies.filter(s => s.apexMaeComparison).length > 1 && (
                                  <div style={{ marginTop: '2rem' }}>
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#fff', marginBottom: '0.75rem' }}>Per Strategy</h3>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                      {assessment.strategies.filter(s => s.apexMaeComparison).map((s, i) => (
                                        <div key={i} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <span style={{ fontWeight: 600 }}>{s.strategyName}</span>
                                          <span style={{ color: s.apexMaeComparison.exceedsMae ? '#ef4444' : '#10b981', fontWeight: 600 }}>
                                            {s.apexMaeComparison.exceedsMae ? 'NO GO' : 'GO'}
                                            {s.apexMaeComparison.maeBreachProbability != null && ` (${parseFloat(s.apexMaeComparison.maeBreachProbability).toFixed(1)}%)`}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        {renderStrategyDetailModal()}
      </div>
    );
  }

  if (viewMode === 'current') {
    const currentViewConnections = activeConnections;
    // For metrics only: exclude accounts marked exclude_from_metrics (matches Accounts page)
    const currentMetricsConnections = metricsConnections;

    // Helper to format relative time
    const formatRelativeTime = (dateString) => {
      if (!dateString) return 'Never';
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays}d ago`;
    };

    // Get current week's day names
    const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    const dayAbbrev = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
    const todayET = getCurrentETWeekday();

    // Pre-compute weekly P&L for each account from actual current-week trades
    // For today (ET), prefer realized_pnl when available (aligns with NinjaTrader/My Trade History)
    const weeklyPnLMap = {};
    currentViewConnections.forEach(conn => {
      const accountTrades = trades[conn.account_name];
      let weekSum = 0;
      const realizedPnl = Number(conn.realized_pnl);
      const hasRealizedPnl = Number.isFinite(realizedPnl) && dayOrder.includes(todayET);
      if (accountTrades) {
        dayOrder.forEach(day => {
          const dayTrades = accountTrades[day] || [];
          const tradeSum = dayTrades.reduce((sum, trade) => sum + (parseFloat(trade.net_pnl) || 0), 0);
          if (day === todayET && hasRealizedPnl) {
            weekSum += realizedPnl;
          } else {
            weekSum += tradeSum;
          }
        });
      }
      weeklyPnLMap[conn.id] = weekSum;
    });

    const currentWeekTotalPnl = currentMetricsConnections.reduce((sum, c) => sum + (weeklyPnLMap[c.id] || 0), 0);

    // Calculate metrics from current-week P&L (exclude flat/zero accounts from win rate)
    const winningAccounts = currentMetricsConnections.filter(c => (weeklyPnLMap[c.id] || 0) > 0).length;
    const losingAccounts = currentMetricsConnections.filter(c => (weeklyPnLMap[c.id] || 0) < 0).length;
    const accountsWithPnL = winningAccounts + losingAccounts; // Only count accounts that traded
    const winRate = accountsWithPnL > 0 ? (winningAccounts / accountsWithPnL * 100).toFixed(1) : 0;

    // Filter connections by search term and filters
    let filteredConnections = currentViewConnections.filter(conn => {
      // Search filter
      const displayStrategy = getDisplayStrategy(conn);
      const matchesSearch = conn.account_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (displayStrategy?.strategy_name && displayStrategy.strategy_name.toLowerCase().includes(searchTerm.toLowerCase()));
      
      if (!matchesSearch) return false;
      
      // Status filter (Win/Loss) - use calculated true P&L
      const truePnL = calculateTruePnL(conn);
      if (statusFilter === 'winners' && truePnL <= 0) return false;
      if (statusFilter === 'losers' && truePnL >= 0) return false;
      
      // Strategy filter
      if (strategyFilter) {
        if (strategyFilter === 'Multiple Strats') {
          if (getUsableStrategies(conn).length < 2) return false;
        } else if (displayStrategy?.strategy_name !== strategyFilter) {
          return false;
        }
      }
      
      return true;
    });
    
    // Sort connections
    filteredConnections = [...filteredConnections].sort((a, b) => {
      let comparison = 0;
      
      if (sortBy === 'name') {
        comparison = a.account_name.localeCompare(b.account_name);
      } else if (sortBy === 'pnl') {
        // Use calculated true P&L for sorting
        const aPnl = calculateTruePnL(a);
        const bPnl = calculateTruePnL(b);
        comparison = aPnl - bPnl;
      } else if (sortBy === 'cash') {
        const aCash = a.starting_balance || 0;
        const bCash = b.starting_balance || 0;
        comparison = aCash - bCash;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    // Get unique strategies for filter dropdown (include "Multiple Strats" when account has 2+)
    const uniqueStrategies = [...new Set(
      currentViewConnections
        .map(c => {
          const usable = getUsableStrategies(c);
          if (usable.length >= 2) return 'Multiple Strats';
          return getDisplayStrategy(c)?.strategy_name;
        })
        .filter(Boolean)
    )];

    return (
      <div className="ninjatrader-dashboard-view">
        {/* Metric Cards */}
        {currentViewConnections.length > 0 && (
          <div className="nt-metrics-grid" style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            <div className="nt-metric-card" style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                Total Accounts
                <HeaderInfoTooltip
                  label=""
                  tooltip="Excludes accounts marked 'Exclude from Metrics' on the Accounts page."
                />
              </div>
              <div style={{ fontSize: '1.75rem', fontWeight: '600', color: '#fff' }}>
                {currentMetricsConnections.length}
              </div>
            </div>
            <div className="nt-metric-card" style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem' }}>
                Total P&L
              </div>
              <div style={{ 
                fontSize: '1.75rem', 
                fontWeight: '600', 
                color: currentWeekTotalPnl >= 0 ? '#10b981' : '#ef4444' 
              }}>
                {formatCurrency(currentWeekTotalPnl)}
              </div>
            </div>
            <div className="nt-metric-card" style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem' }}>
                Win Rate
              </div>
              <div style={{ 
                fontSize: '1.75rem', 
                fontWeight: '600', 
                color: winRate >= 50 ? '#10b981' : '#ef4444' 
              }}>
                {winRate}%
              </div>
            </div>
            <div className="nt-metric-card" style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem' }}>
                Winners / Losers
              </div>
              <div style={{ fontSize: '1.75rem', fontWeight: '600', color: '#fff' }}>
                <span style={{ color: '#10b981' }}>{winningAccounts}</span>
                {' / '}
                <span style={{ color: '#ef4444' }}>{losingAccounts}</span>
              </div>
            </div>
          </div>
        )}

        {/* Search Bar and Filters */}
        {currentViewConnections.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <input
              type="text"
              placeholder="Search accounts or strategies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="nt-search-input"
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '0.95rem',
                marginBottom: '1rem'
              }}
            />
            
            {/* Filters */}
            <div style={{ 
              display: 'flex', 
              gap: '0.75rem', 
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Accounts</option>
                <option value="winners">Winners Only</option>
                <option value="losers">Losers Only</option>
              </select>
              <select
                value={strategyFilter}
                onChange={(e) => setStrategyFilter(e.target.value)}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Strategies</option>
                {sortStrategies(uniqueStrategies).map(strategy => (
                  <option key={strategy} value={strategy}>{strategy}</option>
                ))}
              </select>
              
              {/* Sort Controls */}
              <div style={{ 
                display: 'flex', 
                gap: '0.5rem',
                alignItems: 'center',
                marginLeft: 'auto'
              }}>
                <span style={{ 
                  fontSize: '0.875rem', 
                  color: 'rgba(255, 255, 255, 0.6)',
                  whiteSpace: 'nowrap'
                }}>
                  Sort by:
                </span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  style={{
                    padding: '0.5rem 1rem',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '0.875rem',
                    cursor: 'pointer'
                  }}
                >
                  <option value="name">Account Name</option>
                  <option value="pnl">Weekly P&L</option>
                  <option value="cash">Cash Value</option>
                </select>
                <button
                  onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
                >
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </button>
              </div>
              
              {(statusFilter !== 'all' || strategyFilter !== '' || sortBy !== 'name' || sortDirection !== 'asc') && (
                <button
                  onClick={() => {
                    setStatusFilter('all');
                    setStrategyFilter('');
                    setSortBy('name');
                    setSortDirection('asc');
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '6px',
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Reset All
                </button>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="nt-loading">
            <div className="nt-spinner"></div>
            <p>Loading trading data...</p>
          </div>
        ) : (
          <div className="results-group-list">
            {filteredConnections.map((conn) => {
              const isExpanded = expandedCards.has(conn.id);
              
              // Calculate daily P&L from actual trades; for today (ET), prefer realized_pnl when available
              const dailyPnL = {};
              const dailyTrades = {};
              const connRealizedPnl = Number(conn.realized_pnl);
              const useRealizedForToday = Number.isFinite(connRealizedPnl) && dayOrder.includes(todayET);
              dayOrder.forEach(day => {
                dailyPnL[day] = 0;
                dailyTrades[day] = [];
              });
              
              const accountTrades = trades[conn.account_name];
              if (accountTrades) {
                dayOrder.forEach(day => {
                  const dayTrades = accountTrades[day] || [];
                  dailyTrades[day] = dayTrades;
                  const tradeSum = dayTrades.reduce((sum, trade) => sum + (parseFloat(trade.net_pnl) || 0), 0);
                  dailyPnL[day] = (day === todayET && useRealizedForToday) ? connRealizedPnl : tradeSum;
                });
              }
              
              // Weekly P&L = sum of this week's daily P&L (not all-time)
              const weeklyPnl = weeklyPnLMap[conn.id] || 0;
              const status = weeklyPnl > 0 ? 'WIN' : weeklyPnl < 0 ? 'LOSS' : 'FLAT';
              
              // If no trades data yet, check if we need to sync
              const hasTrades = Object.values(dailyTrades).some(arr => arr.length > 0);
              const hasWeeklyPnl = weeklyPnl !== 0;
              
              return (
                <div key={conn.id} className="strategy-card">
                  <div 
                    className="strategy-card-top" 
                    onClick={() => {
                      const newExpanded = new Set(expandedCards);
                      if (isExpanded) {
                        newExpanded.delete(conn.id);
                      } else {
                        newExpanded.add(conn.id);
                      }
                      setExpandedCards(newExpanded);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="left">
                      <p 
                        className="strategy-name nt-account-link" 
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent card expansion
                          console.log('Account name clicked:', conn.account_name);
                          fetchAccountTrades(conn.account_name);
                        }}
                        style={{ 
                          position: 'relative',
                          zIndex: 10
                        }}
                        title="Click to view trades"
                      >
                        {conn.account_name}
                      </p>
                      {(() => {
                        const usableStrategies = getUsableStrategies(conn);
                        const displayStrategy = getDisplayStrategy(conn);
                        const hasMultiple = usableStrategies.length >= 2;
                        if (usableStrategies.length === 0 && !displayStrategy) {
                          return <span className="category-pill" style={{ opacity: 0.5 }}>No Active Strategies</span>;
                        }
                        const stratToUse = usableStrategies[0] || displayStrategy;
                        const stratWithInstrument = usableStrategies.find(s => s.instrument && String(s.instrument).trim() !== '');
                        const instrument = stratWithInstrument?.instrument ?? (stratToUse?.instrument && String(stratToUse.instrument).trim() !== '' ? stratToUse.instrument : null);
                        const contractType = instrument ? extractContractType(instrument) : null;
                        const pillContent = hasMultiple ? 'Multiple Strats' : (stratToUse?.strategy_name || 'Unknown');
                        return (
                          <>
                            <span
                              className="category-pill"
                              style={hasMultiple ? { cursor: 'pointer', textDecoration: 'underline' } : {}}
                              onClick={hasMultiple ? (e) => {
                                e.stopPropagation();
                                setSelectedStrategyDetail({ strategies: usableStrategies, accountName: conn.account_name });
                                setStrategyDetailTab(0);
                                setShowStrategyModal(true);
                              } : undefined}
                              title={hasMultiple ? `Click to view ${usableStrategies.length} strategies` : undefined}
                            >
                              {pillContent}
                            </span>
                            {contractType && (
                              <span className="category-pill" style={{ marginLeft: '0.35rem', background: 'rgba(255,255,255,0.08)' }} title={instrument}>
                                {contractType}
                              </span>
                            )}
                          </>
                        );
                      })()}
                      <span className={`badge ${status.toLowerCase()}`}>{status}</span>
                    </div>
                    <div className="right">
                      <div style={{ textAlign: 'right' }}>
                        <span className={`weekly ${weeklyPnl >= 0 ? 'pos' : 'neg'}`}>
                          {formatCurrency(weeklyPnl)}
                        </span>
                        {conn.starting_balance !== null && conn.starting_balance !== undefined && (
                          <div style={{ 
                            fontSize: '0.75rem', 
                            color: 'rgba(255, 255, 255, 0.5)',
                            marginTop: '0.25rem'
                          }}>
                            Cash: {formatCurrency(conn.starting_balance)}
                          </div>
                        )}
                      </div>
                      <span className="chevron">{isExpanded ? '▴' : '▾'}</span>
                    </div>
                  </div>

                  <div className="day-chips">
                    {dayOrder.map((day, idx) => {
                      const val = dailyPnL[day] || 0;
                      const tone = val > 0 ? 'pos' : val < 0 ? 'neg' : 'flat';
                      const tradesCount = dailyTrades[day].length;
                      return (
                        <span 
                          key={day} 
                          className={`day-chip ${tone}`}
                          title={tradesCount > 0 ? `${tradesCount} trade${tradesCount !== 1 ? 's' : ''}` : 'No trades - click Sync NinjaTrader'}
                        >
                          {dayAbbrev[idx]} {val !== 0 ? formatCurrency(val) : ''}
                        </span>
                      );
                    })}
                  </div>
                  
                  {/* Message if no daily breakdown available */}
                  {!hasTrades && hasWeeklyPnl && (
                    <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' }}>
                      Click "Sync NinjaTrader" above to see daily P&L breakdown
                    </p>
                  )}

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="strategy-details">
                      <div className="details-grid">
                        {dayOrder.map((day) => {
                          const val = dailyPnL[day];
                          const tradesCount = dailyTrades[day].length;
                          return (
                            <div key={day} className="detail-row">
                              <span>{day} ({tradesCount} trade{tradesCount !== 1 ? 's' : ''})</span>
                              <span className={val >= 0 ? 'pos' : 'neg'}>
                                {formatCurrency(val)}
                              </span>
                            </div>
                          );
                        })}
                        <div className="detail-row total">
                          <span>Weekly Total</span>
                          <span className={weeklyPnl >= 0 ? 'pos' : 'neg'}>
                            {formatCurrency(weeklyPnl)}
                          </span>
                        </div>
                      </div>
                      <p className="interpretation" style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
                        <strong>Last synced:</strong> {formatRelativeTime(conn.last_sync_at)}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        {!loading && filteredConnections.length === 0 && (
          <div className="nt-empty-state">
            <p>{searchTerm ? 'No accounts match your search' : 'No trading activity this week'}</p>
          </div>
        )}
        
        {/* Trade Modal */}
        {renderTradeModal()}
        
        {/* Account Settings Modal */}
        {renderAccountSettingsModal()}
        {renderStrategyDetailModal()}
      </div>
    );
  }

  if (viewMode === 'history') {
    const handleHistoryAccountClick = (accountName, dateKey) => {
      const [y, m, d] = dateKey.split('-').map(Number);
      const startDate = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const endDate = startDate;
      fetchAccountTrades(accountName, { startDate, endDate });
    };
    return (
      <div className="ninjatrader-dashboard-view">
        <MyTradeHistory
          user={user}
          connections={connections}
          riskAssessments={riskAssessments}
          formatCurrency={formatCurrency}
          dateRangeFilter={mthDateRangeFilter}
          customStartDate={mthCustomStartDate}
          customEndDate={mthCustomEndDate}
          selectedAccountNames={mthSelectedAccounts === 'all' ? null : mthSelectedAccounts}
          onAccountClick={handleHistoryAccountClick}
          getUsableStrategies={getUsableStrategies}
          onOpenStrategyModal={(strategies, accountName) => {
            setSelectedStrategyDetail({ strategies, accountName });
            setStrategyDetailTab(0);
            setShowStrategyModal(true);
          }}
        />
        {renderTradeModal()}
        {renderStrategyDetailModal()}
      </div>
    );
  }

  // Summary View - full account management with all features
  return (
    <div className="ninjatrader-dashboard-view">
      {/* Quick Setup Modal */}
      {showQuickSetup && (
        <>
          <div className="nt-modal-overlay" onClick={() => setShowQuickSetup(false)} />
          <div className="nt-trades-modal" style={{ maxWidth: '600px', maxHeight: '85vh', overflowY: 'auto' }}>
            <div className="nt-modal-header">
              <h2 style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <IconZap size={20} /> RiskLo Connector Setup
              </h2>
              <button 
                className="nt-modal-close"
                onClick={() => setShowQuickSetup(false)}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '1.25rem' }}>
              <div style={{ 
                padding: '0.75rem', 
                background: 'rgba(59, 130, 246, 0.1)', 
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '6px',
                marginBottom: '1rem'
              }}>
                <p style={{ margin: 0, fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.9)', lineHeight: '1.4' }}>
                  <strong>Free NinjaTrader add-on</strong> that auto-syncs accounts, trades, and strategies every 15 seconds
                </p>
              </div>

              <h3 style={{ fontSize: '0.95rem', marginBottom: '0.75rem', color: '#fff' }}>📥 Installation Steps</h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                    <span style={{ 
                      background: 'rgba(59, 130, 246, 0.2)', 
                      color: '#60a5fa', 
                      width: '22px', 
                      height: '22px', 
                      borderRadius: '50%', 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontWeight: '600',
                      fontSize: '0.75rem',
                      flexShrink: 0
                    }}>1</span>
                    <strong style={{ color: '#fff', fontSize: '0.9rem' }}>Generate API Key</strong>
                  </div>
                  <p style={{ marginLeft: '1.75rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8rem', lineHeight: '1.4', margin: '0 0 0 1.75rem' }}>
                    Scroll down and click "Generate New API Key"
                  </p>
                </div>

                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                    <span style={{ 
                      background: 'rgba(59, 130, 246, 0.2)', 
                      color: '#60a5fa', 
                      width: '22px', 
                      height: '22px', 
                      borderRadius: '50%', 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontWeight: '600',
                      fontSize: '0.75rem',
                      flexShrink: 0
                    }}>2</span>
                    <strong style={{ color: '#fff', fontSize: '0.9rem' }}>Download Add-on</strong>
                  </div>
                  <a 
                    href={`${API_BASE_URL}/api/ninjatrader/connector/download`}
                    style={{
                      marginLeft: '1.75rem',
                      display: 'inline-block',
                      padding: '0.4rem 0.75rem',
                      background: 'rgba(59, 130, 246, 0.2)',
                      border: '1px solid rgba(59, 130, 246, 0.4)',
                      borderRadius: '4px',
                      color: '#60a5fa',
                      textDecoration: 'none',
                      fontSize: '0.8rem',
                      fontWeight: '500'
                    }}
                  >
                    ⬇️ Download RiskLoConnectorV2_v2.2.0.zip
                  </a>
                </div>

                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                    <span style={{ 
                      background: 'rgba(59, 130, 246, 0.2)', 
                      color: '#60a5fa', 
                      width: '22px', 
                      height: '22px', 
                      borderRadius: '50%', 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontWeight: '600',
                      fontSize: '0.75rem',
                      flexShrink: 0
                    }}>3</span>
                    <strong style={{ color: '#fff', fontSize: '0.9rem' }}>Import to NinjaTrader</strong>
                  </div>
                  <p style={{ marginLeft: '1.75rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8rem', lineHeight: '1.4', margin: '0 0 0 1.75rem' }}>
                    <code style={{ background: 'rgba(255,255,255,0.1)', padding: '0.15rem 0.4rem', borderRadius: '3px', fontSize: '0.75rem' }}>Tools → Import → NinjaScript Add-On</code> → Select ZIP
                  </p>
                </div>

                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                    <span style={{ 
                      background: 'rgba(59, 130, 246, 0.2)', 
                      color: '#60a5fa', 
                      width: '22px', 
                      height: '22px', 
                      borderRadius: '50%', 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontWeight: '600',
                      fontSize: '0.75rem',
                      flexShrink: 0
                    }}>4</span>
                    <strong style={{ color: '#fff', fontSize: '0.9rem' }}>Configure & Start</strong>
                  </div>
                  <p style={{ marginLeft: '1.75rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8rem', lineHeight: '1.4', margin: '0 0 0 1.75rem' }}>
                    <code style={{ background: 'rgba(255,255,255,0.1)', padding: '0.15rem 0.4rem', borderRadius: '3px', fontSize: '0.75rem' }}>Tools → RiskLo Connector</code> → Enter API key, set URL to <code style={{ background: 'rgba(255,255,255,0.1)', padding: '0.15rem 0.4rem', borderRadius: '3px', fontSize: '0.75rem' }}>https://risklo-staging.up.railway.app</code>, test, then "Start Syncing"
                  </p>
                </div>
              </div>

              <div style={{ 
                padding: '0.65rem', 
                background: 'rgba(34, 197, 94, 0.1)', 
                border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: '6px',
                marginTop: '1rem',
                marginBottom: '1rem'
              }}>
                <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.8)', lineHeight: '1.4' }}>
                  <strong style={{ color: '#22c55e' }}>✅ Done!</strong> Accounts auto-sync every 15s. Enable strategies in NT8 to sync.
                </div>
              </div>

              <button
                onClick={() => setShowQuickSetup(false)}
                className="nt-btn nt-btn-primary"
                style={{ width: '100%', padding: '0.65rem' }}
              >
                Got it!
              </button>
            </div>
          </div>
        </>
      )}

      {renderStrategyDetailModal()}

      {/* Connected Accounts Section */}
      <div className="nt-accounts-section" ref={connectedAccountsRef}>

        {loading ? (
          <div className="nt-loading">
            <div className="nt-spinner"></div>
            <p>Loading connections...</p>
          </div>
        ) : connections.length === 0 ? (
          <>
            <div className="nt-empty-state">
              <div className="nt-empty-icon">📊</div>
              <h3>No Accounts Connected</h3>
              <p>Connect your NinjaTrader accounts below using the connector add-on</p>
              {!connectButtonInParent && !showConnectForm && (
                <button 
                  className="nt-btn nt-btn-primary"
                  onClick={() => {
                    setConnectAccountMethod('risklo-connector');
                    setShowConnectForm(true);
                  }}
                >
                  Connect Accounts
                </button>
              )}
            </div>
            {/* Connect form when no accounts - as modal when connectButtonInParent */}
            {showConnectForm && (
              connectButtonInParent ? (
              <div className="nt-connect-modal-overlay" onClick={() => { setShowConnectForm(false); setConnectAccountMethod('risklo-connector'); }}>
              <div className="nt-connect-form-card nt-connect-modal" style={{ marginTop: 0, marginBottom: 0 }} onClick={(e) => e.stopPropagation()}>
                <div className="nt-form-header">
                  <h2>Connect Accounts</h2>
                  <button 
                    className="nt-form-close"
                    onClick={() => {
                      setShowConnectForm(false);
                      setConnectAccountMethod('risklo-connector');
                      setApiKey('');
                      setAccountNames(['']);
                      setUploadedFileName(null);
                    }}
                  >
                    ✕
                  </button>
                </div>

                <ConnectAccountMethodSelect
                  id="connect-method-no-accounts-modal"
                  value={connectAccountMethod}
                  onChange={setConnectAccountMethod}
                />
                {connectAccountMethod === 'risklo-connector' ? (
                <form onSubmit={handleBulkConnect} className="nt-form">
                  {/* API Key Generation for RiskLo Connector - No Accounts form */}
                  {apiProvider === 'risklo-connector' && (
                    <div className="nt-form-group" style={{
                      background: '#1e293b',
                      padding: '1.5rem',
                      borderRadius: '8px',
                      border: '2px solid #3b82f6',
                      marginBottom: '1.5rem'
                    }}>
                      <div style={{ marginBottom: '1rem' }}>
                        <h4 style={{
                          color: '#60a5fa',
                          fontSize: '1rem',
                          marginBottom: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          🔑 Generate Your API Key
                        </h4>
                        <p style={{ fontSize: '0.875rem', color: '#94a3b8', marginBottom: '1rem' }}>
                          Create a key here, then paste it into the RiskLo Connector add-on in NinjaTrader.
                        </p>
                      </div>
                      <div style={{ marginBottom: '1rem' }}>
                        <label htmlFor="keyNameNoAccounts" style={{
                          fontSize: '0.875rem',
                          color: '#cbd5e1',
                          display: 'block',
                          marginBottom: '0.5rem'
                        }}>
                          Key Name (Optional)
                        </label>
                        <input
                          type="text"
                          id="keyNameNoAccounts"
                          value={keyName}
                          onChange={(e) => setKeyName(e.target.value)}
                          placeholder="My Desktop PC"
                          className="nt-input"
                          style={{ fontSize: '0.875rem' }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleGenerateApiKey}
                        disabled={generatingKey}
                        className="nt-btn nt-btn-primary"
                        style={{
                          width: '100%',
                          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                          padding: '0.75rem',
                          fontSize: '0.95rem',
                          fontWeight: '600'
                        }}
                      >
                        {generatingKey ? (
                          <>
                            <div className="nt-btn-spinner"></div>
                            Generating...
                          </>
                        ) : (
                          '✨ Generate New API Key'
                        )}
                      </button>
                      {userApiKeys.length > 0 && (
                        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #334155' }}>
                          <h5 style={{ fontSize: '0.875rem', color: '#cbd5e1', marginBottom: '0.5rem', fontWeight: '600' }}>Your keys</h5>
                          {userApiKeys.filter(k => k.is_active).map((key) => (
                            <div
                              key={key.id}
                              style={{
                                background: '#0f172a',
                                padding: '0.75rem',
                                borderRadius: '6px',
                                border: '1px solid #334155',
                                marginBottom: '0.5rem',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '0.75rem'
                              }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>
                                  {key.key_name || 'Unnamed Key'}
                                </div>
                                <code style={{ fontSize: '0.75rem', color: '#e2e8f0', wordBreak: 'break-all' }}>
                                  {key.api_key ? `${key.api_key.substring(0, 8)}••••${key.api_key.slice(-4)}` : '••••••••'}
                                </code>
                              </div>
                              <button
                                type="button"
                                className="nt-btn nt-btn-secondary nt-btn-sm"
                                onClick={() => { setApiKey(key.api_key); copyToClipboard(key.api_key); }}
                                title="Use this key"
                              >
                                Use
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {apiProvider === 'risklo-connector' ? (
                    <div className="nt-form-group">
                      <label htmlFor="apiKeyFieldNoAccounts">Connector API Key</label>
                      <select
                        id="apiKeyFieldNoAccounts"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        required
                        className="nt-input"
                        disabled={loadingKeys || activeConnectorKeys.length === 0}
                      >
                        {activeConnectorKeys.length === 0 ? (
                          <option value="">{loadingKeys ? 'Loading keys...' : 'Generate a key above first'}</option>
                        ) : (
                          activeConnectorKeys.map((key) => (
                            <option key={key.id} value={key.api_key}>
                              {(key.key_name || 'Unnamed Key')} - {maskApiKey(key.api_key)}
                            </option>
                          ))
                        )}
                      </select>
                      <small className="nt-form-help">
                        Select the key you pasted into the NinjaTrader add-on.
                      </small>
                    </div>
                  ) : (
                    <div className="nt-form-group">
                      <label htmlFor="apiKeyFieldNoAccounts">CrossTrade Secret Key</label>
                      <input
                        type="password"
                        id="apiKeyFieldNoAccounts"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        placeholder="nlgSNZE4SEJCayNQDWTx9LUXFy4Q1ZfK..."
                        required
                        className="nt-input"
                      />
                      <small className="nt-form-help">
                        Find this in CrossTrade Dashboard → Manual Trader section
                      </small>
                    </div>
                  )}
                  <div className="nt-form-group">
                    <label>NinjaTrader Account Name(s)</label>
                    {accountNames.map((name, index) => (
                      <div key={index} className="nt-account-name-row">
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => handleAccountNameChange(index, e.target.value)}
                          placeholder={index === 0 ? 'e.g., Sim101' : `Account ${index + 1}`}
                          className="nt-input"
                        />
                        {accountNames.length > 1 && (
                          <button type="button" onClick={() => handleRemoveAccountField(index)} className="nt-btn nt-btn-danger nt-btn-sm">✕</button>
                        )}
                      </div>
                    ))}
                    <button type="button" onClick={handleAddAccountField} className="nt-btn nt-btn-secondary nt-btn-sm" style={{ marginTop: '0.5rem' }}>+ Add another account</button>
                  </div>
                  <div className="nt-form-actions">
                    <button type="button" className="nt-btn nt-btn-secondary" onClick={() => { setShowConnectForm(false); setConnectAccountMethod('risklo-connector'); setApiKey(''); setAccountNames(['']); setUploadedFileName(null); }}>Cancel</button>
                    <button type="submit" className="nt-btn nt-btn-primary" disabled={connecting}>
                      {connecting ? <> <span className="nt-btn-spinner"></span> Connecting...</> : 'Connect Accounts'}
                    </button>
                  </div>
                </form>
                ) : (
                  <div className="nt-form" style={{ paddingTop: 0 }}>
                    <TradovatePanel
                      user={user}
                      embedded
                      hideAccountsTable
                      onSessionChange={fetchConnections}
                    />
                    <div className="nt-form-actions" style={{ marginTop: '1rem' }}>
                      <button
                        type="button"
                        className="nt-btn nt-btn-secondary"
                        onClick={() => {
                          setShowConnectForm(false);
                          setConnectAccountMethod('risklo-connector');
                          setApiKey('');
                          setAccountNames(['']);
                          setUploadedFileName(null);
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
              </div>
            ) : (
              <div className="nt-connect-form-card" style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
                <div className="nt-form-header">
                  <h2>Connect Accounts</h2>
                  <button className="nt-form-close" onClick={() => { setShowConnectForm(false); setConnectAccountMethod('risklo-connector'); setApiKey(''); setAccountNames(['']); setUploadedFileName(null); }}>✕</button>
                </div>
                <ConnectAccountMethodSelect
                  id="connect-method-no-accounts-inline"
                  value={connectAccountMethod}
                  onChange={setConnectAccountMethod}
                />
                {connectAccountMethod === 'risklo-connector' ? (
                <form onSubmit={handleBulkConnect} className="nt-form">
                  {apiProvider === 'risklo-connector' && (
                    <div className="nt-form-group" style={{ background: '#1e293b', padding: '1.5rem', borderRadius: '8px', border: '2px solid #3b82f6', marginBottom: '1.5rem' }}>
                      <div style={{ marginBottom: '1rem' }}>
                        <h4 style={{ color: '#60a5fa', fontSize: '1rem', marginBottom: '0.5rem' }}>🔑 Generate Your API Key</h4>
                        <p style={{ fontSize: '0.875rem', color: '#94a3b8', marginBottom: '1rem' }}>Create a key here, then paste it into the RiskLo Connector add-on in NinjaTrader.</p>
                      </div>
                      <button type="button" onClick={handleGenerateApiKey} disabled={generatingKey} className="nt-btn nt-btn-primary" style={{ width: '100%', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', padding: '0.75rem', fontSize: '0.95rem', fontWeight: '600' }}>
                        {generatingKey ? <><div className="nt-btn-spinner"></div> Generating...</> : '✨ Generate New API Key'}
                      </button>
                    </div>
                  )}
                  {apiProvider === 'risklo-connector' ? (
                    <div className="nt-form-group">
                      <label htmlFor="apiKeyFieldNoAccountsInline">Connector API Key</label>
                      <select
                        id="apiKeyFieldNoAccountsInline"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        className="nt-input"
                        required
                        disabled={loadingKeys || activeConnectorKeys.length === 0}
                      >
                        {activeConnectorKeys.length === 0 ? (
                          <option value="">{loadingKeys ? 'Loading keys...' : 'Generate a key above first'}</option>
                        ) : (
                          activeConnectorKeys.map((key) => (
                            <option key={key.id} value={key.api_key}>
                              {(key.key_name || 'Unnamed Key')} - {maskApiKey(key.api_key)}
                            </option>
                          ))
                        )}
                      </select>
                      <small className="nt-form-help">
                        Select the key you pasted into the NinjaTrader add-on.
                      </small>
                    </div>
                  ) : (
                    <div className="nt-form-group">
                      <label htmlFor="apiKeyFieldNoAccountsInline">CrossTrade Secret Key</label>
                      <input
                        id="apiKeyFieldNoAccountsInline"
                        type="text"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        placeholder="Paste your API key"
                        className="nt-input"
                        required
                      />
                    </div>
                  )}
                  <div className="nt-form-group">
                    <label>Account Name(s)</label>
                    {accountNames.map((name, index) => (
                      <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <input type="text" value={name} onChange={(e) => handleAccountNameChange(index, e.target.value)} placeholder={index === 0 ? 'e.g., Sim101' : `Account ${index + 1}`} className="nt-input" />
                        {accountNames.length > 1 && <button type="button" onClick={() => handleRemoveAccountField(index)} className="nt-btn nt-btn-danger nt-btn-sm">✕</button>}
                      </div>
                    ))}
                    <button type="button" onClick={handleAddAccountField} className="nt-btn nt-btn-secondary nt-btn-sm" style={{ marginTop: '0.5rem' }}>+ Add another account</button>
                  </div>
                  <div className="nt-form-actions">
                    <button type="button" className="nt-btn nt-btn-secondary" onClick={() => { setShowConnectForm(false); setConnectAccountMethod('risklo-connector'); setApiKey(''); setAccountNames(['']); setUploadedFileName(null); }}>Cancel</button>
                    <button type="submit" className="nt-btn nt-btn-primary" disabled={connecting}>{connecting ? <><span className="nt-btn-spinner"></span> Connecting...</> : 'Connect Accounts'}</button>
                  </div>
                </form>
                ) : (
                  <div className="nt-form" style={{ paddingTop: 0 }}>
                    <TradovatePanel
                      user={user}
                      embedded
                      hideAccountsTable
                      onSessionChange={fetchConnections}
                    />
                    <div className="nt-form-actions" style={{ marginTop: '1rem' }}>
                      <button
                        type="button"
                        className="nt-btn nt-btn-secondary"
                        onClick={() => {
                          setShowConnectForm(false);
                          setConnectAccountMethod('risklo-connector');
                          setApiKey('');
                          setAccountNames(['']);
                          setUploadedFileName(null);
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
            )}
          </>
        ) : (
          <>
            {/* Connect Another Account Button - hidden when parent provides it (e.g. ResultsDashboard) */}
            {!connectButtonInParent && !showConnectForm && (
              <button 
                className="nt-btn nt-btn-secondary nt-btn-block"
                onClick={() => {
                  setConnectAccountMethod('risklo-connector');
                  setShowConnectForm(true);
                }}
                style={{ marginBottom: '1.5rem' }}
              >
                + Connect Another Account
              </button>
            )}

            {/* Connect Form - as modal popup when opened */}
            {showConnectForm && (
              <div className="nt-connect-modal-overlay" onClick={() => { setShowConnectForm(false); setConnectAccountMethod('risklo-connector'); }}>
              <div className="nt-connect-form-card nt-connect-modal" style={{ marginBottom: '1.5rem' }} onClick={(e) => e.stopPropagation()}>
                <div className="nt-form-header">
                  <h2>Connect Accounts</h2>
                  <button 
                    className="nt-form-close"
                    onClick={() => {
                      setShowConnectForm(false);
                      setConnectAccountMethod('risklo-connector');
                      setApiKey('');
                      setAccountNames(['']);
                      setUploadedFileName(null);
                    }}
                  >
                    ✕
                  </button>
                </div>

                <ConnectAccountMethodSelect
                  id="connect-method-has-accounts-modal"
                  value={connectAccountMethod}
                  onChange={setConnectAccountMethod}
                />
                {connectAccountMethod === 'risklo-connector' ? (
                <form onSubmit={handleBulkConnect} className="nt-form">
                  {/* API Key Generation for RiskLo Connector */}
                  {apiProvider === 'risklo-connector' && (
                    <div className="nt-form-group" style={{ 
                      background: '#1e293b', 
                      padding: '1.5rem', 
                      borderRadius: '8px',
                      border: '2px solid #3b82f6',
                      marginBottom: '1.5rem'
                    }}>
                      <div style={{ marginBottom: '1rem' }}>
                        <h4 style={{ 
                          color: '#60a5fa', 
                          fontSize: '1rem', 
                          marginBottom: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          🔑 Generate Your API Key
                        </h4>
                        <p style={{ fontSize: '0.875rem', color: '#94a3b8', marginBottom: '1rem' }}>
                          Create a unique API key for your NinjaTrader add-on. You'll paste this key into the add-on settings.
                        </p>
                      </div>

                      {/* Key Name Input */}
                      <div style={{ marginBottom: '1rem' }}>
                        <label htmlFor="keyName" style={{ 
                          fontSize: '0.875rem', 
                          color: '#cbd5e1',
                          display: 'block',
                          marginBottom: '0.5rem'
                        }}>
                          Key Name (Optional)
                        </label>
                        <input
                          type="text"
                          id="keyName"
                          value={keyName}
                          onChange={(e) => setKeyName(e.target.value)}
                          placeholder="My Desktop PC"
                          className="nt-input"
                          style={{ fontSize: '0.875rem' }}
                        />
                      </div>

                      {/* Generate Button */}
                      <button
                        type="button"
                        onClick={handleGenerateApiKey}
                        disabled={generatingKey}
                        className="nt-btn nt-btn-primary"
                        style={{
                          width: '100%',
                          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                          padding: '0.75rem',
                          fontSize: '0.95rem',
                          fontWeight: '600'
                        }}
                      >
                        {generatingKey ? (
                          <>
                            <div className="nt-btn-spinner"></div>
                            Generating...
                          </>
                        ) : (
                          '✨ Generate New API Key'
                        )}
                      </button>

                      {/* Existing Keys List */}
                      {userApiKeys.length > 0 && (
                        <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #334155' }}>
                          <h5 style={{ 
                            fontSize: '0.875rem', 
                            color: '#cbd5e1', 
                            marginBottom: '0.75rem',
                            fontWeight: '600'
                          }}>
                            Your Existing Keys:
                          </h5>
                          {loadingKeys ? (
                            <div style={{ textAlign: 'center', padding: '1rem', color: '#64748b' }}>
                              <div className="nt-btn-spinner" style={{ margin: '0 auto 0.5rem' }}></div>
                              Loading keys...
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                              {userApiKeys.filter(k => k.is_active).map((key) => (
                                <div
                                  key={key.id}
                                  style={{
                                    background: '#0f172a',
                                    padding: '0.75rem',
                                    borderRadius: '6px',
                                    border: '1px solid #334155',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: '0.75rem'
                                  }}
                                >
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ 
                                      fontSize: '0.75rem', 
                                      color: '#94a3b8',
                                      marginBottom: '0.25rem'
                                    }}>
                                      {key.key_name || 'Unnamed Key'}
                                    </div>
                                    <code style={{
                                      fontSize: '0.8rem',
                                      color: '#60a5fa',
                                      fontFamily: 'monospace',
                                      wordBreak: 'break-all'
                                    }}>
                                      {key.api_key}
                                    </code>
                                    {key.last_used_at && (
                                      <div style={{
                                        fontSize: '0.7rem',
                                        color: '#64748b',
                                        marginTop: '0.25rem'
                                      }}>
                                        Last used: {new Date(key.last_used_at).toLocaleString()}
                                      </div>
                                    )}
                                  </div>
                                  <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                                    <button
                                      type="button"
                                      onClick={() => copyToClipboard(key.api_key)}
                                      className="nt-btn nt-btn-sm"
                                      style={{
                                        background: '#1e40af',
                                        padding: '0.4rem 0.75rem',
                                        fontSize: '0.75rem'
                                      }}
                                      title="Copy to clipboard"
                                    >
                                      <IconCopy size={14} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteApiKey(key.id)}
                                      className="nt-btn nt-btn-danger nt-btn-sm"
                                      style={{
                                        padding: '0.4rem 0.75rem',
                                        fontSize: '0.75rem'
                                      }}
                                      title="Deactivate key"
                                    >
                                      <IconTrash size={14} />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {apiProvider === 'risklo-connector' ? (
                    <div className="nt-form-group">
                      <label htmlFor="apiKey">Connector API Key</label>
                      <select
                        id="apiKey"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        required
                        className="nt-input"
                        disabled={loadingKeys || activeConnectorKeys.length === 0}
                      >
                        {activeConnectorKeys.length === 0 ? (
                          <option value="">{loadingKeys ? 'Loading keys...' : 'Generate a key above first'}</option>
                        ) : (
                          activeConnectorKeys.map((key) => (
                            <option key={key.id} value={key.api_key}>
                              {(key.key_name || 'Unnamed Key')} - {maskApiKey(key.api_key)}
                            </option>
                          ))
                        )}
                      </select>
                      <small className="nt-form-help">
                        Select the same key configured in the connector add-on.
                      </small>
                      <small className="nt-form-help" style={{ display: 'block', marginTop: '0.5rem', color: '#60a5fa' }}>
                        📚 <a
                          href="https://github.com/myleshumphrey/risklo/blob/main/ninjatrader-addon/v2-push/INSTALLATION.md"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#60a5fa', textDecoration: 'underline' }}
                        >
                          Installation Guide
                        </a> - Install the free add-on first
                      </small>
                    </div>
                  ) : (
                    <div className="nt-form-group">
                      <label htmlFor="apiKey">CrossTrade Secret Key</label>
                      <input
                        type="password"
                        id="apiKey"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        placeholder="nlgSNZE4SEJCayNQDWTx9LUXFy4Q1ZfK..."
                        required
                        className="nt-input"
                      />
                      <small className="nt-form-help">
                        Find this in CrossTrade Dashboard → Manual Trader section
                      </small>
                    </div>
                  )}

                  <div className="nt-form-group">
                    <label>NinjaTrader Account Names</label>
                    
                    {/* CSV Upload Option */}
                    <div className="nt-csv-upload-section">
                      <label 
                        htmlFor="csv-upload-summary" 
                        className={`nt-csv-upload-label ${isDragging ? 'nt-dragging' : ''}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        {uploadingCsv ? (
                          <>
                            <div className="nt-btn-spinner"></div>
                            Loading CSV...
                          </>
                        ) : isDragging ? (
                          <>
                            📥 Drop CSV file here
                          </>
                        ) : (
                          <>
                            📄 Click or Drag CSV with Account Names
                          </>
                        )}
                      </label>
                      <input
                        type="file"
                        id="csv-upload-summary"
                        accept=".csv"
                        onChange={handleCsvUpload}
                        style={{ display: 'none' }}
                      />
                      {uploadedFileName && (
                        <small className="nt-form-help" style={{ marginTop: '0.5rem', display: 'block' }}>
                          ✅ Loaded: {uploadedFileName}
                        </small>
                      )}
                    </div>

                    {/* Manual Entry Fields */}
                    {accountNames.map((name, index) => (
                      <div key={index} className="nt-account-name-row">
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => handleAccountNameChange(index, e.target.value)}
                          placeholder={`Account ${index + 1} (e.g., Sim101)`}
                          className="nt-input"
                        />
                        {accountNames.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveAccountField(index)}
                            className="nt-btn nt-btn-danger nt-btn-sm"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={handleAddAccountField}
                      className="nt-btn nt-btn-secondary nt-btn-sm"
                      style={{ marginTop: '0.5rem' }}
                    >
                      + Add Another Account
                    </button>
                  </div>

                  <div className="nt-form-actions">
                    <button
                      type="button"
                      onClick={() => {
                        setShowConnectForm(false);
                        setConnectAccountMethod('risklo-connector');
                        setApiKey('');
                        setAccountNames(['']);
                        setUploadedFileName(null);
                      }}
                      className="nt-btn nt-btn-secondary"
                      disabled={connecting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="nt-btn nt-btn-primary"
                      disabled={connecting}
                    >
                      {connecting ? (
                        <>
                          <div className="nt-btn-spinner"></div>
                          Connecting...
                        </>
                      ) : (
                        'Connect Accounts'
                      )}
                    </button>
                  </div>
                </form>
                ) : (
                  <div className="nt-form" style={{ paddingTop: 0 }}>
                    <TradovatePanel
                      user={user}
                      embedded
                      hideAccountsTable
                      onSessionChange={fetchConnections}
                    />
                    <div className="nt-form-actions" style={{ marginTop: '1rem' }}>
                      <button
                        type="button"
                        className="nt-btn nt-btn-secondary"
                        onClick={() => {
                          setShowConnectForm(false);
                          setConnectAccountMethod('risklo-connector');
                          setApiKey('');
                          setAccountNames(['']);
                          setUploadedFileName(null);
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
              </div>
            )}

            {/* Active/Inactive Tabs */}
            <div className="nt-view-toggle" style={{ marginBottom: '1rem' }}>
              <button
                className={`nt-toggle-btn ${!showInactiveAccounts ? 'active' : ''}`}
                onClick={() => setShowInactiveAccounts(false)}
              >
                Active Accounts ({activeConnections.length})
              </button>
              <button
                className={`nt-toggle-btn ${showInactiveAccounts ? 'active' : ''}`}
                onClick={() => setShowInactiveAccounts(true)}
              >
                Inactive Accounts ({connections.filter(c => !c.is_active).length})
              </button>
            </div>

            {/* Metrics Dashboard - Only show for active accounts */}
            {!showInactiveAccounts && activeConnections.length > 0 && (() => {
              // Calculate true P&L for each account based on cash value
              const accountsWithTruePnL = metricsConnections.map(conn => ({
                ...conn,
                truePnL: calculateTruePnL(conn)
              }));
              
              const totalPnL = accountsWithTruePnL.reduce((sum, conn) => sum + conn.truePnL, 0);
              const winners = accountsWithTruePnL.filter(c => c.truePnL > 0).length;
              const losers = accountsWithTruePnL.filter(c => c.truePnL < 0).length;
              // Win rate excludes $0 P&L accounts: only count winners vs losers
              const accountsWithNonZeroPnL = winners + losers;
              const winRate = accountsWithNonZeroPnL > 0 ? (winners / accountsWithNonZeroPnL * 100) : 0;
              const avgPnL = metricsConnections.length > 0 ? totalPnL / metricsConnections.length : 0;
              const bestAccount = accountsWithTruePnL.reduce((best, conn) => {
                return conn.truePnL > best.truePnL ? conn : best;
              }, accountsWithTruePnL[0] || { truePnL: 0 });
              const worstAccount = accountsWithTruePnL.reduce((worst, conn) => {
                return conn.truePnL < worst.truePnL ? conn : worst;
              }, accountsWithTruePnL[0] || { truePnL: 0 });
              // Count all active, usable strategies across active metric accounts
              // (instead of only one display strategy per account).
              const totalStrategies = metricsConnections.reduce((sum, conn) => {
                const activeUsableStrategies = getUsableStrategies(conn).filter((s) => s.is_active !== false);
                return sum + activeUsableStrategies.length;
              }, 0);
              
              // Additional metrics
              const totalTradesFromAccounts = metricsConnections.reduce(
                (sum, conn) => sum + (conn.total_trades ?? 0),
                0
              );
              // Fallback: if total_trades not provided, use fetched trades data for current week
              const activeAccountNames = new Set(metricsConnections.map(conn => conn.account_name));
              const totalTradesFromState = Object.entries(trades).reduce((sum, [accountName, days]) => {
                if (!activeAccountNames.has(accountName)) return sum;
                return sum + Object.values(days).reduce((daySum, list) => daySum + (list?.length || 0), 0);
              }, 0);
              const totalTrades = totalTradesFromAccounts || totalTradesFromState;
              const avgContractsPerTrade = totalTrades > 0 
                ? metricsConnections.reduce((sum, conn) => {
                    const displayStrategy = getDisplayStrategy(conn);
                    return sum + (displayStrategy?.quantity ? parseInt(displayStrategy.quantity) : 0);
                  }, 0) / metricsConnections.length
                : 0;
              
              // Calculate win/loss P&L for profit factor
              const winPnL = accountsWithTruePnL.filter(c => c.truePnL > 0).reduce((sum, c) => sum + c.truePnL, 0);
              const lossPnL = accountsWithTruePnL.filter(c => c.truePnL < 0).reduce((sum, c) => sum + c.truePnL, 0);
              const profitFactor = lossPnL !== 0 ? Math.abs(winPnL / lossPnL) : (winPnL > 0 ? Infinity : 0);

              return (
                <div className={`nt-metrics-section ${showMoreMetrics ? 'nt-metrics-expanded' : ''}`} style={{ marginBottom: '1.5rem' }}>
                  <div className="nt-stats-grid" style={{ 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '1rem'
                  }}>
                    {/* 1. Total Active Accounts */}
                    <div className="nt-stat-card">
                      <div className="nt-stat-card-label">Total Active Accounts</div>
                      <div className="nt-stat-card-value" style={{ color: '#60a5fa' }}>
                        {metricsConnections.length}
                      </div>
                    </div>
                    
                    {/* 2. Active Strategies */}
                    <div className="nt-stat-card">
                      <div className="nt-stat-card-label">Active Strategies</div>
                      <div className="nt-stat-card-value" style={{ color: '#fbbf24' }}>
                        {totalStrategies}
                      </div>
                    </div>
                    
                    {/* 3. Total P&L */}
                    <div className="nt-stat-card">
                      <div className="nt-stat-card-label">Total P&L</div>
                      <div className="nt-stat-card-value" style={{ color: totalPnL >= 0 ? '#10b981' : '#ef4444' }}>
                        {formatCurrency(totalPnL)}
                      </div>
                    </div>
                    
                    {/* 4. Avg P&L per Account */}
                    <div className="nt-stat-card">
                      <div className="nt-stat-card-label">Avg P&L per Account</div>
                      <div className="nt-stat-card-value" style={{ color: avgPnL >= 0 ? '#10b981' : '#ef4444' }}>
                        {formatCurrency(avgPnL)}
                      </div>
                    </div>
                    
                    {/* 5. Win Rate - hidden on mobile until "More Metrics" */}
                    <div className="nt-stat-card nt-stat-card-more">
                      <div className="nt-stat-card-label">Win Rate</div>
                      <div className="nt-stat-card-value" style={{ color: winRate >= 50 ? '#10b981' : '#ef4444' }}>
                        {winRate.toFixed(1)}%
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.5)', marginTop: '0.25rem' }}>
                        {winners}W / {losers}L
                      </div>
                    </div>
                    
                    {/* 6. Best Account - hidden on mobile until "More Metrics" */}
                    <div className="nt-stat-card nt-stat-card-more">
                      <div className="nt-stat-card-label">Best Account</div>
                      <div className="nt-stat-card-value" style={{ color: '#10b981', fontSize: '0.8rem' }} title={bestAccount.account_name || ''}>
                        {bestAccount.account_name && bestAccount.account_name.length > 14
                          ? `${bestAccount.account_name.slice(0, 8)}…${bestAccount.account_name.slice(-4)}`
                          : (bestAccount.account_name || 'N/A')}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#10b981', marginTop: '0.25rem' }}>
                        {formatCurrency(bestAccount.truePnL || 0)}
                      </div>
                    </div>
                    
                    {/* 7. Worst Account - hidden on mobile until "More Metrics" */}
                    <div className="nt-stat-card nt-stat-card-more">
                      <div className="nt-stat-card-label">Worst Account</div>
                      <div className="nt-stat-card-value" style={{ color: '#ef4444', fontSize: '0.8rem' }} title={worstAccount.account_name || ''}>
                        {worstAccount.account_name && worstAccount.account_name.length > 14
                          ? `${worstAccount.account_name.slice(0, 8)}…${worstAccount.account_name.slice(-4)}`
                          : (worstAccount.account_name || 'N/A')}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: '0.25rem' }}>
                        {formatCurrency(worstAccount.truePnL || 0)}
                      </div>
                    </div>
                    
                    {/* 8. Total Trades - hidden on mobile until "More Metrics" */}
                    <div className="nt-stat-card nt-stat-card-more">
                      <div className="nt-stat-card-label">Total Trades</div>
                      <div className="nt-stat-card-value" style={{ color: themeColor('#a78bfa') }}>
                        {totalTrades}
                      </div>
                    </div>
                    
                    {/* 9. Profit Factor - hidden on mobile until "More Metrics" */}
                    <div className="nt-stat-card nt-stat-card-more">
                      <div className="nt-stat-card-label">Profit Factor</div>
                      <div
                        className="nt-stat-card-value"
                        style={{ color: profitFactor >= 2 ? '#10b981' : profitFactor >= 1 ? '#fbbf24' : '#ef4444' }}
                        title={profitFactor === Infinity ? 'No losing trades in this period; profit factor is gross profit ÷ gross loss, which is undefined when loss is $0.' : undefined}
                      >
                        {profitFactor === Infinity ? 'No losses' : profitFactor.toFixed(2)}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.5)', marginTop: '0.25rem' }}>
                        {formatCurrency(winPnL)} / {formatCurrency(Math.abs(lossPnL))}
                      </div>
                    </div>
                    
                    {/* 10. Avg Contracts - hidden on mobile until "More Metrics" */}
                    <div className="nt-stat-card nt-stat-card-more">
                      <div className="nt-stat-card-label">Avg Contracts</div>
                      <div className="nt-stat-card-value" style={{ color: '#ec4899' }}>
                        {avgContractsPerTrade.toFixed(1)}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.5)', marginTop: '0.25rem' }}>
                        per account
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="nt-metrics-toggle-btn"
                    onClick={() => setShowMoreMetrics(!showMoreMetrics)}
                  >
                    {showMoreMetrics ? 'Less Metrics' : 'More Metrics'}
                  </button>
                </div>
              );
            })()}

            {/* Bulk Actions for Cards View */}
            {effectiveCardViewMode === 'cards' && selectedAccounts.size > 0 && (
              <div className="nt-bulk-actions-bar" style={{ marginBottom: '1rem' }}>
                <div className="nt-bulk-selection">
                  <div className="nt-bulk-checkbox">
                    <input
                      type="checkbox"
                      checked={true}
                      onChange={() => setSelectedAccounts(new Set())}
                    />
                  </div>
                  <span className="nt-bulk-count">{selectedAccounts.size} selected</span>
                </div>
                <div className="nt-bulk-buttons">
                  <button 
                    className={`nt-btn nt-btn-sm ${showInactiveAccounts ? 'nt-btn-success' : 'nt-btn-warning'}`}
                    onClick={() => {
                      // Bulk toggle active/inactive
                      const newActiveState = showInactiveAccounts; // If showing inactive, make them active
                      const promises = Array.from(selectedAccounts).map(id => {
                        return fetch(`${API_BASE_URL}/api/ninjatrader/toggle-active`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
                          body: JSON.stringify({
                            userEmail: user.email,
                            connectionId: id,
                            isActive: newActiveState
                          })
                        });
                      });
                      
                      Promise.all(promises).then(() => {
                        fetchConnections();
                        setSelectedAccounts(new Set());
                        setSuccess(`Marked ${selectedAccounts.size} account(s) as ${newActiveState ? 'active' : 'inactive'}`);
                        setTimeout(() => setSuccess(null), 3000);
                      }).catch(err => {
                        setError(err.message);
                        setTimeout(() => setError(null), 5000);
                      });
                    }}
                  >
                    {showInactiveAccounts ? '✓ Mark Active' : '⏸ Mark Inactive'}
                  </button>
                  <button 
                    className="nt-btn nt-btn-secondary nt-btn-sm"
                    onClick={() => {
                      // Bulk toggle exclude_from_metrics
                      const promises = Array.from(selectedAccounts).map(id => {
                        const conn = connections.find(c => c.id === id);
                        return fetch(`${API_BASE_URL}/api/ninjatrader/toggle-exclude-metrics`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
                          body: JSON.stringify({
                            userEmail: user.email,
                            connectionId: id,
                            excludeFromMetrics: !conn.exclude_from_metrics // Toggle
                          })
                        });
                      });
                      
                      Promise.all(promises).then(() => {
                        fetchConnections();
                        setSelectedAccounts(new Set());
                        const conn = connections.find(c => selectedAccounts.has(c.id));
                        setSuccess(`${conn.exclude_from_metrics ? 'Included in' : 'Excluded from'} metrics`);
                        setTimeout(() => setSuccess(null), 3000);
                      }).catch(err => {
                        setError(err.message);
                        setTimeout(() => setError(null), 5000);
                      });
                    }}
                  >
                    📊 Exclude from Metrics
                  </button>
                  <button 
                    className="nt-btn nt-btn-danger nt-btn-sm"
                    onClick={async () => {
                      if (window.confirm(`Permanently disconnect and remove ${selectedAccounts.size} accounts? This will delete all associated data.`)) {
                        try {
                          const promises = Array.from(selectedAccounts).map(id => {
                            return fetch(`${API_BASE_URL}/api/ninjatrader/disconnect`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
                              body: JSON.stringify({
                                userEmail: user.email,
                                connectionId: id,
                                deleteData: true
                              })
                            });
                          });
                          
                          await Promise.all(promises);
                          await fetchConnections();
                          setSelectedAccounts(new Set());
                          setSuccess(`Disconnected ${selectedAccounts.size} account(s)`);
                          setTimeout(() => setSuccess(null), 3000);
                        } catch (err) {
                          setError(err.message);
                          setTimeout(() => setError(null), 5000);
                        }
                      }
                    }}
                  >
                    Disconnect
                  </button>
                  {isAdmin && (
                    <button
                      className="nt-btn nt-btn-danger nt-btn-sm"
                      onClick={handleDeleteStressTestAccounts}
                      disabled={cleaningStressAccounts}
                      title="Admin cleanup for StressTestAccount_* records"
                    >
                      {cleaningStressAccounts ? 'Removing...' : 'Remove Stress Tests'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Table View */}
            {effectiveCardViewMode === 'table' && (
              <div className="nt-table-container">
                <table className="nt-accounts-table">
                  <thead>
                    {selectedAccounts.size > 0 ? (
                      <tr className="nt-table-header-bulk" style={{ height: '56px' }}>
                        <th colSpan="10" style={{ height: '56px', padding: 0 }}>
                          <div className="nt-bulk-header-content">
                            <div className="nt-bulk-header-left">
                              <input
                                type="checkbox"
                                checked={true}
                                onChange={() => setSelectedAccounts(new Set())}
                              />
                              <span className="nt-bulk-header-count">{selectedAccounts.size} selected</span>
                            </div>
                            <div className="nt-bulk-header-actions">
                              <button 
                                className={`nt-btn nt-btn-sm ${showInactiveAccounts ? 'nt-btn-success' : 'nt-btn-warning'}`}
                                onClick={() => {
                                  const newActiveState = showInactiveAccounts;
                                  const promises = Array.from(selectedAccounts).map(id => {
                                    return fetch(`${API_BASE_URL}/api/ninjatrader/toggle-active`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
                                      body: JSON.stringify({
                                        userEmail: user.email,
                                        connectionId: id,
                                        isActive: newActiveState
                                      })
                                    });
                                  });
                                  
                                  Promise.all(promises).then(() => {
                                    fetchConnections();
                                    setSelectedAccounts(new Set());
                                    setSuccess(`Marked ${selectedAccounts.size} account(s) as ${newActiveState ? 'active' : 'inactive'}`);
                                    setTimeout(() => setSuccess(null), 3000);
                                  }).catch(err => {
                                    setError(err.message);
                                    setTimeout(() => setError(null), 5000);
                                  });
                                }}
                              >
                                {showInactiveAccounts ? '✓ Mark Active' : '⏸ Mark Inactive'}
                              </button>
                              <button 
                                className="nt-btn nt-btn-secondary nt-btn-sm"
                                onClick={() => {
                                  // Bulk toggle exclude from metrics
                                  const promises = Array.from(selectedAccounts).map(id => {
                                    const conn = connections.find(c => c.id === id);
                                    return fetch(`${API_BASE_URL}/api/ninjatrader/toggle-exclude-metrics`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
                                      body: JSON.stringify({
                                        userEmail: user.email,
                                        connectionId: id,
                                        excludeFromMetrics: !conn?.exclude_from_metrics
                                      })
                                    });
                                  });
                                  
                                  Promise.all(promises).then(() => {
                                    fetchConnections();
                                    setSelectedAccounts(new Set());
                                    setSuccess(`Updated ${selectedAccounts.size} account(s)`);
                                    setTimeout(() => setSuccess(null), 3000);
                                  }).catch(err => {
                                    setError(err.message);
                                    setTimeout(() => setError(null), 5000);
                                  });
                                }}
                              >
                                📊 Exclude from Metrics
                              </button>
                              <button 
                                className="nt-btn nt-btn-danger nt-btn-sm"
                                onClick={async () => {
                                  if (window.confirm(`Permanently disconnect and remove ${selectedAccounts.size} accounts? This will delete all associated data.`)) {
                                    try {
                                      const promises = Array.from(selectedAccounts).map(id => {
                                        return fetch(`${API_BASE_URL}/api/ninjatrader/disconnect`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
                                          body: JSON.stringify({
                                            userEmail: user.email,
                                            connectionId: id,
                                            deleteData: true
                                          })
                                        });
                                      });
                                      
                                      await Promise.all(promises);
                                      await fetchConnections();
                                      setSelectedAccounts(new Set());
                                      setSuccess(`Disconnected ${selectedAccounts.size} account(s)`);
                                      setTimeout(() => setSuccess(null), 3000);
                                    } catch (err) {
                                      setError(err.message);
                                      setTimeout(() => setError(null), 5000);
                                    }
                                  }
                                }}
                              >
                                Disconnect
                              </button>
                              {isAdmin && (
                                <button
                                  className="nt-btn nt-btn-danger nt-btn-sm"
                                  onClick={handleDeleteStressTestAccounts}
                                  disabled={cleaningStressAccounts}
                                  title="Admin cleanup for StressTestAccount_* records"
                                >
                                  {cleaningStressAccounts ? 'Removing...' : 'Remove Stress Tests'}
                                </button>
                              )}
                            </div>
                          </div>
                        </th>
                      </tr>
                    ) : (
                      <tr style={{ height: '56px' }}>
                        <th style={{ width: '40px' }}>
                          <input
                            type="checkbox"
                            checked={selectedAccounts.size === sortedConnections.length && sortedConnections.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedAccounts(new Set(sortedConnections.map(c => c.id)));
                              } else {
                                setSelectedAccounts(new Set());
                              }
                            }}
                          />
                        </th>
                        <th 
                          onClick={() => handleSort('account_name')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          title="Account Name"
                        >
                          {isMobile ? 'Acct' : 'Account Name'} {sortColumn === 'account_name' && (tableSortDirection === 'asc' ? '▲' : '▼')}
                        </th>
                        <th 
                          onClick={() => handleSort('last_sync')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          title="Last Synced"
                        >
                          {isMobile ? 'Synced' : 'Last Synced'} {sortColumn === 'last_sync' && (tableSortDirection === 'asc' ? '▲' : '▼')}
                        </th>
                        <th 
                          onClick={() => handleSort('strategy')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          title="Strategy"
                        >
                          {isMobile ? 'Strat' : 'Strategy'} {sortColumn === 'strategy' && (tableSortDirection === 'asc' ? '▲' : '▼')}
                        </th>
                        <th 
                          onClick={() => handleSort('instrument')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          title="Instrument"
                        >
                          {isMobile ? 'Instr' : 'Instrument'} {sortColumn === 'instrument' && (tableSortDirection === 'asc' ? '▲' : '▼')}
                        </th>
                        <th 
                          onClick={() => handleSort('contracts')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          title="Contracts"
                        >
                          {isMobile ? 'Ctr' : 'Contracts'} {sortColumn === 'contracts' && (tableSortDirection === 'asc' ? '▲' : '▼')}
                        </th>
                        <th 
                          onClick={() => handleSort('pnl')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          title="Total P&L"
                        >
                          {isMobile ? 'P&L' : 'Total P&L'} {sortColumn === 'pnl' && (tableSortDirection === 'asc' ? '▲' : '▼')}
                        </th>
                        <th 
                          onClick={() => handleSort('cash_value')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          title="Cash Value"
                        >
                          {isMobile ? 'Cash' : 'Cash Value'} {sortColumn === 'cash_value' && (tableSortDirection === 'asc' ? '▲' : '▼')}
                        </th>
                        <th 
                          onClick={() => handleSort('trailing_drawdown')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          title="Maximum adverse excursion (trailing drawdown) from NinjaTrader"
                        >
                          {isMobile ? 'DD' : 'Trailing DD'} {sortColumn === 'trailing_drawdown' && (tableSortDirection === 'asc' ? '▲' : '▼')}
                        </th>
                        <th style={{ width: '40px' }}></th>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {sortedConnections.map((conn) => {
                      const isExpanded = expandedRows.has(conn.id);
                      const truePnL = calculateTruePnL(conn);
                      return (
                        <React.Fragment key={conn.id}>
                          <tr
                            onClick={() => toggleRowExpansion(conn.id)}
                            className={selectedAccounts.has(conn.id) ? 'selected' : ''}
                            style={{ cursor: 'pointer' }}
                          >
                            <td onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedAccounts.has(conn.id)}
                                onChange={(e) => {
                                  const newSelected = new Set(selectedAccounts);
                                  if (e.target.checked) {
                                    newSelected.add(conn.id);
                                  } else {
                                    newSelected.delete(conn.id);
                                  }
                                  setSelectedAccounts(newSelected);
                                }}
                              />
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span 
                                  className="nt-account-link" 
                                  onClick={() => conn.platform !== 'tradovate' && fetchAccountTrades(conn.account_name)}
                                  title={conn.platform === 'tradovate' ? `Tradovate account — full trade sync uses ${appName} with supported brokers` : 'Click to view trades'}
                                  style={{ cursor: conn.platform === 'tradovate' ? 'default' : 'pointer' }}
                                >
                                  {conn.account_name}
                                </span>
                                {conn.platform === 'tradovate' && (
                                  <span
                                    style={{
                                      fontSize: '0.7rem',
                                      padding: '0.15rem 0.4rem',
                                      background: 'rgba(99, 102, 241, 0.2)',
                                      border: '1px solid rgba(99, 102, 241, 0.45)',
                                      borderRadius: '3px',
                                      color: themeColor('#a5b4fc'),
                                      fontWeight: '500',
                                      whiteSpace: 'nowrap',
                                    }}
                                    title="Connected via Tradovate OAuth"
                                  >
                                    Tradovate
                                  </span>
                                )}
                                {conn.exclude_from_metrics && (
                                  <span 
                                    style={{
                                      fontSize: '0.7rem',
                                      padding: '0.15rem 0.4rem',
                                      background: 'rgba(251, 146, 60, 0.2)',
                                      border: '1px solid rgba(251, 146, 60, 0.4)',
                                      borderRadius: '3px',
                                      color: '#fb923c',
                                      fontWeight: '500',
                                      whiteSpace: 'nowrap'
                                    }}
                                    title="This account is excluded from metrics calculations"
                                  >
                                    EXCLUDED
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="nt-table-date">
                              {formatDateTimeET(conn.last_sync_at)}
                            </td>
                            <td className="nt-table-strategy" onClick={(e) => e.stopPropagation()}>
                              {(() => {
                                const usableStrategies = getUsableStrategies(conn);
                                const displayStrategy = getDisplayStrategy(conn);
                                if (usableStrategies.length === 0 && !displayStrategy) {
                                  return <em style={{ opacity: 0.5 }}>No Strategies</em>;
                                }
                                const hasMultiple = usableStrategies.length >= 2;
                                return (
                                <button
                                  type="button"
                                  className="nt-strategy-link"
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    color: '#60a5fa',
                                    cursor: 'pointer',
                                    textDecoration: 'none',
                                    textAlign: 'left',
                                    font: 'inherit',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                  }}
                                  onClick={() => {
                                    const stratToShow = hasMultiple ? null : (usableStrategies[0] || displayStrategy);
                                    setSelectedStrategyDetail(hasMultiple
                                      ? { strategies: usableStrategies, accountName: conn.account_name }
                                      : { strategy: stratToShow, accountName: conn.account_name }
                                    );
                                    setStrategyDetailTab(0);
                                    setShowStrategyModal(true);
                                  }}
                                >
                                  <span style={{ textDecoration: 'underline' }}>
                                    {hasMultiple ? 'Multiple Strats' : displayStrategy.strategy_name}
                                  </span>
                                  {!hasMultiple && (displayStrategy.is_active ? (
                                    <span style={{ 
                                      fontSize: '10px', 
                                      padding: '2px 6px', 
                                      borderRadius: '4px', 
                                      background: '#10b98166', 
                                      color: '#10b981',
                                      fontWeight: '600',
                                      border: '1px solid #10b981',
                                      textDecoration: 'none'
                                    }}>
                                      LIVE
                                    </span>
                                  ) : (
                                    <span style={{ 
                                      fontSize: '10px', 
                                      padding: '2px 6px', 
                                      borderRadius: '4px', 
                                      background: 'rgba(255,255,255,0.1)', 
                                      color: 'rgba(255,255,255,0.5)',
                                      fontWeight: '600',
                                      border: '1px solid rgba(255,255,255,0.3)',
                                      textDecoration: 'none'
                                    }}>
                                      OFF
                                    </span>
                                  ))}
                                </button>
                                );
                              })()}
                            </td>
                            <td className="nt-table-instrument">
                              {(() => {
                                const displayStrategy = getDisplayStrategy(conn);
                                if (!displayStrategy && (!conn.strategies || conn.strategies.length === 0)) return '-';
                                // Prefer display strategy instrument, then fall back to any active strategy instrument
                                const displayInstrument = displayStrategy?.instrument && String(displayStrategy.instrument).trim() !== '' 
                                  ? displayStrategy.instrument 
                                  : null;
                                const stratWithInstrument = conn.strategies?.find(s => s.instrument && String(s.instrument).trim() !== '');
                                const instrument = displayInstrument ?? stratWithInstrument?.instrument ?? null;
                                if (instrument) {
                                  return extractContractType(instrument) || '-';
                                }
                                return '-';
                              })()}
                            </td>
                            <td className="nt-table-contracts">
                              {(() => {
                                const displayStrategy = getDisplayStrategy(conn);
                                if (!displayStrategy) return '-';
                                return (
                                  parseInt(displayStrategy.quantity) || 
                                  extractContractSize(displayStrategy.display_name, displayStrategy.strategy_name) || 
                                  '-'
                                );
                              })()}
                            </td>
                            <td className="nt-table-pnl">
                              <span style={{ color: truePnL >= 0 ? '#10b981' : '#ef4444' }}>
                                {formatCurrency(truePnL)}
                              </span>
                            </td>
                            <td className="nt-table-cash">
                              {formatCurrency(conn.starting_balance || 0)}
                            </td>
                            <td className="nt-table-drawdown">
                              {conn.trailing_max_drawdown != null && parseFloat(conn.trailing_max_drawdown) > 0
                                ? formatCurrency(conn.trailing_max_drawdown)
                                : '-'}
                            </td>
                            <td className="nt-table-expand">
                              <span className="nt-expand-icon">
                                {isExpanded ? '▼' : '▶'}
                              </span>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="nt-table-actions-row">
                              <td colSpan="10">
                                <div className="nt-table-actions">
                                  {conn.platform !== 'tradovate' && (
                                  <>
                                  <button
                                    className="nt-btn nt-btn-secondary nt-btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openAccountSettings(conn);
                                    }}
                                  >
                                    ⚙️ Settings
                                  </button>
                                  <button
                                    className="nt-btn nt-btn-secondary nt-btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      fetchAccountTrades(conn.account_name);
                                    }}
                                    disabled={loadingModalTrades}
                                  >
                                    {loadingModalTrades && selectedAccount === conn.account_name ? (
                                      <>
                                        <div className="nt-btn-spinner"></div>
                                        Loading...
                                      </>
                                    ) : (
                                      '📊 View Trades'
                                    )}
                                  </button>
                                  
                                  {/* Only show manual sync buttons for CrossTrade (push-based RiskLo Connector auto-syncs) */}
                                  {conn.api_provider !== 'risklo-connector' && (
                                    <>
                                      <button
                                        className="nt-btn nt-btn-secondary nt-btn-sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSyncAccount(conn.account_name);
                                        }}
                                        disabled={syncing}
                                      >
                                        {syncing ? (
                                          <>
                                            <div className="nt-btn-spinner"></div>
                                            Syncing...
                                          </>
                                        ) : (
                                          '🔄 Sync Trades'
                                        )}
                                      </button>
                                      <button
                                        className="nt-btn nt-btn-secondary nt-btn-sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSyncStrategies(conn.account_name);
                                        }}
                                        disabled={syncingStrategies[conn.account_name]}
                                      >
                                        {syncingStrategies[conn.account_name] ? (
                                          <>
                                            <div className="nt-btn-spinner"></div>
                                            Syncing...
                                          </>
                                        ) : (
                                          '📈 Sync Strategies'
                                        )}
                                      </button>
                                    </>
                                  )}
                                  
                                  <button
                                    className="nt-btn nt-btn-warning nt-btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      fetch(`${API_BASE_URL}/api/ninjatrader/toggle-active`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
                                        body: JSON.stringify({
                                          userEmail: user.email,
                                          connectionId: conn.id,
                                          isActive: !conn.is_active
                                        })
                                      })
                                      .then(() => {
                                        fetchConnections();
                                        setSuccess(`${conn.account_name} marked as ${conn.is_active ? 'inactive' : 'active'}`);
                                        setTimeout(() => setSuccess(null), 3000);
                                      })
                                      .catch(err => {
                                        setError(err.message);
                                        setTimeout(() => setError(null), 5000);
                                      });
                                    }}
                                  >
                                    {conn.is_active ? '⏸ Mark Inactive' : '✓ Mark Active'}
                                  </button>
                                  
                                  <button
                                    className="nt-btn nt-btn-secondary nt-btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      fetch(`${API_BASE_URL}/api/ninjatrader/toggle-exclude-metrics`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
                                        body: JSON.stringify({
                                          userEmail: user.email,
                                          connectionId: conn.id,
                                          excludeFromMetrics: !conn.exclude_from_metrics
                                        })
                                      })
                                      .then(() => {
                                        fetchConnections();
                                        setSuccess(`${conn.account_name} ${conn.exclude_from_metrics ? 'included in' : 'excluded from'} metrics`);
                                        setTimeout(() => setSuccess(null), 3000);
                                      })
                                      .catch(err => {
                                        setError(err.message);
                                        setTimeout(() => setError(null), 5000);
                                      });
                                    }}
                                  >
                                    {conn.exclude_from_metrics ? '📊 Include in Metrics' : '🚫 Exclude from Metrics'}
                                  </button>
                                  </>
                                  )}
                                  {conn.platform === 'tradovate' && (
                                    <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.65)', marginRight: '0.5rem' }}>
                                      OAuth-linked account — use NinjaTrader connector accounts for full sync and trade history here.
                                    </span>
                                  )}
                                  <button
                                    className="nt-btn nt-btn-danger nt-btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDisconnect(conn.id, conn.account_name, conn.platform);
                                    }}
                                  >
                                    <IconTrash size={14} style={{ marginRight: '0.35rem', verticalAlign: 'middle' }} />
                                    Disconnect
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Card View */}
            {effectiveCardViewMode === 'cards' && (
              <div className="nt-accounts-grid">
                {sortedConnections.map((conn) => {
                  const truePnL = calculateTruePnL(conn);
                  return (
                  <div key={conn.id} className="nt-account-card">
                    <div className="nt-account-header">
                      <input
                        type="checkbox"
                        className="nt-account-checkbox"
                        checked={selectedAccounts.has(conn.id)}
                        onChange={(e) => {
                          const newSelected = new Set(selectedAccounts);
                          if (e.target.checked) {
                            newSelected.add(conn.id);
                          } else {
                            newSelected.delete(conn.id);
                          }
                          setSelectedAccounts(newSelected);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="nt-account-info">
                        <h3 
                          className="nt-account-link"
                          onClick={() => fetchAccountTrades(conn.account_name)}
                          style={{ cursor: 'pointer' }}
                        >
                          {conn.account_name}
                        </h3>
                        <div className="nt-account-badges">
                          <span className={`nt-status-badge ${conn.is_active ? 'active' : 'inactive'}`}>
                            {conn.is_active ? '✓ Active' : '⏸ Inactive'}
                          </span>
                          <span className="nt-provider-badge" title={conn.platform === 'tradovate' ? 'Tradovate OAuth' : conn.api_provider === 'risklo-connector' ? 'Auto-syncs every 15 seconds from NinjaTrader' : 'Manual sync required'}>
                            {conn.platform === 'tradovate' ? 'Tradovate' : conn.api_provider === 'risklo-connector' ? '⚡ RiskLo Connector' : 'CrossTrade'}
                          </span>
                          {conn.exclude_from_metrics && (
                            <span 
                              style={{
                                fontSize: '0.7rem',
                                padding: '0.15rem 0.4rem',
                                background: 'rgba(251, 146, 60, 0.2)',
                                border: '1px solid rgba(251, 146, 60, 0.4)',
                                borderRadius: '3px',
                                color: '#fb923c',
                                fontWeight: '500',
                                whiteSpace: 'nowrap'
                              }}
                              title="This account is excluded from metrics calculations"
                            >
                              EXCLUDED
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="nt-account-stats">
                      <div className="nt-stat">
                        <span className="nt-stat-label">P&L</span>
                        <span className="nt-stat-value" style={{ color: truePnL >= 0 ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                          {formatCurrency(truePnL)}
                        </span>
                      </div>
                      <div className="nt-stat">
                        <span className="nt-stat-label">Cash Value</span>
                        <span className="nt-stat-value">
                          {formatCurrency(conn.starting_balance || 0)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="nt-account-stats">
                      <div className="nt-stat">
                        <span className="nt-stat-label">Last Synced</span>
                        <span className="nt-stat-value">
                        {conn.last_sync_at ? new Date(conn.last_sync_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' }) : 'Never'}
                        </span>
                      </div>
                      <div className="nt-stat">
                        <span className="nt-stat-label">Connected</span>
                        <span className="nt-stat-value">
                          {new Date(conn.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                    </div>

                    {/* Strategies Section */}
                    <div className="nt-strategies-section">
                      <div className="nt-strategies-header">
                        <h4>Active Strategies ({conn.strategies?.length || 0})</h4>
                      </div>
                      <div className="nt-strategies-list">
                        {renderCardStrategies(conn)}
                      </div>
                    </div>

                    <div className="nt-account-actions">
                      <button
                        className="nt-btn nt-btn-secondary"
                        onClick={() => fetchAccountTrades(conn.account_name)}
                        disabled={loadingTrades}
                      >
                        {loadingTrades && selectedAccount === conn.account_name ? (
                          <>
                            <div className="nt-btn-spinner"></div>
                            Loading...
                          </>
                        ) : (
                          '📊 View Trades'
                        )}
                      </button>
                      <button
                        className={`nt-btn ${conn.is_active ? 'nt-btn-warning' : 'nt-btn-success'}`}
                        onClick={() => {
                          // Toggle active/inactive status
                          fetch(`${API_BASE_URL}/api/ninjatrader/toggle-active`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
                            body: JSON.stringify({
                              userEmail: user.email,
                              connectionId: conn.id,
                              isActive: !conn.is_active
                            })
                          })
                          .then(res => res.json())
                          .then(() => {
                            fetchConnections();
                            setSuccess(`${conn.account_name} marked as ${conn.is_active ? 'inactive' : 'active'}`);
                            setTimeout(() => setSuccess(null), 3000);
                          })
                          .catch(err => {
                            setError(err.message);
                            setTimeout(() => setError(null), 5000);
                          });
                        }}
                      >
                        {conn.is_active ? '⏸ Mark Inactive' : '✓ Mark Active'}
                      </button>
                      <button
                        className="nt-btn nt-btn-danger"
                        onClick={() => handleDisconnect(conn.id, conn.account_name, conn.platform)}
                      >
                        Disconnect
                      </button>
                    </div>
                  </div>
                );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* API Key Generated Modal */}
      {showApiKeyModal && newApiKey && (
        <div className="nt-modal-overlay" onClick={() => setShowApiKeyModal(false)}>
          <div className="nt-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="nt-modal-header" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                🎉 API Key Generated!
              </h2>
              <button className="nt-modal-close" onClick={() => setShowApiKeyModal(false)}>✕</button>
            </div>
            <div className="nt-modal-body">
              <div style={{
                background: '#1e293b',
                padding: '1.5rem',
                borderRadius: '8px',
                border: '2px solid #3b82f6',
                marginBottom: '1.5rem'
              }}>
                <p style={{ 
                  fontSize: '0.9rem', 
                  marginBottom: '1rem',
                  color: '#f59e0b',
                  fontWeight: '600'
                }}>
                  ⚠️ IMPORTANT: Save this key now! You won't be able to see it again.
                </p>
                
                <div style={{
                  background: '#0f172a',
                  padding: '1rem',
                  borderRadius: '6px',
                  marginBottom: '1rem',
                  position: 'relative'
                }}>
                  <code style={{
                    fontSize: '1.1rem',
                    color: '#60a5fa',
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                    display: 'block',
                    lineHeight: '1.6'
                  }}>
                    {newApiKey}
                  </code>
                </div>

                <button
                  onClick={() => copyToClipboard(newApiKey)}
                  className="nt-btn nt-btn-primary"
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    fontSize: '1rem',
                    fontWeight: '600',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                  }}
                >
                  <IconCopy size={18} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                  Copy to Clipboard
                </button>
              </div>

              <div style={{
                background: '#1e3a8a',
                padding: '1rem',
                borderRadius: '6px',
                marginBottom: '1rem'
              }}>
                <h4 style={{ fontSize: '0.95rem', marginBottom: '0.75rem', color: '#93c5fd', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <IconList size={18} />
                  Next Steps:
                </h4>
                <ol style={{ 
                  fontSize: '0.875rem', 
                  lineHeight: '1.8',
                  paddingLeft: '1.5rem',
                  margin: 0
                }}>
                  <li>Copy the API key above</li>
                  <li>Open NinjaTrader 8</li>
                  <li>Go to <strong>Tools → Import → NinjaScript Add-On</strong></li>
                  <li>Import the latest ZIP file (RiskLoConnector) – do not unzip it</li>
                  <li>In the Control Center panel, go to <strong>New → RiskLo Connector</strong></li>
                  <li>Paste your API key in the settings, then click <strong>OK</strong> and restart NinjaTrader</li>
                </ol>
              </div>

              <div style={{
                fontSize: '0.8rem',
                color: '#94a3b8',
                padding: '0.75rem',
                background: '#0f172a',
                borderRadius: '6px',
                borderLeft: '3px solid #3b82f6'
              }}>
                💡 <strong>Tip:</strong> Your add-on will automatically sync your accounts every 30 seconds once configured!
              </div>
            </div>
            <div className="nt-modal-actions">
              <button 
                className="nt-btn nt-btn-primary" 
                onClick={() => {
                  setShowApiKeyModal(false);
                  setNewApiKey(null);
                }}
                style={{ width: '100%' }}
              >
                Got it! Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disconnect Confirmation Modal */}
      {showDisconnectModal && (
        <div className="nt-modal-overlay" onClick={() => setShowDisconnectModal(false)}>
          <div className="nt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="nt-modal-header">
              <h2>Confirm Disconnect</h2>
              <button className="nt-modal-close" onClick={() => setShowDisconnectModal(false)}>✕</button>
            </div>
            <div className="nt-modal-body">
              <p>Are you sure you want to permanently disconnect <strong>{accountToDisconnect?.name}</strong>?</p>
              <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)' }}>
                {accountToDisconnect?.platform === 'tradovate'
                  ? `This removes your Tradovate OAuth session from ${appName} (all listed Tradovate accounts will disappear from My Results).`
                  : 'This will completely remove the account connection and all associated trade data.'}
              </p>
            </div>
            <div className="nt-modal-actions">
              <button className="nt-btn nt-btn-secondary" onClick={() => setShowDisconnectModal(false)}>
                Cancel
              </button>
              <button className="nt-btn nt-btn-danger" onClick={confirmDisconnect}>
                Disconnect
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success/Error Messages */}
      {success && (
        <div className="nt-success-message">
          ✅ {success}
        </div>
      )}
      {error && (
        <div className="nt-error-message">
          ❌ {error}
        </div>
      )}

      {/* Trade Modal */}
      {renderTradeModal()}
      
      {/* Account Settings Modal */}
      {renderAccountSettingsModal()}
    </div>
  );
});

NinjaTraderDashboard.displayName = 'NinjaTraderDashboard';

export default NinjaTraderDashboard;
